import {
  ApiError,
  applyAuth,
  request as coreRequest,
  requestBlob,
  toApiError
} from "@/shared/api/http";
import type { RequestScope } from "@/shared/api/http";
import type { Role } from "@/shared/utils/roles";
import type { ApiMention, ApiPublication } from "@/shared/types/mention";
import type { PolarityPalette, PolarityTone } from "@/shared/types/polarity";
import {
  MENTION_ORDER_DEFAULT,
  type MentionOrder
} from "@/shared/vocabulary/mention-orders";
import type {
  ExportFormat,
  ExportKind,
  ExportStatus,
  ExportThemeLayout
} from "@/shared/vocabulary/exports";
import type { MentionSearchField } from "@/shared/vocabulary/mention-search-fields";
import type { ErrorLineGroup, ErrorLineState } from "@/shared/vocabulary/error-line-groups";
import type {
  RuleReviewOutcome,
  RuleReviewProposalKind,
  RuleReviewStatus,
  RuleReviewTask
} from "@/shared/vocabulary/rule-reviews";
import type { DeterministicScope, ThemeClassificationMode } from "@/shared/vocabulary/theme-classification";
import type { UploadSituation } from "@/shared/vocabulary/upload-situations";
import type { VehicleKind } from "@/shared/vocabulary/vehicle-kinds";
import type { WelcomeEmailOutcome } from "@/shared/vocabulary/welcome-email";
import type { MentionFilterCriteria } from "@/features/mentions/mention-filter-criteria";

// O default aponta pro `web_api/` (porta 8080, prefixo `/api`), que é o
// backend de verdade deste front. ATENÇÃO: parte dos métodos abaixo ainda fala
// o contrato do backend que veio junto com esta pasta e foi descartado — essas
// chamadas dão 404. Os de `/auth/*` (login, MFA, sessões, perfil, foto,
// exportação, desativação) saíram em 2026-10-11: a identidade é do Keycloak. As verticais vivas
// (upload, menções, relatório, dashboard, ajustes, empresas) estão repontadas.
// Não usar este arquivo como inventário do que existe no back.
export const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8080/api";

export function resolveApiAssetUrl(value?: string | null): string | undefined {
  if (!value) return undefined;
  // `blob:` é a foto de perfil já baixada com o token (a rota exige
  // `Authorization`, que um `<img src>` não manda): já é um endereço
  // pronto, e prefixar a API o quebraria.
  if (/^(https:\/\/|blob:)/i.test(value)) return value;
  return `${API_URL}${value.startsWith("/") ? value : `/${value}`}`;
}

export type CompanyContext = {
  id: string;
  name: string;
  slug: string | null;
  website: string | null;
  /** Vem do `/me`: `false` só chega a quem é Máster (quem não é
   * nem recebe a empresa desativada). Substituiu o `onboarding_status`,
   * que era um valor fixo preenchido pela casca sem fonte nenhuma. */
  is_active: boolean;
  logo_url: string | null;
  permissions: string[];
  /** Se a empresa EXIBE o índice CENA (o cálculo acontece sempre).
   * Obrigatório de propósito: opcional aqui + `?? true` no hook viraria
   * "campo esquecido num mapeamento = CENA nunca esconde", sem erro
   * nenhum. Obrigatório, o esquecimento é erro de compilação. */
  displays_cena: boolean;
  /** A escala de classificação da empresa — `'four_level'` (padrão) ou
   * `'three_level'`. Obrigatório pelo MESMO motivo do campo acima; o
   * guard de runtime (`?? 'four_level'`) mora no hook, porque tipo não
   * protege contra payload velho de `/me` em cache. */
  polarity_scale: "four_level" | "three_level";
  /** Os núcleos ativos da empresa, vindos do `/me` (2026-11-03) — o filtro
   * "Núcleo" da tela de Empresas (Máster e Administrador). Opcional porque
   * a ausência é o estado seguro: a empresa cai em "Sem núcleo". */
  groups?: CompanyGroupRef[];
};

/** Um núcleo como o `/me` o mostra. */
export type CompanyGroupRef = { id: string; name: string };

/** A resposta de `POST /companies/{id}/disable|enable`. */
export type CompanyState = {
  id: string;
  name: string;
  is_active: boolean;
  deleted_on: string | null;
};

export type CompanyAutofillSuggestion = {
  legal_name: string | null;
  website: string | null;
  country_code: string | null;
  timezone: string | null;
  aliases: string[];
  slogans: string[];
  common_names: string[];
  products: string[];
  initiatives: string[];
  campaigns: string[];
  is_government_entity: boolean;
};

/**
 * O corpo de `POST /companies` — o do backend VIVO.
 *
 * Até 2026-08-30 este tipo descrevia o backend descartado
 * (`primary_brand`, `legal_name`, `tax_id`, `country_code`, `timezone`,
 * `logo`). O servidor real ignora campo extra em silêncio, então a
 * empresa nascia só com o nome e tudo o mais que a pessoa digitou era
 * descartado sem aviso.
 *
 * `citation_tags` é `categoria -> valores`, e as categorias são as
 * cinco de `web_api/application/companies.py::CITATION_TAG_CATEGORIES`
 * — servidor recusa categoria desconhecida.
 *
 * `main_brand_terms` (expressão booleana de matching) existe no
 * servidor e o wizard NÃO a coleta: apelido não é termo de matching, e
 * mandar um como o outro gravaria expressão inválida.
 */
export type CreateCompanyPayload = {
  name: string;
  /** Sem isto, a marca principal herda o nome da empresa. */
  main_brand_name?: string;
  main_brand_terms?: string;
  citation_tags?: Record<string, string[]>;
  /** O núcleo da empresa (RN-01, 2026-10-22). O Administrador com um
   *  núcleo só pode omitir; com mais de um, escolhe. O Máster escolhe ou
   *  deixa sem (vincula depois em Núcleos). */
  group_id?: number;
};

/** O que `POST /companies/from-spreadsheet` devolve: `import_id` nulo com
 * `import_error` quando a empresa nasceu e a fila recusou a importação. */
export type CompanyFromSpreadsheet = {
  id: string;
  name: string;
  import_id: number | null;
  import_error: string | null;
};

export type CreatedCompany = {
  id: string;
  name: string;
  slug: string;
  website: string | null;
  onboarding_status: string;
  permissions: string[];
  primary_brand: {
    id: string;
    name: string;
    slug: string | null;
    aliases: string[];
    website: string | null;
    is_primary: boolean;
    logo_url: string | null;
  };
};

/** Uma linha por marca, e não um porta-voz com lista de marcas.
 *
 * O contrato antigo tinha `brands: SpokespersonBrandLink[]` — um
 * porta-voz agregando N vínculos. O novo é N linhas, uma por marca, e
 * isso é decisão de domínio registrada, não detalhe de transporte: "se
 * o porta-voz X foi aplicado para 3 marcas, ele vai constar 3 vezes na
 * listagem". O mesmo vale pra tema e mensagem-chave. Agrupar de volta
 * na tela é escolha de apresentação; o dado é por marca. */
export type Spokesperson = {
  id: number;
  brand_id: number;
  name: string;
  /** Variações separadas por `;` — mesma forma que o banco guarda. */
  name_variations: string | null;
  counts_toward_spokesperson_score: boolean;
  valid_until: string | null;
  is_active: boolean;
  deleted_on: string | null;
};

export type CreateSpokespersonPayload = {
  brand_id: number;
  name: string;
  name_variations?: string | null;
  counts_toward_spokesperson_score?: boolean;
  valid_until?: string | null;
};

export type UpdateSpokespersonPayload = Partial<CreateSpokespersonPayload> & {
  is_active?: boolean;
};

/** Os "Dados da Empresa" da spec (`Mapa de Domínio ReputAItion`, seção
 * "Configurações da Empresa") — e só eles.
 *
 * O tipo anterior (`CompanyDetail`) tinha razão social, CNPJ, website,
 * país, fuso, `active` e `onboarding_status`. **Nenhum desses está na
 * spec nem no schema**: eram do backend descartado, e o `GET` que os
 * buscava respondia 404 — a origem do "Not Found" no topo da tela. */
/** A sugestão do assistente de configuração (2026-10-06): o cadastro
 * de classificação inteiro, já no vocabulário das rotas de catálogo —
 * nada daqui persiste sozinho; quem grava é `apply-plan.ts` depois de a
 * pessoa marcar o que quer. Espelho de
 * `web_api/application/config_suggestion.py::ConfigSuggestion`. */
export type SuggestedRule = { rule_type: string; source: PostSource; text: string };
export type SuggestedTerm = { category: TermCategory; source: PostSource; term: string };
export type SuggestedFocus = { category: FocusCategory; source: PostSource; term: string };
export type SuggestedThemeGroup = { name: string; weight: number };
export type SuggestedTheme = {
  name: string;
  group: string;
  terms: string | null;
  detection_assumptions: string;
  non_detection_assumptions: string;
};
export type SuggestedKeyMessage = {
  message: string;
  data_source: PostSource;
  rules: string;
  exclusion_rules: string | null;
};
export type SuggestedSpokesperson = { name: string; name_variations: string | null };
export type SuggestedPublisherTier = {
  name: string;
  weight: number;
  min_count: number | null;
  max_count: number | null;
};
export type ConfigSuggestion = {
  brand_name: string;
  /** Expressão pronta pra `Brand.terms`, já validada pela gramática. */
  brand_terms: string;
  citation_tags: Record<string, string[]>;
  focus: SuggestedFocus[];
  rules: SuggestedRule[];
  proprietary_terms: SuggestedTerm[];
  theme_groups: SuggestedThemeGroup[];
  themes: SuggestedTheme[];
  key_messages: SuggestedKeyMessage[];
  spokespersons: SuggestedSpokesperson[];
  publisher_tiers: SuggestedPublisherTier[];
  /** O que o servidor descartou da resposta da IA, pra tela mostrar. */
  dropped: string[];
  sample_size: number;
  ai_calls: number;
  /** Avisos sobre a amostra ("nenhuma publicação", "cortada em 800"). */
  notes: string[];
};

/** O pedido de sugestão como a tela o acompanha (2026-10-27): a IA leva
 * de 45 s a mais de 2 min e o balanceador corta em 60 s, então o POST
 * responde 202 com a linha e a tela pergunta por ela até terminar.
 * `result` só vem em `FINISHED`; `message`, em `ERRORED`. Espelho de
 * `routes/company_config.py::ConfigSuggestionRunOut`. */
export type ConfigSuggestionRun = {
  id: number;
  status: "QUEUED" | "STARTED" | "FINISHED" | "ERRORED";
  requested_at: string;
  started_at: string | null;
  finished_at: string | null;
  message: string | null;
  result: ConfigSuggestion | null;
};

export type ConfigSuggestionInput = {
  sampleSource: "upload" | "history";
  /** A planilha de clipadora, quando `sampleSource === "upload"`. */
  sample?: File | null;
  /** Textos de contexto (briefing, árvore de temas) — só texto. */
  contextFiles?: File[];
  notes?: string;
};

/** A mini base de calibração: um upload DE VERDADE, recortado da
 *  planilha da amostra — `rows` linhas das `sample_rows` que ela tinha. */
export type CalibrationUpload = {
  upload_id: number;
  status: string;
  template: string;
  rows: number;
  sample_rows: number;
  message: string | null;
};

export type PremiseRevisionAction = "update" | "create" | "deactivate";

/** Uma proposta da IA sobre UMA premissa de polaridade: editar a que
 *  existe (`rule_id`), criar outra, ou desativar. `before` é sempre o
 *  texto GRAVADO (o servidor o reescreve), `after` o proposto. */
export type PremiseRevision = {
  action: PremiseRevisionAction;
  rule_id: number | null;
  /** O bloco, no vocabulário do assistente (`global`, `positivo`…). */
  rule_type: string;
  source: PostSource;
  before: string;
  after: string;
  why: string;
};

export type PremiseRevisionResult = {
  summary: string;
  revisions: PremiseRevision[];
  dropped: string[];
  /** O que a base ensinou: corrigidas, confirmadas (revisadas sem
   *  correção) e as que ninguém olhou. */
  corrected: number;
  confirmed: number;
  unreviewed: number;
  ai_calls: number;
  notes: string[];
};

export type CompanyProfile = {
  id: string;
  name: string;
  /** "Cadastrar concorrentes": liga a análise de concorrente. */
  has_competitor_analysis: boolean;
  is_government: boolean;
  /** Exibe o índice CENA nas telas. O cálculo acontece SEMPRE —
   * desligar só oculta os números. */
  displays_cena: boolean;
  /** O peso do grupo de publicador compete com a faixa de seguidores
   * no p_publi (o maior vence). Continua valendo no cálculo mesmo com
   * o CENA oculto. */
  publisher_group_counts_toward_cena: boolean;
  /** A escala de classificação: muda o que o motor produz DAQUI PRA
   * FRENTE e o que as telas oferecem — nunca o histórico. */
  polarity_scale: "four_level" | "three_level";
  /** O SVG inteiro, não uma URL. Renderizar SEMPRE em
   * `<img src="data:image/svg+xml…">` — nunca como HTML, senão markup
   * de upload passa a executar no navegador de quem abre a tela. */
  color_logo: string | null;
  gray_logo: string | null;
  /** Chaves que só o Máster liga, por empresa: desligadas, quem
   * está abaixo dele só LÊ o prompt base / o modelo por tarefa. */
  can_edit_prompts: boolean;
  can_edit_models: boolean;
};

/** As linhas automáticas do painel "Pontos de atenção" da Visão Geral. O
 * vocabulário é do servidor (`web_api/application/overview_attention.py`);
 * o que cada linha conta e mostra, de `features/overview/attention-lines.ts`. */
export type OverviewAttentionLine =
  | "negative_mentions"
  | "negative_share"
  | "negative_title_citation"
  | "negative_top_tier"
  | "low_cena"
  | "low_cena_balance"
  | "unclassified_mentions"
  | "neutral_final"
  | "unreviewed_mentions"
  | "no_key_message"
  | "no_theme_highlight"
  | "shared_post"
  | "upload_issues"
  | "stale_data"
  | "expiring_drafts";

/** Uma linha: aparece ou não, e em que limite ACENDE (a unidade é da
 * linha: itens, %, nota, pontos ou dias). Abaixo do limite ela continua
 * aparecendo com o número, sem o alerta. */
export type OverviewAttentionRule = { enabled: boolean; threshold: number };

/** O painel inteiro, sempre completo (o servidor preenche o default). */
export type OverviewAttention = Record<OverviewAttentionLine, OverviewAttentionRule>;

/** Uma categoria de "Detecção de Citação no Título" com seus termos.
 * Determinística (a pontuação é por presença do termo). */
export type CitationTagGroup = {
  category: string;
  tags: string[];
};

/** `competitor_company_id` é a empresa-SOMBRA que serve de alvo do
 * monitoramento — concorrente não é um texto solto, é uma empresa de
 * verdade no banco, sem usuário nenhum vinculado. Vem só pra
 * leitura/diagnóstico; nenhuma rota o aceita de volta. */
export type Competitor = {
  id: number;
  competitor_company_id: string;
  name: string;
  terms: string | null;
  is_active: boolean;
  deleted_on: string | null;
};

export interface SettingsCounts {
  brands: number;
  competitors: number;
  spokespersons: number;
  key_messages: number;
  theme_groups: number;
  themes: number;
  publisher_tiers: number;
  publisher_persons: number;
  prompts: number;
  channels: number;
  vehicles: number;
}

/** Um item da lista "Configuração da empresa", calculada no servidor. O
 * `detail` já vem em português; `tab` é a aba de Ajustes que resolve;
 * `action` é uma ação de um clique que o servidor sabe oferecer. */
export interface CompanyChecklistItem {
  key: string;
  status: "missing" | "warning" | "ok";
  detail: string;
  tab: string | null;
  action: "standard_channels" | null;
}

export interface CompanyChecklist {
  items: CompanyChecklistItem[];
  missing: number;
  warning: number;
}

export type ThemeGroup = {
  id: number;
  name: string;
  weight: number | null;
  is_active: boolean;
  deleted_on: string | null;
};

/** Uma linha por marca — mesma razão do `Spokesperson` acima ("um tema
 * X aplicado a duas marcas vira dois temas X"). */
export type Theme = {
  id: number;
  brand_id: number;
  theme_group_id: number;
  name: string;
  /** Uma ou mais expressões booleanas (`"a" AND NOT b`) separadas por `;`, cada uma validada no servidor e avaliada sozinha pela esteira. */
  terms: string | null;
  /** Só determinístico, só IA ou os dois. */
  classification_mode: ThemeClassificationMode;
  /** Em que fonte o termo vale, no modo "os dois"; nos outros é `both`. */
  deterministic_scope: DeterministicScope;
  detection_assumptions: string | null;
  non_detection_assumptions: string | null;
  is_active: boolean;
  deleted_on: string | null;
};

/** Um alvo da regra de tema — id pra gravar, nome pra exibir. */
export type AssignmentTarget = { id: number; name: string };

/** "Publicação destes publicadores/veículos sempre recebe este tema." */
export type ThemeAssignment = {
  theme_id: number;
  publisher_groups: AssignmentTarget[];
  publisher_persons: AssignmentTarget[];
  vehicles: AssignmentTarget[];
};

/** Um nome alternativo de canal/veículo. Resolve pro MESMO cadastro,
 * herdando peso, tier e o "ignorar" do dono — a clipadora manda
 * "Comentários no Facebook" e o sistema pontua como "Facebook". */
export type CatalogAlias = {
  id: number;
  name: string;
};

export type Channel = {
  id: number;
  name: string;
  post_source: "network" | "press";
  ignore_term_verification: boolean;
  /** Código de tier ('A'/'B'/'C'…), ligado por VALOR, sem FK. **Não
   * pontua** — é mecanismo paralelo herdado do legado. */
  tier: string | null;
  /** Peso do canal no CENA social. `null` = usa o peso padrão global;
   * `0` = a empresa decidiu que este canal não pontua. Os dois são
   * diferentes em todo o caminho, então nunca tratar com `||`. */
  weight: number | null;
  /** Post deste canal NÃO entra: vira PULO deliberado, não erro nem
   * item na fila de correção. */
  ignored: boolean;
  aliases: CatalogAlias[];
  is_active: boolean;
  deleted_on: string | null;
};

/** Veículo de imprensa/TV/rádio. É tabela SEPARADA de `Channel`: canal
 * com `post_source='press'` é herança do schema legado, e o registro
 * certo de veículo é este. */
export type Vehicle = {
  id: number;
  name: string;
  vehicle_kind: VehicleKind;
  /** `'supertier'`/`'tier1'`..`'tier5'` — alimenta o `p_veiculo` do CENA. */
  tier_key: string | null;
  /** Veículo de paywall: falha de scraping é esperada e não vira item
   * na fila de correção. */
  content_optional: boolean;
  /** Ver `Channel.ignored` — mesma semântica. */
  ignored: boolean;
  /** Ver `Channel.ignore_term_verification` — toda matéria do veículo
   * entra, sem casar com os termos da marca. */
  ignore_term_verification: boolean;
  aliases: CatalogAlias[];
  is_active: boolean;
  deleted_on: string | null;
  /** UF (caixa alta quando são duas letras) — texto livre do `dcanal`. */
  state: string | null;
  region: string | null;
  country: string | null;
  /** Preço base em reais. Só cadastro: nenhuma nota ou valoração o lê. */
  base_price: number | null;
};

/** Uma execução de importação de catálogo por planilha.
 *
 * Os contadores são calculados por QUERY a cada leitura, não gravados —
 * é o que faz o poll da tela mostrar o recibo crescendo enquanto o
 * worker aplica, e o que garante que reentrega de mensagem na fila
 * nunca infle o número.
 *
 * `total_rows` menos a soma dos quatro dá o que ainda falta; o servidor
 * manda `pending_count` pronto pra tela não refazer essa conta. */
export type CatalogImport = {
  id: number;
  filename: string;
  /** O formato da planilha: `catalog` (canais e veículos) ou
   * `publishers` (publicadores nos grupos) ou `company` (a configuração
   * da empresa, várias abas). */
  kind: CatalogImportKind;
  /** `QUEUED` | `STARTED` | `FINISHED` | `FINISHED_WITH_REJECTIONS` |
   * `CANCELLED` | `ERRORED`. O nome próprio pro fim atípico é a
   * convenção da casa — "terminou com recusa" não é "terminou". */
  status: string;
  requested_at: string;
  started_at: string | null;
  finished_at: string | null;
  total_rows: number;
  created_count: number;
  updated_count: number;
  alias_created_count: number;
  rejected_count: number;
  /** Linhas lidas e não aplicadas (abas da configuração que o formato
   * ainda não importa). */
  skipped_count: number;
  pending_count: number;
  message: string | null;
};

/** Os formatos de planilha da importação em massa (vocabulário
 * `CATALOG_IMPORT_KINDS` do core): canais e veículos, publicadores nos
 * grupos, a configuração da empresa (várias abas), as premissas (uma aba)
 * e os temas e subtemas (duas abas). */
export type CatalogImportKind = "catalog" | "publishers" | "company" | "rules" | "themes";

/** Uma linha do recibo. `outcome` `null` = ainda não aplicada. */
export type CatalogImportRow = {
  row_index: number;
  raw: Record<string, unknown>;
  outcome:
    | "created"
    | "updated"
    | "alias_created"
    | "rejected"
    | "skipped"
    | null;
  /** `channel` | `vehicle` | `publisher`; na configuração da empresa, a
   * CHAVE da aba (`companySheetName` traduz). */
  catalog_kind: string | null;
  message: string | null;
};

/** Faixa de seguidores → peso. Alimenta o `p_publi` do CENA social.
 *
 * `is_default` marca a faixa de QUEDA: é nela que cai a publicação de
 * rede que não traz número de seguidores — ou traz `0`, que o cálculo
 * trata igual — e a que não casou range nenhum. Uma por empresa; marcar
 * outra rebaixa a anterior. Nada obriga a ter uma; sem ela, essas
 * publicações ficam com `p_publi` zero. */
export type PublisherTier = {
  id: number;
  name: string;
  weight: number;
  /** `null` = faixa sem range; o cálculo chega nela pelo NOME. */
  min_count: number | null;
  /** `null` = faixa de topo aberta. */
  max_count: number | null;
  /** Vigência agendada — diferente de desativar, que é o botão. */
  valid_until: string | null;
  is_active: boolean;
  deleted_on: string | null;
  is_default: boolean;
};

/** Grupo de publicadores. A outra metade do `p_publi` — compete com a
 * faixa de seguidores, e o maior dos dois vence. */
export type PublisherGroup = {
  id: number;
  name: string;
  /** `null` = só rotula, não pontua. Não é o mesmo que zero. */
  weight: number | null;
  /** Grupo do sistema: vinculável por todos, editável por ninguém. */
  is_global: boolean;
  is_active: boolean;
  deleted_on: string | null;
};

export type PublisherProfileRef = {
  id: number;
  /** Casa com o `Canal` da publicação — por isso é escolhido de uma
   * lista de canais cadastrados, não digitado. */
  platform: string;
  profile_url: string;
};

/** Pessoa publicadora com seus perfis e grupos — o agregado que a tela
 * edita de uma vez. */
export type PublisherPerson = {
  id: number;
  name: string;
  profiles: PublisherProfileRef[];
  group_ids: number[];
  is_active: boolean;
  deleted_on: string | null;
};

/** Uma linha por marca — mesma razão do `Spokesperson`. `rules` é
 * OBRIGATÓRIO: é texto livre renderizado dentro do prompt de IA ("usa
 * essa tag quando…"), e sem critério a IA escolhe sozinha. */
export type KeyMessage = {
  id: number;
  brand_id: number;
  data_source: "press" | "network";
  message: string;
  rules: string;
  exclusion_rules: string | null;
  is_active: boolean;
  deleted_on: string | null;
};

// ── Configuração de classificação ──────────────────────────────────
//
// São TRÊS conceitos distintos, com três consumidores distintos no
// motor, e a distinção é o que a tela precisa preservar:
//
//   `ClassificationTerm` decide a polaridade SEM chamar a IA — é
//   atalho determinístico, conta ocorrência de termo no post.
//   `FocusTerm` e `PromptRule` são texto que vai DENTRO do prompt.
//
// Juntar os três numa "configuração" só faria a tela ensinar errado o
// que cada um faz.

/** Fonte do post. `network` = redes sociais, `press` = imprensa. */
export type PostSource = "network" | "press";

/** "Foco cliente" / "Foco concorrente" na tela — serve pra IA saber de
 * quem a matéria fala. */
export type FocusCategory = "client" | "competitor";

export type FocusTerm = {
  id: number;
  brand_id: number;
  category: FocusCategory;
  source: PostSource;
  term: string;
  is_active: boolean;
  deleted_on: string | null;
};

/** A polaridade que o termo FORÇA, sem chamar a IA. As duas compostas
 * (`positive_favorable`/`negative_unfavorable`) são a segunda passada,
 * usada quando a primeira devolve NEUTRO. */
export type TermCategory =
  | "positive" | "negative" | "favorable" | "unfavorable"
  | "positive_favorable" | "negative_unfavorable";

/** Onde o termo é procurado: no autor da publicação (`publisher`), no
 * veículo (`vehicle`) ou no texto do post (`proprietary_terms`). */
export type TermType = "publisher" | "vehicle" | "proprietary_terms";

export type ClassificationTerm = {
  id: number;
  brand_id: number;
  term_type: TermType;
  category: TermCategory;
  source: PostSource;
  term: string;
  is_active: boolean;
  deleted_on: string | null;
};

export type PromptRule = {
  id: number;
  brand_id: number;
  task: string;
  /** CAIXA ALTA sem underscore (`EXTRAPOSITIVO`) — é a chave interna
   * que liga o marcador do prompt às regras. Nunca exibida. */
  rule_type: string;
  source: PostSource;
  /** O número GLOBAL que a IA cita de volta ("Regra: 7"): POSITIVO
   * 1-15, NEGATIVO 16-30, FAVORAVEL 31-45, DESFAVORAVEL 46-60. A tela
   * mostra este número mesmo (decisão da dona, 2026-10-21): um número só
   * por premissa, o mesmo da aba de Ajustes e da página de Justificativas. */
  position: number;
  text: string;
  is_active: boolean;
  deleted_on: string | null;
};

/** O texto do prompt GERAL de uma linha, ao lado da personalização. */
export type PromptGeneralText = {
  header: string;
  extra_header: string | null;
  output_template: string;
  temperature: number;
  system_message: string | null;
  updated_at: string | null;
};

/**
 * Uma das 13 linhas EFETIVAS do prompt da empresa (2026-10-27): a empresa
 * segue o prompt geral (`prompt_default`) e só diverge na linha que alguém
 * personalizou. Não há `id` estável — a identidade é `(task, source,
 * position)` — nem desativação: são sempre as 13 do geral.
 */
export type Prompt = {
  task: string;
  source: PostSource;
  /** ÍNDICE, não ordenação cosmética: `0` é o prompt principal, `1` o
   * de desempate quando o primeiro devolve NEUTRO. */
  position: number;
  /** Os campos abaixo são os EFETIVOS: o que o motor usa pra esta empresa. */
  header: string;
  extra_header: string | null;
  /** Regex que extrai valor + número da regra da resposta da IA. */
  output_template: string;
  temperature: number;
  system_message: string | null;
  /** `default` segue o geral; `override` foi personalizada. */
  origin: "default" | "override";
  /** O geral inteiro — só quando a linha é personalizada. */
  default: PromptGeneralText | null;
  /** O geral mudou depois que esta linha foi personalizada. */
  default_changed: boolean;
  /** O `updated_at` do geral na hora da personalização. */
  default_updated_at: string | null;
  /** Marcador de premissa usado no texto sem nenhuma premissa cadastrada
   * pra ele (agora vêm também na leitura, por linha). */
  warnings: string[];
};

/** O corpo do PUT: a personalização é da linha INTEIRA. Tarefa, fonte e
 *  posição vão no caminho. */
export type PromptCustomizeInput = {
  header: string;
  extra_header: string | null;
  output_template: string;
  temperature: number;
  system_message: string | null;
};

/** A escolha gravada de uma tarefa: `model_id` é o id do catálogo
 *  (`ai_model.id`, FK desde 2026-09-24) e `model_name` o nome dele. */
export type PromptModel = { id: number; task: string; model_id: number; model_name: string };

/**
 * A escolha de modelo de UMA tarefa, já resolvida como o motor resolve:
 * o override da empresa, ou o padrão da instalação.
 *
 * `origin` é o campo que não dá pra derivar do resto — sem ele, "usa o
 * padrão" e "esta empresa escolheu exatamente o mesmo modelo do padrão"
 * são indistinguíveis, e a tela não sabe quando oferecer "voltar ao
 * padrão".
 */
export type EffectivePromptModel = {
  task: string;
  model_id: number | null;
  model_name: string | null;
  provider: string | null;
  origin: "override" | "default" | null;
  model_active: boolean;
};

/** Quem hospeda os modelos: o id do provedor como o litellm o conhece
 * (`openai`, `anthropic`, `mistral`...). A lista vem do servidor
 * (`listAiProviders`), não é escrita aqui — decisão de 2026-09-18. */
export type AiProvider = string;

/** Um provedor que a instalação PODE cadastrar, com o que ele exige. */
export type AiProviderOption = {
  id: string;
  label: string;
  requires_endpoint: boolean;
  requires_api_version: boolean;
  /** Aparece no topo da caixa de escolha. */
  common: boolean;
};

/**
 * A credencial de um provedor, como a tela a vê.
 *
 * **Nunca traz a chave** — só o mascarado (`api_key_hint`). `unreadable`
 * é o estado que a rotação da chave-mestra produz: a linha existe e não
 * decifra mais, e o conserto é redigitar.
 */
export type AiProviderCredential = {
  provider: AiProvider;
  api_key_hint: string;
  api_base: string | null;
  api_version: string | null;
  is_active: boolean;
  unreadable: boolean;
  updated_by: string | null;
  /** Nome de hoje no cadastro; `null` = fora dele (a tela cai no id). */
  updated_by_name?: string | null;
  updated_at: string;
};

export type TaskDefault = { task: string; model_id: number | null; model_name: string | null };

/**
 * Os pesos do CENA de um escopo (a instalação, ou uma empresa).
 *
 * `values` é um mapa porque a lista de campos vem do MODELO no servidor —
 * uma coluna nova aparece aqui sem passar por este arquivo. Os números
 * são STRING de propósito: `float` reintroduziria ruído de ponto
 * flutuante justamente no valor que alguém confere ao investigar por que
 * uma nota mudou.
 */
export type CenaValue = string | boolean | null;

/** (2026-10-22) `values` carrega também 5 flags booleanas, que ligam e
 *  desligam os bônus: pesos são texto, flags são `boolean`. */
export type CenaScoreConfig = {
  company_id: string | null;
  values: Record<string, CenaValue>;
};

/** Algo suspeito no que foi salvo — não impede de salvar. */
export type CenaWarning = { field: string; message: string };

export type CenaSaveResult = { config: CenaScoreConfig; warnings: CenaWarning[] };

/** Os pesos que valem para a empresa ATIVA (2026-10-22): os próprios, se
 *  ela tem, senão os padrão — e os padrão à parte, porque a configuração
 *  própria nasce como cópia deles. */
export type CompanyCenaConfig = {
  has_override: boolean;
  values: Record<string, CenaValue>;
  default_values: Record<string, CenaValue>;
};

export type CenaOverrideCompany = {
  company_id: string;
  name: string;
  has_override: boolean;
};

/**
 * Uma mudança registrada na trilha.
 *
 * `company_id`/`company_name` nulos são as configurações da INSTALAÇÃO
 * (catálogo de IAs, pesos padrão do CENA) — a tela rotula como "Global".
 *
 * `diff` vem como o escritor gravou: `{campo: {old, new}}`.
 */
export type ConfigAuditEntry = {
  id: number;
  table_name: string;
  record_id: number | null;
  company_id: string | null;
  company_name: string | null;
  action: "create" | "update" | "delete";
  diff: Record<string, { old: unknown; new: unknown }>;
  changed_by: string | null;
  /** Nome de hoje no cadastro local; `null` sem cadastro (a tela cai no
   *  código de `changed_by`). */
  changed_by_name?: string | null;
  /** Em quem a mudança caiu, nas seções de acesso (2026-10-23): o nome da
   *  pessoa ou do núcleo. `null`/ausente nas outras seções. */
  record_label?: string | null;
  changed_at: string;
};

/** Uma opção dos filtros da Auditoria (empresa ou pessoa). `name` nulo
 *  é quem não está no cadastro local — a tela mostra o código. */
export type AuditOption = { id: string; name: string | null };

export type ConfigAuditPage = {
  items: ConfigAuditEntry[];
  total: number;
  page: number;
  size: number;
};

/** Uma tentativa de login malsucedida, copiada do Keycloak (2026-10-23,
 *  proposta de IAM §8.2). `username` é o que a pessoa DIGITOU; `error` é
 *  o código do Keycloak, que a tela traduz. */
export type LoginFailure = {
  occurred_at: string;
  person_name: string | null;
  username: string | null;
  ip_address: string | null;
  error: string;
};

export type LoginFailurePage = {
  items: LoginFailure[];
  total: number;
  page: number;
  size: number;
};

export type CenaScorePoint = {
  table: string;
  key: string[];
  label: string;
  point: string;
};

// --- Consumo de IA (`GET /admin/ai-usage/*`) ---------------------------

/** Os contadores de um recorte — total, dia, etapa ou modelo. `calls`
 *  conta só linha COM chamada (modelo respondido), e se divide em
 *  `succeeded` + `failed` + `unfinished`. `resolved_without_ai` e
 *  `not_called_by_config` são linhas SEM chamada (o determinismo
 *  resolveu, ou a configuração pulou) e ficam FORA de `calls` — a
 *  linha do log é uma por post × marca × tarefa, chamada ou não.
 *  Tokens e custo são `null`
 *  quando NENHUMA linha do recorte os tem — soma parcial quando algumas
 *  têm —, nunca zero por ausência; `tokenless_calls`/`unpriced_calls`
 *  dizem quantas ficaram de fora. */
export type AiUsageMetric = {
  calls: number;
  succeeded: number;
  failed: number;
  unfinished: number;
  resolved_without_ai: number;
  not_called_by_config: number;
  tokenless_calls: number;
  unpriced_calls: number;
  priced_calls: number;
  input_tokens: number | null;
  output_tokens: number | null;
  /** Dinheiro como STRING (o servidor preserva a escala); `null` é
   *  "sem custo apurado", nunca `"0"`. */
  cost: string | null;
};

export type AiUsageTimelinePoint = AiUsageMetric & { day: string };

export type AiUsageStageSlice = AiUsageMetric & {
  key: string;
  family: "pipeline" | "personal";
};

export type AiUsageModelSlice = AiUsageMetric & {
  key: string;
  provider: string;
  /** O nome como está no cadastro; `null` = linha antiga que não casou
   *  com nada, ou modelo apagado. */
  catalog_name: string | null;
  responded_names: string[];
};

export type AiUsageGroupSlice = {
  key: string;
  label: string;
  calls: number;
  failed: number;
  unfinished: number;
  input_tokens: number | null;
  output_tokens: number | null;
  cost: string | null;
  unpriced_calls: number;
  /** Só pra pessoa: nome do cadastro local; `null` sem cadastro. */
  user_name?: string | null;
};

export type AiUsageSummary = {
  date_from: string;
  date_to: string;
  /** O fuso em que o servidor fecha o dia — a tela confere contra o seu. */
  timezone: string;
  currency: string;
  totals: AiUsageMetric;
  timeline: AiUsageTimelinePoint[];
  stages: AiUsageStageSlice[];
  models: AiUsageModelSlice[];
  /** Vazios quando o pedido filtrou por empresa ou pessoa. */
  companies: AiUsageGroupSlice[];
  users: AiUsageGroupSlice[];
};

export type AiUsageOutcome = "ok" | "failed" | "unfinished" | "resolved_without_ai" | "not_called";
export type AiUsageCostStatus = "priced" | "no_price" | "no_tokens" | "unfinished" | "no_call";

export type AiUsageCall = {
  id: number;
  /** UTC sem `Z`, como todo carimbo do servidor — ver `parseServerTime`. */
  recorded_at: string;
  company_id: string | null;
  company_name: string | null;
  brand_name: string | null;
  upload_id: string | null;
  user_id: string | null;
  user_name?: string | null;
  stage: string;
  provider: string;
  client: string | null;
  model: string | null;
  requested_model: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cost: string | null;
  cost_status: AiUsageCostStatus;
  latency_ms: string | null;
  outcome: AiUsageOutcome;
  error: string | null;
};

export type AiUsageCallsPage = {
  items: AiUsageCall[];
  total: number;
  page: number;
  size: number;
};

export type AiUsageOption = { key: string; label: string; user_name?: string | null };

export type AiUsageOptionsPage = {
  items: AiUsageOption[];
  total: number;
  page: number;
  size: number;
};

/** O recorte da tela. Os dias são `AAAA-MM-DD` no fuso da instalação. */
export type AiUsageQuery = {
  date_from: string;
  date_to: string;
  company_id?: string;
  stage?: string;
  model?: string;
  user_id?: string;
};

export type AiModel = {
  id: number;
  provider: AiProvider;
  name: string;
  label: string | null;
  is_active: boolean;
  /** Preço ATUAL em dólar por milhão de tokens, como STRING (o servidor
   *  preserva a escala: `"0.150000"`). `null` = sem preço cadastrado, que
   *  é diferente de `"0"` (de graça). Andam sempre aos pares. */
  input_price_per_million: string | null;
  output_price_per_million: string | null;
};

export type UserProfile = {
  id: string;
  email: string;
  name: string;
  roles: string[];
  companies: CompanyContext[];
  has_password: boolean;
  avatar_url: string | null;
  google_linked: boolean;
  mfa_enabled: boolean;
  created_at: string | null;
  /** O alias do login por pessoa da instalação (`"google"`), ou nulo com o
   *  modo desligado — de `GET /login-options`. Decide o que a tela de
   *  usuários oferece (método de login, convite). */
  login_mode?: string | null;
  /** Como ESTA pessoa entra (`"google"` ou `"password"`), do cadastro;
   *  nulo sem cadastro (stub). A tela de Segurança só oferece trocar
   *  senha e configurar o autenticador pra quem entra com senha. */
  login_method?: string | null;
};

/** Uma sessão do ReputAItion da própria pessoa (`GET /me/sessions`).
 *  Os instantes vêm em UTC sem fuso, como todo carimbo do servidor. */
/** `GET /me/support`: se este ambiente tem helpdesk ligado, onde a
 *  pessoa acompanha os chamados e os assuntos que o formulário oferece. */
export type SupportStatus = {
  available: boolean;
  portal_url: string | null;
  categories: Record<string, string>;
  /** Limites dos anexos (2026-10-20). O servidor confere de novo, pelos bytes. */
  max_attachments: number;
  attachment_max_bytes: number;
  attachment_extensions: string[];
};

export type SupportTicketInput = {
  category: string;
  subject: string;
  description: string;
  page?: string | null;
  support_code?: string | null;
};

export type SupportTicket = { number: string; portal_url: string };

export type MySession = {
  id: string;
  ip_address: string | null;
  started_at: string;
  last_access_at: string;
  /** A sessão desta aba. */
  current: boolean;
};

/** Uma chave de acesso (passkey) da própria pessoa (2026-10-21). */
export type MyPasskey = {
  id: string;
  /** O nome que o navegador ou a pessoa deu no cadastro; pode faltar. */
  label: string | null;
  created_at: string | null;
};

/** Suspensão em vigor (proposta de IAM, 2026-10-07). `suspended_by_name`
 *  nulo com `reason: "inactivity"` foi a rotina do worker. */
export type AccessSuspension = {
  reason: "leave" | "inactivity" | "recertification";
  note: string | null;
  suspended_on: string;
  suspended_by_name: string | null;
};

export type LoginMethod = "google" | "password";

export type AccessUser = {
  id: number;
  name: string;
  email: string;
  role: Role;
  status: "pending" | "active" | "suspended" | "disabled";
  email_verified: boolean;
  created_on: string;
  activated_on: string | null;
  disabled_on: string | null;
  login_method: LoginMethod;
  last_login_at: string | null;
  suspension: AccessSuspension | null;
  /** Até quando a exceção "usuário e senha" vale; nulo = sem prazo. */
  login_exception_until: string | null;
};

/** `is_active` falso é empresa desativada (2026-10-23): a lista a traz —
 *  o vínculo que já existe precisa aparecer —, e quem oferece escolhe. */
export type AccessCompany = { id: string; name: string; is_active?: boolean };

export type CompanyGroup = {
  id: number;
  name: string;
  description: string | null;
  is_active: boolean;
  created_by_user_id: number | null;
  created_on: string;
};

export type AccessUserDetail = AccessUser & {
  groups: CompanyGroup[];
  companies: AccessCompany[];
  /** A identidade do Keycloak também serve a outros sistemas da FSB
   *  (2026-10-20): a tela avisa antes de mexer em senha. Só vem pro
   *  Máster; ausente ou nulo = sem aviso. */
  shared_identity?: { other_clients: string[]; created_before_invite: boolean } | null;
  /** O bloqueio por tentativas erradas de login (2026-10-22). Só vem pro
   *  Máster, que é quem desbloqueia; ausente ou nulo = não se sabe. */
  login_lockout?: { locked: boolean; failures: number; last_failure_at: string | null } | null;
};

export type CompanyGroupDetail = CompanyGroup & {
  users: AccessUser[];
  companies: AccessCompany[];
};

/** Um aviso do sino (`GET /notifications`). O texto vem pronto do
 *  servidor, congelado no momento em que o job terminou; o front só
 *  decide o ícone (`level`) e o link (`kind` + `subject_id` +
 *  `company_id`, ver `shell/notifications.ts`). */
export type AppNotificationKind =
  | "upload"
  | "social_report"
  | "catalog_import"
  | "bulk_edit"
  | "ai_reprocess"
  | "ai_reprocess_sample"
  | "correction_batch"
  | "export"
  | "company_data_reprocessed"
  | "config_change"
  // Versa (ponto de encaixe 15): a base de um relatório Versa ficou pronta.
  | "versa_report"
  // A consultoria de regras com IA terminou (só pra quem pediu).
  | "rule_review"
  // O provedor de IA recusou por crédito ou cota (só pro Máster).
  | "ai_quota_exhausted";

export type AppNotification = {
  id: number;
  kind: AppNotificationKind;
  level: "success" | "warning" | "error" | "info";
  title: string;
  body: string | null;
  company_id: string | null;
  company_name: string | null;
  subject_id: string | null;
  created_at: string;
  read: boolean;
  /** Quem causou (mudança de configuração) — o "por quem" da RN-18. */
  actor_name?: string | null;
};

export type AppNotificationList = {
  items: AppNotification[];
  unread_count: number;
};

/** Marca no contrato real (`GET /api/brands`). */
export type BrandOut = {
  id: number;
  name: string;
  is_main_brand: boolean;
  terms: string | null;
  /** Derivado de `deleted_on IS NULL` — é o interruptor da tela.
   *  `deleted_on` vem junto pra ela poder dizer "desativada em". */
  is_active: boolean;
  deleted_on: string | null;
};

/** Filtros de menção — todos opcionais, todos aplicados em SQL pelo
 * servidor (`web_api/infrastructure/persistence/mentions.py`). */
/** O que `POST /mentions/reference-sets` devolve (2026-10-03): a lista
 *  colada, resolvida e GUARDADA no servidor. `id` é o que vira
 *  `referenceSetId` do `MentionQuery`; `unmatched` é AMOSTRA das
 *  referências sem par (as primeiras, na ordem colada) e
 *  `unmatched_count` é o total. A lista vence em `expires_at`. */
export type MentionReferenceSet = {
  id: number;
  reference_count: number;
  post_count: number;
  unmatched: string[];
  unmatched_count: number;
  expires_at: string;
  limit: number;
};

export type MentionQuery = {
  page?: number;
  size?: number;
  /** A ordem da lista (2026-10-02); ausente ou padrão não vai na URL. */
  order?: MentionOrder;
  q?: string;
  /** Onde `q` procura (2026-10-14); só vai na URL com `q` e fora do padrão `all`. */
  q_in?: MentionSearchField;
  /** Busca de texto DENTRO das justificativas da classificação (2026-10-20),
   *  da página "Justificativas". Só dela: não vai pro filtro salvo nem pro
   *  recorte do relatório. */
  whyQ?: string;
  /**
   * Cada dimensão é uma lista com flag de exclusão, desde 2026-08-27 —
   * o mesmo modelo do dashboard. Na URL o parâmetro continua no
   * SINGULAR (`?polarity=X&polarity=Y`), então deep link antigo com um
   * valor só continua valendo sem caso especial.
   *
   * As dimensões de CATÁLOGO viajam por id (a FK viva); as de
   * vocabulário e valor cru, por string. O sentinela `__none__`
   * seleciona o bucket "sem valor" — em polaridade ele é o antigo
   * `only_unclassified`.
   */
  polarities?: string[];
  excludePolarities?: boolean;
  approaches?: string[];
  excludeApproaches?: boolean;
  postSources?: string[];
  excludePostSources?: boolean;
  /** Canal de rede, por ID (2026-11-03; era o nome cru do post, e caixa
   *  diferente entre o cadastro e o post deixava o canal de fora). */
  channelIds?: number[];
  excludeChannels?: boolean;
  brandIds?: number[];
  excludeBrands?: boolean;
  /** Só-imprensa: incluir um valor recorta a imprensa e deixa a rede
   *  passar inteira (o servidor decide isso — ver `half_scope_condition`). */
  tiers?: string[];
  excludeTiers?: boolean;
  formats?: string[];
  excludeFormats?: boolean;
  /** Só-rede, espelho do par acima. Porte do publicador por ID da faixa
   *  (2026-11-03; era o nome, que não casava por caixa). */
  publisherTierIds?: number[];
  excludePublisherTiers?: boolean;
  publisherGroupIds?: number[];
  excludePublisherGroups?: boolean;
  /** Veículo (2026-10-19): por ID, só-imprensa — recorta a imprensa e
   *  deixa a rede passar inteira (o espelho de `channels`, que é só-rede). */
  vehicleIds?: number[];
  excludeVehicles?: boolean;
  /** Clipadora (2026-10-31): `clipping_service` repetido, inclusão pura
   *  (sem modo excluir). É a clipadora do UPLOAD do post — o legado
   *  migrado vem como `legado`, sem upload. */
  clippingServices?: string[];
  themeIds?: number[];
  /** "Sem subtema" (2026-11-03): o bucket que não é id e vai como flag
   *  própria, na forma de `noKeyMessage`. A exclusão vale pros dois. */
  noTheme?: boolean;
  excludeThemes?: boolean;
  themeGroupIds?: number[];
  excludeThemeGroups?: boolean;
  spokespersonIds?: number[];
  excludeSpokespersons?: boolean;
  /** Mensagem-chave (2026-11-01), por id; `noKeyMessage` é o bucket "Sem
   *  mensagem-chave", que não é id e vai como flag própria. A exclusão vale
   *  pros dois (só o "sem" com exclusão = "só as que têm alguma"). */
  keyMessageIds?: number[];
  noKeyMessage?: boolean;
  excludeKeyMessages?: boolean;
  /** Tipo de mídia (2026-11-01): `print`/`online`/`tv`/`radio` e `__none__`
   *  ("não informado"). Só imprensa: recorta a imprensa e deixa a rede
   *  passar inteira. */
  mediaTypes?: string[];
  excludeMediaTypes?: boolean;
  /** "Editado por": o `sub` de quem corrigiu a classificação à mão.
   *  Dimensão da TELA — não viaja pro recorte do relatório. */
  editorIds?: string[];
  excludeEditors?: boolean;
  /** `true` lista SÓ as menções descartadas (o caminho de recuperação
   *  além do "desfazer"). Ausente/false = só o que está na tela. */
  discarded?: boolean;
  /** Lixeira (2026-10-18): só as descartadas há menos de 15 dias; implica `discarded`. */
  inTrash?: boolean;
  /** Revisão (2026-10-03): ausente = as duas; `true` = só revisadas;
   *  `false` = só as que faltam revisar. */
  reviewed?: boolean;
  /** Edição (2026-10-07): ausente = as duas; `true` = só as que alguém
   *  mexeu (há linha na trilha); `false` = só as intocadas. */
  edited?: boolean;
  /** Citação no título (2026-10-22), só imprensa: `true` = que citam,
   *  `false` = que não citam; ausente = sem filtro. A rede passa
   *  inteira e a imprensa sem o dado (anterior à entrega) fica fora. */
  titleCitation?: boolean;
  /** Subtema em destaque (2026-11-01): `true` = só as que têm, `false` =
   *  só as que não têm; ausente = sem filtro. */
  themeHighlight?: boolean;
  /** Post em mais de um cliente (2026-11-01): `true` = o post também é
   *  menção de outra marca do escopo; `false` = só desta. */
  sharedPost?: boolean;
  dateFrom?: string;
  dateTo?: string;
  minIcena?: number;
  /** Valoração mínima, em REAIS INTEIROS (o servidor recusa 0 e
   *  fracionário). Meio-escopo: recorta a imprensa e deixa a rede
   *  passar inteira. */
  minAdValue?: number;
  /** Só as menções criadas por estes uploads. Vazio = não filtrar. */
  uploadIds?: number[];
  /** A LISTA COLADA (2026-10-03): o número que
   *  `createMentionReferenceSet` devolveu. Um inteiro só — os posts
   *  moram no servidor. Efêmero como `uploadIds` (fora do filtro
   *  salvo), mas VIAJA pro relatório. */
  referenceSetId?: number;
  /**
   * De quem são as menções: `own` (padrão, só as suas marcas),
   * `competitors` (só os concorrentes ativos) ou `all`.
   *
   * Vocabulário fechado e NUNCA uma lista de `company_id` — o servidor
   * é quem resolve quais empresas você enxerga. Se aceitasse id,
   * mandar o de outro cliente leria as menções dele.
   */
  scope?: "own" | "competitors" | "all";
};

/**
 * Os agregados da janela — o topo da Visão Geral e da Home.
 *
 * Substituiu o `DashboardSummary` do backend descartado (que chamava
 * `/dashboard/summary`, rota que nunca existiu aqui: a chamada falhava
 * em silêncio e a seção nascia vazia).
 *
 * `total_mentions` conta MENÇÕES (post × marca); alcance e interações
 * contam POSTS — engajamento é fato do post físico, então um post que
 * cita duas marcas tem duas menções e uma curtida só.
 *
 * `average_cena` é `null` quando nada foi pontuado na janela, e esse é o
 * estado PERMANENTE de uma empresa só de rede social: post de rede só
 * ganha nota dentro de relatório. A tela diz isso, não mostra traço mudo.
 */
export type PolarityCount = {
  name: string;
  label: string;
  tone: PolarityTone;
  palette: PolarityPalette;
  count: number;
};

export type MentionSummary = {
  window_days: number;
  /** Limites do `WHERE` em hora de parede (fim exclusivo). A tela escreve
   *  o período por `first_day`/`last_day`. */
  window_start: string;
  window_end: string;
  /** "AAAA-MM-DD": a janela são `window_days` dias inteiros terminando no
   *  dia do último dado (2026-10-19), não hoje. */
  first_day: string;
  last_day: string;
  /** A menção mais recente, em hora de parede; `null` sem dado nenhum. */
  latest_published_at: string | null;
  total_mentions: number;
  total_reach: number;
  total_interactions: number;
  average_cena: number | null;
  positive_count: number;
  negative_count: number;
  neutral_count: number;
  unclassified_count: number;
  /** Uma linha por polaridade do domínio, na ordem do domínio e com as
   *  zeradas incluídas. Nome fora do domínio e polaridade nula NÃO
   *  entram: estão em `unclassified_count`. */
  polarity_counts: PolarityCount[];
  press_total_mentions: number;
  press_distinct_outlets: number;
  press_average_cena: number | null;
  /** Valoração somada das matérias da janela, por POST FÍSICO
   *  (matéria multimarca conta uma vez). `null` = nenhuma matéria
   *  com valoração; a tela mostra "—", nunca R$ 0. */
  press_total_ad_value: number | null;
  /** Alcance de imprensa (beta): Σ por veículo do maior valor que a
   *  planilha informou — cada veículo uma vez na janela. SEPARADO de
   *  `total_reach` (rede), nunca somado. `null` = nenhuma matéria da
   *  janela trouxe alcance: a tela mostra "—", não 0. */
  press_reach: number | null;
  /** Saldo reputacional: SOMA do ESCena (nota antes da reescala, de −280
   *  a +280 por menção) das menções da janela COM nota. Sem teto: cresce
   *  com o volume. `null` = nenhuma menção com nota ("—", nunca 0). No
   *  vivo só imprensa tem nota. */
  cena_balance: number | null;
  /** Quantas menções da janela têm nota (o denominador do saldo). */
  scored_mentions: number;
};

/** A chave do bucket "sem a dimensão" — mesma constante do servidor
 * (`web_api/ports.py::DASHBOARD_NULL_KEY`). Vem na resposta e volta como
 * filtro: é o que torna a fatia "Não informado" clicável. */
export const DASHBOARD_NULL_KEY = "__none__";

export type DashboardMetric = {
  key: string;
  label: string;
  count: number;
  reach: number;
  interactions: number;
  average_cena: number | null;
  /** Soma da valoração da fatia. `null` = nenhuma matéria valorada
   *  aqui, e NUNCA "vale R$ 0". Nos breakdowns N:N (tema, grupo-tema,
   *  porta-voz) uma matéria com dois temas soma nos dois — as fatias
   *  passam do total. O alcance também passa, por outro motivo: o mesmo perfil
   *  conta uma vez em cada fatia. */
  total_ad_value: number | null;
  /** Menções da fatia por polaridade do domínio (chave de wire,
   *  `POSITIVO`…), mais `__none__` pras sem polaridade — somam `count`.
   *  É o que desenha as barras empilhadas por sentimento. */
  by_polarity: Record<string, number>;
  /** Só em `press_channel_media` (o tipo de mídia, `MEDIA_TYPES`) e em
   *  `authors` (o canal da rede). */
  parent: string | null;
};

export type DashboardTimelinePoint = {
  /** Primeiro dia do bucket (ISO). `label` já vem formatado do servidor. */
  period: string;
  label: string;
  mentions: number;
  reach: number;
  interactions: number;
  average_cena: number | null;
  total_ad_value: number | null;
  by_polarity: Record<string, number>;
  /** Menções de imprensa do período e as que citaram a marca no título. */
  press_mentions: number;
  title_mentions: number;
};

/** Menções de imprensa de um porta-voz num período. */
export type DashboardSpokespersonPoint = { period: string; key: string; count: number };

/** Uma célula do mapa de intensidade: 0 = domingo, hora de parede. */
export type DashboardHeatCell = { day: number; hour: number; count: number };

export type DashboardMatrixPoint = {
  key: string;
  label: string;
  count: number;
  /** Relevância do veículo, 0 a 5. */
  x: number;
  /** Relevância do conteúdo com o sinal do sentimento, -5 a 5. */
  y: number;
  average_cena: number | null;
};

export type DashboardMatrix = { press: DashboardMatrixPoint[]; network: DashboardMatrixPoint[] };

/** Os textos da Análise de Temas, por relatório salvo e recorte de temas. */
export type ThemeNoteRecorte = Partial<Record<"themeGroups" | "themes" | "sources", { values: string[]; exclude: boolean }>>;

export type ThemeNote = {
  recorte: ThemeNoteRecorte;
  positive_text: string;
  negative_text: string;
  updated_by_name: string | null;
  updated_at: string;
};

export type DashboardTopMention = {
  /** É o `post_company_link_id` — abre o drawer de menção que já existe. */
  mention_id: number;
  title: string | null;
  channel: string | null;
  source: string | null;
  link: string | null;
  published_at: string;
  polarity: string | null;
  interactions: number;
  reach: number;
  /** `null` = menção sem nota (neutra sem peso cadastrado). */
  cena: number | null;
  /** O começo do texto, já em uma linha — o que a lista mostra quando a
   *  publicação não tem título (post de rede quase nunca tem). */
  excerpt?: string | null;
};

/**
 * O dashboard de um relatório. Cada lista é um GROUP BY de uma dimensão
 * CONGELADA na foto — nada aqui vem de tabela viva, então o número não
 * muda depois de uma correção manual.
 *
 * `previous_mentions` é `null` quando o intervalo anterior não cabe
 * inteiro na janela do relatório: "não comparável", diferente de zero.
 */
export type SocialReportDashboard = {
  generated_at: string;
  total_mentions: number;
  total_reach: number;
  total_interactions: number;
  average_cena: number | null;
  ultraperformance_count: number;
  /** Menções sem nota — a média acima é de `total_mentions` menos isto. */
  unscored_count: number;
  /** Veículos de imprensa distintos — o `channel` congelado é o nome do
   * veículo pra imprensa. */
  press_vehicle_count: number;
  previous_mentions: number | null;
  period_start: string | null;
  period_end: string | null;
  /** Soma da valoração congelada — imprensa. Conta por (post × marca):
   *  matéria que cita duas marcas soma duas vezes. O alcance não: deduplica
   *  por perfil. */
  total_ad_value: number | null;
  /** Alcance de imprensa (beta): cada veículo uma vez, com o maior valor
   *  da planilha; separado de `total_reach` (rede). `null` = não apurado
   *  (foto anterior à coluna, ou planilha sem ela). Só o total: as fatias
   *  e a série temporal não levam alcance de imprensa ainda. */
  press_reach: number | null;
  /** Saldo reputacional da foto: soma do ESCena das menções COM nota
   *  (`total_mentions - unscored_count`). `null` = nenhuma com nota. */
  cena_balance: number | null;
  /** Perfis de rede distintos que publicaram no recorte — (canal,
   *  autor), a identidade de `authors` e do alcance. */
  network_profile_count: number;
  sentiments: DashboardMetric[];
  sources: DashboardMetric[];
  channels: DashboardMetric[];
  approaches: DashboardMetric[];
  formats: DashboardMetric[];
  tiers: DashboardMetric[];
  influencer_types: DashboardMetric[];
  publisher_groups: DashboardMetric[];
  brands: DashboardMetric[];
  theme_groups: DashboardMetric[];
  themes: DashboardMetric[];
  /** `null` — e não `[]` — quando o relatório é ANTERIOR à dimensão
   *  porta-voz. A distinção é o ponto: `[]` quer dizer "ninguém citou",
   *  `null` quer dizer "esta foto não tem o dado". As duas
   *  renderizariam o mesmo vazio, e a segunda culparia o filtro de quem
   *  está olhando. */
  spokespersons: DashboardMetric[] | null;
  timeline: DashboardTimelinePoint[];
  top_mentions: DashboardTopMention[];
  /** Matérias de imprensa que citaram a marca no título, e sobre quantas
   *  a pergunta foi feita (base zero = a foto não tem o componente). */
  title_mentions: number;
  title_mention_base: number;
  /** O canal separado por fonte: veículos de imprensa e redes. */
  press_channels: DashboardMetric[];
  network_channels: DashboardMetric[];
  /** Perfis de rede que mais publicaram (`parent` = a rede). */
  authors: DashboardMetric[];
  /** `false` = foto anterior à hora, ao tipo de mídia e à UF congelados
   *  (2026-10-25): as quatro listas abaixo vêm `null`. */
  extended_dimensions: boolean;
  media_types: DashboardMetric[] | null;
  states: DashboardMetric[] | null;
  /** Veículo × tipo de mídia (`parent` = o tipo). */
  press_channel_media: DashboardMetric[] | null;
  heatmap: DashboardHeatCell[] | null;
  /** `null` quando a foto não tem porta-voz. */
  spokesperson_timeline: DashboardSpokespersonPoint[] | null;
  /** As fontes que o RELATÓRIO tem (`press`/`network`), sem o recorte de
   *  leitura — com "Redes" escolhido, `sources` só fala de rede. */
  report_sources: string[];
};

/**
 * Cada faceta é lista + flag de exclusão, independente das outras.
 *
 * `tiers` (veículo) e `formats` são só-imprensa; `influencerTypes`
 * (porte por seguidores) é só-rede — e filtrar por eles recorta SÓ essa
 * metade, deixando a outra passar inteira (decisão da dona,
 * 2026-08-27). Não é interseção: marcar "Tier 1" num relatório misto não
 * apaga a rede. `influencerTypes` não se chama `publisherTypes` de
 * propósito: o contrato antigo usava esse nome pra press|social, e um
 * deep link velho viraria filtro vazio silencioso.
 *
 * Desde 2026-09-08 este tipo serve QUATRO rotas — os dois dashboards e
 * os dois detalhes de relatório —, por isso o recorte está separado do
 * que é desenho do gráfico (`granularity`/`weekStart`, que só o
 * dashboard manda).
 */
/** De quem são as menções — da lista de menções ou de um relatório:
 *  a empresa, os concorrentes que ela monitora, ou os dois. Espelha
 *  `core/vocabulary.py::MENTION_SCOPES`. */
export type MentionScope = "own" | "competitors" | "all";

export type SnapshotFacetFilters = {
  dateFrom?: string;
  dateTo?: string;
  /** De quem são as menções LIDAS da foto (relatório misto). Ausente =
   *  só a empresa, que é o padrão do servidor mesmo num relatório com
   *  concorrente. */
  mentionScope?: MentionScope;
  sentiments?: string[];
  excludeSentiments?: boolean;
  sources?: string[];
  excludeSources?: boolean;
  channels?: string[];
  excludeChannels?: boolean;
  approaches?: string[];
  excludeApproaches?: boolean;
  formats?: string[];
  excludeFormats?: boolean;
  tiers?: string[];
  excludeTiers?: boolean;
  /** Tipo de mídia CONGELADO na foto (2026-11-01), só imprensa. */
  mediaTypes?: string[];
  excludeMediaTypes?: boolean;
  influencerTypes?: string[];
  excludeInfluencerTypes?: boolean;
  publisherGroups?: string[];
  excludePublisherGroups?: boolean;
  themeGroups?: string[];
  excludeThemeGroups?: boolean;
  themes?: string[];
  excludeThemes?: boolean;
  spokespersons?: string[];
  excludeSpokespersons?: boolean;
  /** Valoração mínima, em REAIS INTEIROS (o servidor recusa 0 e
   *  fracionário). Meio-escopo: recorta a imprensa e deixa a rede
   *  passar inteira — não é interseção como `tiers`/`formats`. */
  minAdValue?: number;
  /** Marca é a única faceta cujo valor é o ID em texto: na foto ela é
   *  coluna própria, não nome congelado. É o mesmo `key` que o breakdown
   *  de marca devolve, então o que a tela manda é o que ela recebeu. */
  brands?: string[];
  excludeBrands?: boolean;
};

/** O recorte MAIS o desenho do gráfico. `weekStart` é o dia-âncora da
 *  semana (0=domingo … 6=sábado). */
export type SocialReportDashboardFilters = SnapshotFacetFilters & {
  granularity?: "day" | "week" | "month";
  weekStart?: number;
};

// `UploadRecord` (shape do backend descartado) saiu em 2026-08-12 — o
// `OverviewPage`, seu último consumidor citado, já usa `UploadSummary` do
// contrato real há tempos.

// ── Contrato REAL de uploads (web_api) — ver web_api/infrastructure/http/routes/uploads.py ──

export type UploadTemplateInfo = {
  /** O que o envio manda de volta; o `name` é rótulo, renomeável. */
  id: number;
  name: string;
  post_source: string;
  required_columns: string[];
  /** As dimensões de classificação que o template mapeia e que o envio
   *  pode tomar da planilha (2026-10-07) — decide se o botão
   *  "Configurações avançadas" aparece e o que o pop-up oferece. */
  importable_dimensions: string[];
};

/** Uma linha do cadastro de templates (Administração → Templates de
 *  upload). O `id` é a referência viva que o upload guarda; o `name` é
 *  rótulo, renomeável. */
export type UploadTemplateColumn = {
  raw_column_name: string;
  canonical_field: string | null;
  priority: number;
  extract_pattern: string | null;
  join_with: string | null;
  is_required: boolean;
};

export type UploadTemplateAdmin = {
  id: number;
  name: string;
  post_source: string;
  scraper: string | null;
  is_active: boolean;
  columns: UploadTemplateColumn[];
  created_by: string;
  created_at: string;
  updated_by: string | null;
  updated_at: string | null;
  deleted_on: string | null;
  /** Tipo da credencial do scraper cadastrada, ou null (sem credencial). */
  credential_auth_kind?: string | null;
};

/** A credencial do scraper como a tela a vê: nunca o segredo, só o hint. */
export type UploadTemplateCredential = {
  auth_kind: string;
  base_url: string | null;
  login: string | null;
  secret_hint: string;
  updated_by: string;
  updated_at: string;
};

/** `secret` ausente (ou vazio) mantém o gravado; o hint NUNCA volta no corpo. */
export type UploadTemplateCredentialInput = {
  auth_kind: string;
  base_url?: string | null;
  login?: string | null;
  secret?: string;
};

/** O corpo de criar/editar — a definição INTEIRA, sempre. */
export type UploadTemplateInput = {
  name: string;
  post_source: string;
  scraper: string | null;
  columns: UploadTemplateColumn[];
};

export type UploadTemplateVocabulary = {
  canonical_fields: string[];
  scrapers: string[];
  post_sources: string[];
};

/** Uma das 13 linhas do prompt GERAL da instalação. Editar vale para toda
 *  empresa que segue a linha, a partir do próximo upload; as linhas que uma
 *  empresa personalizou não mudam (2026-10-27). */
export type PromptDefault = {
  id: number;
  task: string;
  source: string;
  position: number;
  header: string;
  extra_header: string | null;
  output_template: string;
  temperature: number;
  system_message: string | null;
  updated_at: string | null;
  updated_by: string | null;
  /** Nome de hoje no cadastro; `null` pra `seed` e quem está fora dele. */
  updated_by_name?: string | null;
  /** Quantas empresas personalizaram esta linha (não seguem mais o geral). */
  overridden_by: number;
};

/** O corpo do PUT: task, fonte e posição não entram (a posição é o papel
 *  da linha). */
export type PromptDefaultInput = {
  header: string;
  extra_header: string | null;
  output_template: string;
  temperature: number;
  system_message: string | null;
};

/** O que `check` mede antes de gravar o geral: empresas que SEGUEM a linha
 *  e ficariam com marcador de premissa obrigatória sem premissa cadastrada
 *  (parariam de classificar). */
/** Uma empresa sem premissa pra algum marcador obrigatório do texto. */
export type PromptDefaultBlockedCompany = {
  company_id: string;
  company_name: string;
  /** Os marcadores do texto NOVO que ficariam sem premissa. */
  placeholders: string[];
};

export type PromptDefaultCheck = {
  /** Classificam hoje e passariam a parar com o texto novo — o que a
   *  edição causa, e só isso pede confirmação. */
  newly_blocked: PromptDefaultBlockedCompany[];
  /** Já não classificam hoje e continuariam paradas. */
  already_blocked: PromptDefaultBlockedCompany[];
  /** Estão paradas hoje e voltariam a classificar. */
  unblocked: number;
  /** Quantas empresas seguem a linha. */
  following: number;
  /** Quantas personalizaram (não mudam). */
  overridden: number;
};

export type UploadTemplateCheck = {
  columns: string[];
  row_count: number;
  missing_columns: string[];
  detected_by: string | null;
};

export type InspectResult = {
  filename: string;
  columns: string[];
  row_count: number;
  detected_upload_template_id: number | null;
};

export type SubmittedUpload = {
  upload_id: number;
  filename: string;
  status: string;
  clipping_service: string | null;
  total_rows: number;
  missing_columns: string[];
  message: string | null;
};

export type UploadSummary = {
  id: number;
  /** Nome do arquivo enviado; `null` em upload anterior ao registro do
   *  campo — use `uploadLabel()` pra cair no `#id · clipadora`. */
  filename: string | null;
  clipping_service: string | null;
  status: string;
  total_rows: number | null;
  processed_rows: number;
  error_count: number;
  pending_correction_count: number;
  /** Dos pendentes, quantos já foram mandados reprocessar e esperam o
   *  worker. A tela liga o polling por ele: a fila é paginada, então a
   *  página carregada não vê todo item despachado. */
  dispatched_correction_count: number;
  /** Quando a última linha foi REPROCESSADA na fila de correção (carimbo
   *  de verdade, UTC); `null` se nenhuma foi. `finished_at` não muda com o
   *  reprocessamento — esta é a hora à parte. */
  last_reprocessed_at: string | null;
  /** Menções geradas: uma linha pode virar várias (post citando duas
   *  marcas) ou nenhuma (não casou termo) — não sai de `processed_rows`. */
  mention_count: number;
  /** Posts físicos distintos; menções acima disso = post citando mais
   *  de uma marca. */
  distinct_post_count: number;
  /** Linhas em que nenhum termo de marca bateu: o pulo de upload anterior
   *  a 2026-09-14 MAIS os itens pendentes da fila de correção cujo último
   *  erro é esse (estes também entram em `pending_correction_count`). */
  no_brand_match_count: number;
  /** Linhas limpas que não viraram menção: o post já existia de um
   *  upload anterior (a menção pertence àquele upload). */
  already_ingested_count: number;
  /** Linhas de um canal/veículo marcado como "ignorar" no cadastro.
   *  É PULO deliberado — a empresa disse que esse cadastro não entra —,
   *  então não é aviso nem pendência. */
  catalog_ignored_count: number;
  /** ISO-8601 em UTC. `started_at` é nulo enquanto espera na fila;
   *  `finished_at`, enquanto não termina. */
  uploaded_at: string;
  started_at: string | null;
  finished_at: string | null;
  /** Se o upload publicou progresso há pouco.
   *
   *  `status === "STARTED"` NÃO responde isso: quem carimba esse status
   *  é o planejamento (que fatia o arquivo em faixas, em segundos), e o
   *  upload fica nele enquanto espera a vez de cada faixa. Sem este
   *  campo, vários arquivos enfileirados aparecem todos como
   *  "Processando" quando só um está consumindo linhas.
   *
   *  Vem resolvido do servidor de propósito: a conta depende de comparar
   *  com o relógio, e o do navegador não é o mesmo do banco. */
  is_advancing: boolean;
  /** As dimensões tomadas DA PLANILHA neste envio (2026-10-08); vazio =
   *  tudo pelo Reputaition. Ausente em resposta antiga. */
  imported_dimensions?: ImportDimension[];
  /** Fonte do template do upload (`press`/`network`), quando ainda há
   *  template — decide a fonte da mensagem-chave a criar. */
  post_source?: "press" | "network" | null;
};

export type PageOf<T> = {
  items: T[];
  total: number;
  page: number;
  size: number;
};

/** `GET/PATCH /me/preferences` (2026-10-31). */
export type MyPreferences = {
  mention_text_view: "post" | "summary";
};


/** A página de `GET /mentions`. `total` é `null` quando a tela pediu
 *  `withTotal: false` — o número vem depois, de `countMentions`. */
export type MentionPage = Omit<PageOf<ApiMention>, "total"> & {
  total: number | null;
};

/** Uma correção manual registrada. `diff` traz só os campos que
 * mudaram, cada um com `old`/`new`. */
export type ClassificationEdit = {
  id: number;
  diff: Record<string, { old: unknown; new: unknown }>;
  /** `login_user_id` do Keycloak (o `sub`). */
  changed_by: string;
  /** Foto do nome do token na hora da edição; `null` nas edições
   *  anteriores a 2026-09-21 e no stub de header. A tela cai no
   *  `changed_by`. */
  changed_by_name: string | null;
  changed_at: string;
  /** `{campo: {id: nome}}` do que o diff cita por id — temas, mensagens,
   *  porta-vozes e tema em destaque —, resolvido pelo servidor na
   *  leitura (inclusive o desativado). Ausente nas respostas antigas. */
  labels?: Record<string, Record<string, string>>;
  /** De onde veio a linha: `manual`, `bulk_edit`, `ai_reprocess` ou
   *  `revert` (volta de versão, 2026-10-31). `null` nas anteriores a
   *  2026-10-05, que eram todas edição manual. */
  kind?: "manual" | "bulk_edit" | "ai_reprocess" | "revert" | null;
  /** O reprocessamento por IA que gerou a linha, quando foi um. */
  ai_reprocess_run_id?: number | null;
  /** O lote de edição que gerou a linha, quando foi um (2026-10-22). */
  bulk_edit_id?: number | null;
  /** Se a linha é de um lote de DESFAZER, o lote que ele desfez. */
  bulk_edit_undo_of?: number | null;
  /** Numa linha `revert` (2026-10-31), a edição pra qual a menção voltou
   *  e o carimbo dela; os dois nulos numa `revert` = a versão original. */
  reverted_to_id?: number | null;
  reverted_to_at?: string | null;
};

/** O que a volta a uma versão devolve: o `diff` aplicado (vazio = já
 *  estava na versão) e os campos que mudaram depois dela e NÃO voltam. */
export type ClassificationRevertResult = {
  diff: Record<string, { old: unknown; new: unknown }>;
  skipped: string[];
};

/** Uma linha do histórico de edições da EMPRESA: a edição de sempre mais
 *  a menção que ela editou (`GET /mentions/classification-history`). */
export type MentionEditEntry = ClassificationEdit & {
  mention: {
    id: number;
    title: string | null;
    brand_id: number;
    brand_name: string | null;
    channel: string | null;
    post_source: string | null;
    published_at: string | null;
    link: string | null;
  };
};

export type MentionEditPage = {
  items: MentionEditEntry[];
  total: number;
  page: number;
  size: number;
};

/** Uma opção do filtro "Editado por": o `sub` e a foto mais recente do
 *  nome de quem já corrigiu classificação na empresa. */
export type MentionEditor = {
  id: string;
  name: string | null;
};

/** O modo de uma lista no patch em lote — ver `core/bulk_edit/patch.py`. */
export type BulkListMode = "add" | "remove" | "replace";

/** O patch da edição em lote, como o servidor o lê. Dimensão ausente
 *  não mexe; `{ value: null }` tira a classificação. */
export type BulkPatchWire = {
  polarity?: { value: string | null };
  approach?: { value: string | null };
  themes?: { mode: BulkListMode; ids: number[] };
  key_messages?: { mode: BulkListMode; ids: number[] };
  spokespersons?: { mode: BulkListMode; ids: number[] };
  /** Marcar (`true`) ou desmarcar (`false`) como revisada. */
  reviewed?: { value: boolean };
  /** O tema em destaque. Vários ids porque tema é cadastro por marca:
   *  cada menção recebe o destaque no id que ELA tem. */
  highlight?: { theme_ids: number[] };
  /** Descartar (`true`, motivo opcional) ou restaurar (`false`) — lixeira, 2026-10-18. */
  discarded?: { value: boolean; reason?: string };
  /** Só o lote de DESFAZER carrega isto: o id do lote que ele desfaz. */
  undo_of?: number;
};

/** Uma marca pra onde uma menção pode ser vinculada à mão.
 *  `competitor_name` nulo = marca da própria empresa. */
export type LinkTarget = {
  brand_id: number;
  brand_name: string;
  competitor_name: string | null;
  /** O id do vínculo de concorrente — é com ele que a tela busca o
   *  catálogo do concorrente. Nulo nas marcas da própria empresa. */
  competitor_id: number | null;
};

/** Uma aba do pop-up de vincular, como o servidor a lê. Campo ausente =
 *  sem aquela classificação. */
export type LinkTargetWire = {
  brand_id: number;
  polarity?: string;
  approach?: string;
  theme_ids?: number[];
  key_message_ids?: number[];
  spokesperson_ids?: number[];
};

export type LinkMentionsResult = {
  created: number;
  already_linked: number;
  already_linked_discarded: number;
  discarded_sources: number;
  outcomes: Array<{
    source_mention_id: number;
    brand_id: number;
    outcome: "created" | "exists" | "exists_discarded";
    mention_id: number;
  }>;
};

/** Um tema presente em TODAS as menções do alvo de um lote, por nome,
 *  com os ids que o carregam (um por marca). */
export type CommonTheme = { name: string; theme_ids: number[] };
/** Uma marca do alvo do lote; `competitor_id` é o id do VÍNCULO de
 *  concorrente (nulo = marca da própria empresa). */
export type BulkTargetBrand = {
  brand_id: number;
  brand_name: string;
  company_id: string;
  competitor_id: number | null;
  competitor_name: string | null;
};
export type BulkTargetSummary = {
  brands: BulkTargetBrand[];
  common_themes: CommonTheme[];
};

/** O "Resumo da seleção" da visão Justificativas (2026-10-20): quantas
 *  vezes cada polaridade, abordagem, tema e mensagem-chave aparecem no
 *  alvo. `name`/`value` nulos são "sem valor". Temas e mensagens vêm só
 *  com os maiores (`*_truncated` diz que havia mais). */
export type SelectionBreakdown = {
  total: number;
  polarities: Array<{ name: string | null; count: number }>;
  approaches: Array<{ value: string | null; count: number }>;
  themes: Array<{ name: string; count: number; group_name: string | null }>;
  themes_truncated: boolean;
  key_messages: Array<{ name: string; count: number }>;
  key_messages_truncated: boolean;
  /** Ligações menção × tema por origem; `rule` soma publicador e veículo. */
  theme_sources: { term: number; ai: number; rule: number; imported: number; none: number };
};

/** O que toda proposta da consultoria de regras traz. `before` é SEMPRE o
 *  texto gravado (nunca o que o modelo copiou); `after` é o proposto. */
type RuleReviewProposalBase = {
  why: string;
  /** As menções enviadas que motivaram a proposta. */
  mention_ids: number[];
  before: string;
  after: string;
  target_label: string;
};

/** Uma proposta da consultoria (2026-10-21). O `kind` decide o resto. O
 *  `rule_type` é o nome do BLOCO como o marcador do prompt o escreve
 *  (`positivo`, `global`, `protagonista`…). */
export type RuleReviewProposal = RuleReviewProposalBase &
  (
    | {
        kind: Extract<RuleReviewProposalKind, "rule_update" | "rule_deactivate">;
        rule_id: number;
        brand_id: number;
        rule_type: string;
        source: PostSource;
      }
    | { kind: "rule_create"; brand_id: number; rule_type: string; source: PostSource }
    | {
        kind: Extract<RuleReviewProposalKind, "theme_add_term" | "theme_remove_term">;
        theme_id: number;
        brand_id: number;
        /** UMA expressão de termos. */
        term: string;
      }
    | {
        kind: "theme_set_mode";
        theme_id: number;
        brand_id: number;
        classification_mode: ThemeClassificationMode;
        deterministic_scope: DeterministicScope;
      }
    | {
        kind: "theme_update_premise";
        theme_id: number;
        brand_id: number;
        field: "detection_assumptions" | "non_detection_assumptions";
      }
    | { kind: "key_message_update_rules"; key_message_id: number; brand_id: number; source: PostSource }
  );

/** O que o parse recusou: a tela mostra, nunca some em silêncio. */
export type RuleReviewDropped = { reason: string; proposal: Record<string, unknown> | null };

export type RuleReviewApplied = {
  index: number;
  applied_at: string;
  applied_by: string;
  applied_by_name: string | null;
  outcome: RuleReviewOutcome;
};

/** Uma consultoria de regras com IA ("Pedir melhorias à IA", 2026-10-21). */
export type RuleReview = {
  id: number;
  status: RuleReviewStatus;
  task: RuleReviewTask;
  comment: string;
  mention_ids: number[];
  /** O DONO da consultoria (2026-10-25): `null` = a configuração da
   *  empresa; preenchido = o vínculo do concorrente cuja configuração
   *  (a da sombra) foi analisada. O servidor o resolve pelas menções
   *  marcadas — a tela nunca o manda. É o id que vai em
   *  `onCompetitor(...)` na hora de aplicar. */
  competitor_id: number | null;
  competitor_name: string | null;
  requested_at: string;
  requested_by: string;
  requested_by_name: string | null;
  started_at: string | null;
  finished_at: string | null;
  /** O erro, quando `ERRORED`. */
  message: string | null;
  model: string | null;
  /** O parecer. */
  summary: string | null;
  proposals: RuleReviewProposal[];
  dropped: RuleReviewDropped[];
  applied: RuleReviewApplied[];
};

export type RuleReviewMentionLabel = {
  id: number;
  title: string | null;
  source: string;
  channel: string | null;
};

/** A consultoria com os rótulos das menções (só o detalhe os traz). */
export type RuleReviewDetail = RuleReview & { mentions: RuleReviewMentionLabel[] };

export type BulkEditStatus = "QUEUED" | "STARTED" | "FINISHED" | "ERRORED";

export type BulkEditCounters = {
  total: number;
  pending: number;
  changed: number;
  unchanged: number;
  failed: number;
};

/** Um lote de correção — espelha `BulkEditOut` em
 *  `web_api/infrastructure/http/routes/mentions.py`. */
export type BulkEdit = {
  id: number;
  status: BulkEditStatus;
  requested_at: string;
  requested_by: string;
  requested_by_name: string | null;
  started_at: string | null;
  finished_at: string | null;
  patch: BulkPatchWire;
  message: string | null;
  counters: BulkEditCounters;
  /** O lote que ESTE desfaz; `null` num lote comum (2026-10-22). */
  undo_of_id?: number | null;
  /** O lote que desfez este, quando já foi desfeito. */
  undone_by_id?: number | null;
  /** O servidor diz se dá pra desfazer; NÃO olha papel (a tela já é de
   *  Coordenador ou acima). */
  can_undo?: boolean;
  /** Por que não dá, na frase do servidor. */
  cannot_undo_reason?: string | null;
};

export type BulkEditHistoryPage = {
  items: BulkEdit[];
  total: number;
  page: number;
  size: number;
};

export type BulkEditFailure = {
  mention_id: number;
  message: string | null;
};

/** As tarefas que dá pra reprocessar com IA — o vocabulário do motor
 *  (`AI_REPROCESS_TASKS` em `core/vocabulary.py`). `spokesperson` não
 *  chama IA (é o casamento por nome), mas é parte da classificação
 *  automática. */
export type AiReprocessTask =
  | "polarity_intensity"
  | "role"
  | "themes"
  | "key_message"
  | "spokesperson";

export const AI_REPROCESS_TASKS: { value: AiReprocessTask; label: string }[] = [
  { value: "polarity_intensity", label: "Polaridade" },
  { value: "role", label: "Abordagem" },
  { value: "themes", label: "Subtemas" },
  { value: "key_message", label: "Mensagens-chave" },
  { value: "spokesperson", label: "Porta-vozes" }
];

export type AiReprocessRequest = {
  tasks: AiReprocessTask[];
  includeReviewed: boolean;
};

/** O que a tela mostra antes de gastar — espelha `AiReprocessEstimateOut`.
 *  `estimated_cost` é TETO em dólar (tarefa resolvida por regra não chama
 *  a IA); `null` = sem consumo com preço apurado pra comparar. */
export type AiReprocessEstimate = {
  total: number;
  eligible: number;
  skipped_reviewed: number;
  skipped_discarded: number;
  skipped_unclassified: number;
  estimated_cost: number | null;
  cost_basis_calls: number;
  max_items: number;
  active_run_id: number | null;
};

export type AiReprocessStatus = "QUEUED" | "STARTED" | "FINISHED" | "ERRORED";

/** Um pedido de exportação, como a página de Downloads o mostra
 *  (2026-10-15). `criteria` é o recorte de LEITURA congelado no clique, no
 *  vocabulário do servidor (`sentiments`, `channels`, `brands`…; só o que
 *  tem valor), e `criteria_labels` o nome dos itens que só têm id
 *  (hoje `brands`). Os instantes são carimbos de verdade (UTC). */
export type ExportRow = {
  id: number;
  kind: ExportKind;
  format: ExportFormat;
  /** `null` em `export_mentions`: o alvo é o recorte, não um registro. */
  subject_id: number | null;
  subject_label: string | null;
  criteria: Record<string, unknown>;
  criteria_labels: { brands?: Record<string, string> } | null;
  status: ExportStatus;
  requested_at: string;
  requested_by: string;
  requested_by_name: string | null;
  started_at: string | null;
  finished_at: string | null;
  row_count: number | null;
  file_name: string | null;
  file_size: number | null;
  expires_at: string | null;
  message: string | null;
  /** As colunas escolhidas no pedido (chaves do catálogo, na ordem da
   *  planilha); `null` = o colunas padrão. */
  columns: string[] | null;
  /** Se a primeira linha leva os nomes das colunas. */
  header: boolean;
  /** Tema e subtemas: uma coluna por tema ou o par "Tema" + "Subtemas". */
  theme_layout: ExportThemeLayout;
  /** Só o subtema em destaque de cada menção. */
  highlight_only: boolean;
};

/** Uma coluna do catálogo de `GET /exports/columns`. */
export type ExportColumn = {
  key: string;
  label: string;
  /** `default` = o colunas padrão; `extra` = dado atual da menção;
   *  `raw` = coluna da planilha da clipadora que o template não mapeia
   *  (chave `raw:<nome>`). */
  group: "default" | "extra" | "raw";
  note: string | null;
};

/** Uma planilha salva (seleção de colunas) da empresa. */
export type ExportColumnPreset = {
  id: number;
  name: string;
  columns: string[];
  header: boolean;
  theme_layout: ExportThemeLayout;
  highlight_only: boolean;
  created_by_name: string | null;
  created_at: string;
};

export type ExportPage = {
  items: ExportRow[];
  total: number;
  page: number;
  size: number;
};

/** O corpo do `POST /exports`. O recorte de leitura NÃO vai aqui: vai na
 *  querystring, pela mesma forma da lista de menções do relatório. */
export type CreateExportPayload = {
  kind: ExportKind;
  subject_id: number;
  format?: ExportFormat;
  /** Só `export_upload_rows`: as linhas do bronze a baixar. */
  only_row_indexes?: number[];
  /** Ausente = o colunas padrão. Não vale em `export_upload_rows`. */
  columns?: string[];
  /** Ausente = com cabeçalho. */
  header?: boolean;
  /** Ausente = `columns` (uma coluna por tema). */
  theme_layout?: ExportThemeLayout;
  /** Ausente = todos os subtemas. */
  highlight_only?: boolean;
};

/** O corpo do `POST /exports/mentions` (2026-10-30): o MESMO do
 *  `POST /social-reports/runs` (janela + recorte de `recorteToPayload`,
 *  inclusive `reference_set_id`) mais o formato. */
export type MentionsExportPayload = {
  window_start: string;
  window_end: string;
  format: ExportFormat;
  columns?: string[];
  header?: boolean;
  theme_layout?: ExportThemeLayout;
  highlight_only?: boolean;
  [dimension: string]: unknown;
};

export type AiReprocessRun = {
  id: number;
  /** `sample` só propõe; `apply` grava. */
  mode: "sample" | "apply";
  status: AiReprocessStatus;
  tasks: AiReprocessTask[];
  include_reviewed: boolean;
  filter_labels: Record<string, unknown> | null;
  sample_run_id: number | null;
  requested_at: string;
  requested_by: string;
  requested_by_name: string | null;
  started_at: string | null;
  finished_at: string | null;
  message: string | null;
  /** O recorte como foi pedido (`query` = querystring dos filtros,
   *  `mention_ids` = ids marcados). `null` em execução antiga. É o que
   *  deixa o aviso da prévia reabri-la no mesmo recorte. */
  target?: { query: string; mention_ids: number[] | null } | null;
  /** `ERRORED` que a fila ainda vai retomar — a conta é do servidor. */
  retrying?: boolean;
  counters: {
    total: number;
    pending: number;
    changed: number;
    unchanged: number;
    previewed: number;
    skipped: number;
    failed: number;
  };
};

export type AiReprocessItemOutcome =
  | "changed"
  | "unchanged"
  | "previewed"
  | "skipped_reviewed"
  | "skipped_discarded"
  | "skipped_conflict"
  | "failed";

/** Uma menção com desfecho: na amostra, o antes/depois proposto; na
 *  aplicação, o que mudou ou por que foi pulada. */
export type AiReprocessItem = {
  mention_id: number;
  outcome: AiReprocessItemOutcome | null;
  diff: Record<string, { old: unknown; new: unknown }> | null;
  message: string | null;
  /** A matéria, pra amostra dizer de qual menção se trata. */
  title?: string | null;
  channel?: string | null;
  published_at?: string | null;
};

/** Uma chamada ao provedor de IA, como ela saiu.
 *
 * `index` é a POSIÇÃO, não a tentativa: classificar temas manda o
 * catálogo em blocos, e cada bloco é uma chamada legítima — chamar isso
 * de "tentativa 12" descreveria trabalho normal como insistência.
 *
 * `response: null` com `error` preenchido é a chamada que FALHOU
 * (timeout, limite do provedor). Ela aparece na tela de propósito: é o
 * que explica um resultado degradado. */
export type AiTraceCall = {
  index: number;
  model: string | null;
  system_message: string | null;
  prompt: string | null;
  response: string | null;
  error: string | null;
  latency_ms: number;
  /** Texto cortado no teto por chamada. */
  truncated: boolean;
  /** Chamada que existiu mas cujo texto não coube no documento. */
  omitted: boolean;
};

export type AiTraceTask = {
  /** `polarity_intensity` | `role` | `themes` | `key_message`. */
  task: string;
  status: "available" | "expired";
  /** A resposta veio de outra menção com o mesmo texto neste upload. */
  reused: boolean;
  classified_at?: string | null;
  provider?: string | null;
  model?: string | null;
  method?: string | null;
  success?: boolean;
  error?: string | null;
  /** Por que não houve chamada de IA, quando não houve. */
  calls_reason?: string | null;
  parsed_result?: Record<string, unknown> | null;
  calls?: AiTraceCall[];
};

export type MentionAiTrace = {
  mention_id: number;
  status: "available" | "absent";
  /** A classificação foi corrigida à mão depois — o rastro explica o
   * que a IA disse, não o que está valendo hoje. */
  edited_manually: boolean;
  tasks: AiTraceTask[];
};

/**
 * Relatório CENA de redes sociais.
 *
 * O CENA de rede só fecha comparando cada post com os OUTROS (percentis
 * de uma janela móvel), então ele não sai pronto do upload como o de
 * imprensa — é calculado em tempo de relatório, pelo worker do `elt/`.
 * Daí a forma assíncrona: a tela cria uma EXECUÇÃO e acompanha por
 * polling até virar um rascunho.
 */
export type SocialReportRun = {
  id: number;
  brand_id: number | null;
  brand_name: string | null;
  window_start: string;
  window_end: string;
  /** QUEUED | STARTED | FINISHED | FINISHED_EMPTY | ERRORED */
  status: string;
  requested_at: string;
  requested_by: string;
  started_at: string | null;
  finished_at: string | null;
  /** Preenchido = esta execução foi um recálculo daquele rascunho. */
  target_draft_id: number | null;
  /** Preenchido = esta execução atualiza os dados daquele relatório
   *  SALVO (2026-10-05). */
  target_report_id?: number | null;
  /** Só existe depois de terminar — e fica `null` quando a janela não
   * tinha publicação nenhuma (`FINISHED_EMPTY`). */
  report_draft_id: number | null;
  scored_count: number | null;
  skipped_count: number | null;
  /** O recorte e seus rótulos vêm já na EXECUÇÃO, e não só no rascunho:
   *  entre o clique e o fim do processamento a tela precisa mostrar o
   *  que está sendo gerado, e o rascunho ainda não existe. */
  generation_criteria?: GenerationCriteria | null;
  generation_criteria_labels?: GenerationCriteriaLabels | null;
  /** Explica os desfechos que não são o comum: qual tipo de "vazio", ou
   * o erro que derrubou a execução. */
  message: string | null;
};

export type SocialReportAggregates = {
  post_count: number;
  /** Cru; a tela arredonda. `null` quando não há publicação. A média é
   * só das menções COM nota — `unscored_count` diz quantas ficaram de
   * fora. */
  avg_icena: number | null;
  ultraperformance_count: number;
  /** Menções na foto SEM NOTA (neutras sem peso cadastrado). */
  unscored_count: number;
  /** Soma da valoração congelada. `null` = nenhuma matéria valorada
   *  nesta foto (não "vale R$ 0"). Conta por (post × marca), igual ao
   *  alcance: uma matéria que cita duas marcas soma duas vezes. */
  total_ad_value: number | null;
};

export type SocialReportDraft = {
  generation_criteria?: GenerationCriteria | null;
  generation_criteria_labels?: GenerationCriteriaLabels | null;
  id: number;
  brand_id: number | null;
  brand_name: string | null;
  window_start: string;
  window_end: string;
  generated_at: string;
  expires_at: string;
  created_by: string;
  is_expired: boolean;
  aggregates: SocialReportAggregates;
};

/** Hoje só a tela de menções; o dashboard entra depois com o
 *  vocabulário dele. */
export type SavedFilterKind = "mentions";

/** O recorte guardado. `criteria` é o formato da TELA (data e hora
 *  separadas, sem fuso) — ver `mention-filter-criteria.ts`. */
export type SavedFilter = {
  id: number;
  kind: SavedFilterKind;
  name: string;
  criteria: MentionFilterCriteria;
  created_by: string;
  created_at: string;
  /** `null` enquanto ninguém editou. */
  updated_by: string | null;
  updated_at: string | null;
};

export type SocialReportSummary = {
  id: number;
  brand_id: number | null;
  brand_name: string | null;
  window_start: string;
  window_end: string;
  generated_at: string;
  saved_at: string;
  aggregates: SocialReportAggregates;
  /** `null` nos relatórios salvos antes de 2026-08-26 — a lista cai no
   *  rótulo derivado (janela + marca) quando não há nome. */
  name: string | null;
  /** Quem clicou em salvar. Exibição: a lista NUNCA filtra por autor,
   *  relatório salvo é da empresa inteira. */
  saved_by: string | null;
  /** A foto do filtro que gerou os números. */
  generation_criteria: GenerationCriteria | null;
  generation_criteria_labels?: GenerationCriteriaLabels | null;
  /** A última "atualizar dados" (2026-10-05): os números são deste dia,
   *  não do `saved_at`. `null` = nunca atualizado. */
  data_refreshed_at?: string | null;
  data_refreshed_by?: string | null;
  data_refreshed_by_name?: string | null;
};

/** O recorte que produziu o relatório, como o servidor o congelou. A
 *  tela itera as chaves que conhece e ignora o resto — é o que deixa o
 *  formato crescer (canal, polaridade) sem quebrar tela antiga. */
/** Uma opção do filtro "Clipadora": `value` é o que viaja, `label` o que a
 *  tela mostra. */
export type ClippingServiceOption = { value: string; label: string };

export type GenerationCriteria = {
  schema_version: number;
  brand_id?: number | null;
  /** Instantes com fuso (`2026-03-01T00:00:00+00:00`) na versão 2. Na
   *  versão 1 — as linhas que a migração `f1c4e07a92d6` converteu — a
   *  hora foi INVENTADA pela migração, e só o dia é afirmação
   *  verdadeira; ver `formatWindowFromCriteria`. */
  window_start?: string;
  window_end?: string;
  /** As três dimensões escalares da versão 2. Na 3 elas viraram lista
   *  (`polarities`/`approaches`), e a leitura aceita as duas grafias —
   *  relatório salvo antes de 2026-08-28 continua abrindo. */
  polarity?: string;
  approach?: string;
  q?: string;
  /** Onde `q` procura; ausente = tudo. */
  q_in?: MentionSearchField;
  /** As doze dimensões da versão 3, cada uma com sua flag de exclusão.
   *  **Chave AUSENTE, nunca `null`**: é assim que a trava de execução
   *  distingue "sem filtro de polaridade" de "polaridade FAVORAVEL", e
   *  um `null` aqui viraria uma segunda grafia da mesma intenção — duas
   *  travas pro mesmo recorte. */
  brand_ids?: number[];
  exclude_brands?: boolean;
  /** Os posts COLADOS (2026-10-02) — inclusão pura, sem flag de
   *  exclusão. O rótulo de cada id está em `labels.posts`. */
  raw_post_ids?: number[];
  /** Os uploads do filtro de upload da tela (2026-10-17); o nome de cada
   *  um está em `labels.uploads`. */
  upload_ids?: number[];
  /** As clipadoras do recorte (2026-10-31), inclusão pura; só aparece
   *  quando não vazia. O valor É o rótulo (`legado` é o migrado). */
  clipping_services?: string[];
  /** "Só revisadas" (`true`) / "só não revisadas" (`false`); ausente =
   *  sem filtro. O valor É o rótulo. */
  reviewed?: boolean;
  /** Citação no título (só imprensa): `true` = com, `false` = sem;
   *  ausente = sem filtro. O valor É o rótulo. */
  title_citation?: boolean;
  /** Subtema em destaque: `true` = com, `false` = sem; ausente = sem
   *  filtro (2026-11-01). */
  theme_highlight?: boolean;
  /** Post em mais de um cliente: `true` = em mais de um, `false` = só
   *  neste; ausente = sem filtro (2026-11-01). */
  shared_post?: boolean;
  /** Mensagem-chave (2026-11-01): ids (rótulo em `labels.key_messages`),
   *  o "Sem mensagem-chave" e a exclusão, que vale pros dois. */
  key_message_ids?: number[];
  no_key_message?: boolean;
  exclude_key_messages?: boolean;
  /** De quem são as menções da foto; ausente = só a empresa. */
  scope?: MentionScope;
  polarities?: string[];
  exclude_polarities?: boolean;
  approaches?: string[];
  exclude_approaches?: boolean;
  sources?: string[];
  exclude_sources?: boolean;
  /** Canal de rede e porte do publicador por ID (2026-11-03). Amostra
   *  gerada antes traz `channels`/`influencer_types` por nome. */
  channel_ids?: number[];
  exclude_channel_ids?: boolean;
  /** Legado (antes de 2026-11-03): nomes. Só leitura, na hidratação. */
  channels?: string[];
  exclude_channels?: boolean;
  vehicle_ids?: number[];
  exclude_vehicles?: boolean;
  tiers?: string[];
  exclude_tiers?: boolean;
  formats?: string[];
  exclude_formats?: boolean;
  publisher_tier_ids?: number[];
  exclude_publisher_tiers?: boolean;
  /** Legado (antes de 2026-11-03): nomes. Só leitura, na hidratação. */
  influencer_types?: string[];
  exclude_influencer_types?: boolean;
  theme_ids?: number[];
  /** "Sem subtema" (2026-11-03). */
  no_theme?: boolean;
  exclude_themes?: boolean;
  theme_group_ids?: number[];
  exclude_theme_groups?: boolean;
  spokesperson_ids?: number[];
  exclude_spokespersons?: boolean;
  publisher_group_ids?: number[];
  exclude_publisher_groups?: boolean;
  /** Tipo de mídia (2026-11-01): `print`/`online`/`tv`/`radio`/`__none__`. */
  media_types?: string[];
  exclude_media_types?: boolean;
  /** Valoração mínima em reais INTEIROS. Escalar, sem flag de
   *  exclusão — e sem rótulo congelado, porque o valor já é o próprio
   *  rótulo (nada a resolver no catálogo). */
  min_ad_value?: number;
};

/**
 * A grafia CONGELADA dos ids do recorte — o que a tela mostra.
 *
 * Existe porque `GenerationCriteria` guarda ids, e resolver id no
 * catálogo VIVO faria renomear um tema reescrever o rótulo de um
 * relatório salvo meses atrás. Ver `core/report_control/labels.py`.
 *
 * `null` nos relatórios anteriores a 2026-08-28, e id sem entrada no
 * mapa (catálogo apagado) simplesmente não aparece — nos dois casos a
 * tela cai no rótulo derivado, nunca em "None".
 */
export type GenerationCriteriaLabels = {
  schema_version?: number;
  brands?: Record<string, string>;
  vehicles?: Record<string, string>;
  channels?: Record<string, string>;
  publisher_tiers?: Record<string, string>;
  themes?: Record<string, string>;
  theme_groups?: Record<string, string>;
  spokespersons?: Record<string, string>;
  publisher_groups?: Record<string, string>;
  /** O TEXTO de cada mensagem-chave do recorte (2026-11-01). */
  key_messages?: Record<string, string>;
  /** Os NOMES das marcas excluídas no clique. A criteria só conhece as
   *  incluídas (a exclusão é congelada na geração), então este é o
   *  único vestígio de que aquilo nasceu de um "exceto". */
  excluded_brands_origin?: string[];
  /** O link (ou link original, ou código) de cada post colado, na hora
   *  da geração — chave é o id em string, como as outras. É AMOSTRA (os
   *  primeiros 500); o total está em `posts_total`. */
  posts?: Record<string, string>;
  posts_total?: number;
  /** O nome do arquivo de cada upload do recorte (2026-10-17); chave é o
   *  id em string. "Upload #N" quando o upload não tinha nome. */
  uploads?: Record<string, string>;
};

/** A MESMA forma para rascunho e salvo — a tela usa um componente só. */
export type SocialReportItem = {
  /** De quem é a menção na FOTO (relatório misto): o nome congelado da
   *  empresa dela, e se é de um concorrente. */
  company_name?: string | null;
  is_competitor?: boolean;
  /** É o `post_company_link_id`: abre o drawer de menção que já existe. */
  mention_id: number;
  brand_id: number;
  brand_name: string | null;
  title: string | null;
  channel: string | null;
  link: string | null;
  published_at: string;
  polarity: string | null;
  likes: number | null;
  reactions: number | null;
  comments: number | null;
  shares: number | null;
  p_curtida: number | null;
  p_compcomen: number | null;
  p_interacao: number | null;
  p_ultraperformance: number | null;
  vp: number;
  /** `null` nos três = menção sem nota (neutra sem peso cadastrado) —
   * o engajamento e os percentis acima continuam preenchidos. */
  ms: number | null;
  escena: number | null;
  icena: number | null;
};

export type SocialReportDraftDetail = {
  draft: SocialReportDraft;
  items: SocialReportItem[];
  total: number;
  page: number;
  size: number;
};

export type SocialReportDetail = {
  report: SocialReportSummary;
  items: SocialReportItem[];
  total: number;
  page: number;
  size: number;
};

export type SocialReportItemOrder = "icena_desc" | "icena_asc" | "published_desc";

/**
 * Página sem contagem total — `has_next` no lugar de `total`.
 *
 * Usado onde contar custa caro demais pro que a informação vale (ver
 * `listPublications`). A paginação `‹ ›` funciona igual: o botão
 * "próxima" olha `has_next` em vez de comparar página com total.
 */
export type HasNextPageOf<T> = {
  items: T[];
  has_next: boolean;
  page: number;
  size: number;
};

/**
 * A query string compartilhada por `/mentions` e `/publications`.
 *
 * As duas rotas aceitam exatamente os mesmos filtros de propósito — são
 * duas leituras do mesmo recorte. Montar em dois lugares garantiria que
 * um filtro novo entrasse só num deles.
 */
/** As doze dimensões da foto, no par (nome no TS, nome no fio).
 *
 * O servidor monta as quatro rotas com a MESMA dependência
 * (`http/snapshot_filters.py`); esta é a metade de cá dessa paridade, e
 * é por isso que ela é uma lista só e não uma por método. */
const SNAPSHOT_FACETS: Array<[keyof SnapshotFacetFilters, string]> = [
  ["sentiments", "sentiments"],
  ["sources", "sources"],
  ["channels", "channels"],
  ["approaches", "approaches"],
  ["formats", "formats"],
  ["tiers", "tiers"],
  ["mediaTypes", "media_types"],
  ["influencerTypes", "influencer_types"],
  ["publisherGroups", "publisher_groups"],
  ["themeGroups", "theme_groups"],
  ["themes", "themes"],
  ["spokespersons", "spokespersons"],
  ["brands", "brands"]
];

/** Escreve o recorte da foto na query — as facetas, a exclusão de cada
 * uma, a janela e a valoração mínima.
 *
 * Estava inline no `socialReportDashboard`. Com o detalhe do relatório
 * aceitando o mesmo recorte (2026-09-08), uma segunda cópia divergiria
 * da primeira no primeiro filtro novo, e o sintoma seria a lista e o
 * gráfico do MESMO relatório recortando diferente. */
function appendSnapshotFacets(
  query: URLSearchParams,
  filters: SnapshotFacetFilters
): void {
  if (filters.dateFrom) query.set("date_from", filters.dateFrom);
  if (filters.dateTo) query.set("date_to", filters.dateTo);
  if (filters.mentionScope && filters.mentionScope !== "own") {
    query.set("mention_scope", filters.mentionScope);
  }
  for (const [key, param] of SNAPSHOT_FACETS) {
    const values = filters[key] as string[] | undefined;
    values?.forEach(value => query.append(param, value));
    const excludeKey = `exclude${key.charAt(0).toUpperCase()}${key.slice(1)}` as keyof SnapshotFacetFilters;
    if (filters[excludeKey]) query.set(`exclude_${param}`, "true");
  }
  // `!== undefined` e não truthiness — ver o mesmo cuidado no
  // `mentionQuery`.
  if (filters.minAdValue !== undefined) {
    query.set("min_ad_value", String(filters.minAdValue));
  }
}

/** Ordenação + paginação da lista de publicações de um relatório, mais o
 * recorte de LEITURA — mesmos parâmetros no rascunho e no salvo. */
function itemQueryString(options: {
  order?: SocialReportItemOrder;
  page?: number;
  size?: number;
  filters?: SnapshotFacetFilters;
}): URLSearchParams {
  const query = new URLSearchParams();
  if (options.order) query.set("order", options.order);
  if (options.page) query.set("page", String(options.page));
  if (options.size) query.set("size", String(options.size));
  if (options.filters) appendSnapshotFacets(query, options.filters);
  return query;
}

// Exportada pro "Enviar para o Versa" (ponto de encaixe — ver
// versa/ARCHITECTURE.md): a cópia manda o MESMO recorte da lista.
export function mentionQueryString(options: MentionQuery): URLSearchParams {
  const query = new URLSearchParams({
    page: String(options.page ?? 1),
    size: String(options.size ?? 25)
  });
  // Só quando não é a de sempre: o link antigo continua igual, e o
  // servidor já assume o padrão.
  if (options.order && options.order !== MENTION_ORDER_DEFAULT) {
    query.set("order", options.order);
  }
  if (options.q) {
    query.set("q", options.q);
    if (options.q_in && options.q_in !== "all") query.set("q_in", options.q_in);
  }
  if (options.whyQ?.trim()) query.set("why_q", options.whyQ.trim());
  // `append` por valor, sob o nome SINGULAR: é assim que o servidor
  // recebe uma lista, e é o que mantém `?polarity=X` (um valor só,
  // de link antigo) funcionando sem caso especial.
  const facetas: Array<[string, Array<string | number> | undefined, boolean | undefined]> = [
    ["polarity", options.polarities, options.excludePolarities],
    ["approach", options.approaches, options.excludeApproaches],
    ["post_source", options.postSources, options.excludePostSources],
    ["channel_id", options.channelIds, options.excludeChannels],
    ["brand_id", options.brandIds, options.excludeBrands],
    ["vehicle_id", options.vehicleIds, options.excludeVehicles],
    // Inclusão pura: sem flag de exclusão (o `undefined` nunca vira
    // `exclude_*` na consulta).
    ["clipping_service", options.clippingServices, undefined],
    ["tier", options.tiers, options.excludeTiers],
    ["format_size", options.formats, options.excludeFormats],
    ["publisher_tier_id", options.publisherTierIds, options.excludePublisherTiers],
    ["publisher_group_id", options.publisherGroupIds, options.excludePublisherGroups],
    ["theme_id", options.themeIds, options.excludeThemes],
    ["theme_group_id", options.themeGroupIds, options.excludeThemeGroups],
    ["spokesperson_id", options.spokespersonIds, options.excludeSpokespersons],
    ["media_type", options.mediaTypes, options.excludeMediaTypes],
    ["editor", options.editorIds, options.excludeEditors]
  ];
  const EXCLUDE_PARAM: Record<string, string> = {
    polarity: "exclude_polarities",
    approach: "exclude_approaches",
    post_source: "exclude_post_sources",
    channel_id: "exclude_channel_ids",
    brand_id: "exclude_brands",
    vehicle_id: "exclude_vehicles",
    tier: "exclude_tiers",
    format_size: "exclude_formats",
    publisher_tier_id: "exclude_publisher_tiers",
    publisher_group_id: "exclude_publisher_groups",
    theme_id: "exclude_themes",
    theme_group_id: "exclude_theme_groups",
    spokesperson_id: "exclude_spokespersons",
    media_type: "exclude_media_types",
    editor: "exclude_editors"
  };
  for (const [param, values, exclude] of facetas) {
    if (!values?.length) continue;
    for (const value of values) query.append(param, String(value));
    // A exclusão só vai quando há o que excluir: sozinha, ela seria
    // uma segunda grafia de "sem filtro".
    if (exclude) query.set(EXCLUDE_PARAM[param], "true");
  }
  // "Sem subtema" fora do laço, como o da mensagem-chave: a exclusão vale
  // mesmo quando só ele foi escolhido.
  if (options.noTheme) {
    query.set("no_theme", "true");
    if (options.excludeThemes) query.set("exclude_themes", "true");
  }
  // Mensagem-chave fora do laço: o "sem" não é valor da lista, e a
  // exclusão vale mesmo quando só ele foi escolhido.
  for (const id of options.keyMessageIds ?? []) query.append("key_message_id", String(id));
  if (options.noKeyMessage) query.set("no_key_message", "true");
  if (options.excludeKeyMessages && (options.keyMessageIds?.length || options.noKeyMessage)) {
    query.set("exclude_key_messages", "true");
  }
  if (options.discarded) query.set("discarded", "true");
  if (options.inTrash) query.set("in_trash", "true");
  // `!== undefined`: `false` ("só as que faltam") é filtro de verdade.
  if (options.reviewed !== undefined) {
    query.set("reviewed", String(options.reviewed));
  }
  if (options.edited !== undefined) {
    query.set("edited", String(options.edited));
  }
  if (options.titleCitation !== undefined) {
    query.set("title_citation", String(options.titleCitation));
  }
  if (options.themeHighlight !== undefined) {
    query.set("theme_highlight", String(options.themeHighlight));
  }
  if (options.sharedPost !== undefined) {
    query.set("shared_post", String(options.sharedPost));
  }
  if (options.dateFrom) query.set("date_from", options.dateFrom);
  if (options.dateTo) query.set("date_to", options.dateTo);
  if (options.minIcena !== undefined) query.set("min_icena", String(options.minIcena));
  // `!== undefined` e não truthiness: o piso é 1 hoje, mas se um dia
  // baixar, um `if (options.minAdValue)` sumiria com o filtro no zero.
  if (options.minAdValue !== undefined) {
    query.set("min_ad_value", String(options.minAdValue));
  }
  if (options.scope && options.scope !== "own") query.set("scope", options.scope);
  // `append`, não `set`: o mesmo parâmetro repetido é como o servidor
  // recebe vários uploads (`?upload_id=8&upload_id=4`).
  for (const uploadId of options.uploadIds ?? []) {
    query.append("upload_id", String(uploadId));
  }
  if (options.referenceSetId !== undefined) {
    query.set("ref_set", String(options.referenceSetId));
  }
  return query;
}

/** Uma LINHA do upload com erro (2026-10-30), não uma tentativa: mensagem,
 * canal e motivo são os da última tentativa. `correction` (o item da fila,
 * com o que o editor precisa) só vem com a linha pendente ou na fila. */
export type ErrorLine = {
  row_index: number | null;
  group: ErrorLineGroup;
  failure_kind: string | null;
  channel: string | null;
  message: string;
  /** Quantas linhas de `upload_error_log` a linha tem. */
  attempts: number;
  first_at: string;
  last_at: string;
  state: ErrorLineState;
  correction: CorrectionItem | null;
};

export type ErrorLinePage = PageOf<ErrorLine> & {
  text_columns?: string[];
};

export type ErrorLineAttempt = {
  id: number;
  row_index: number | null;
  channel: string | null;
  message: string;
  raw_row: Record<string, unknown> | null;
  failure_kind: string | null;
  at: string;
};

/** Um envio da planilha de pendências corrigida (2026-10-30). */
export type CorrectionSheetSubmission = {
  id: number;
  submitted_by: string;
  submitted_by_name: string | null;
  submitted_at: string;
  filename: string;
  updated_count: number;
  reprocessed_count: number;
  discarded_count: number;
  untouched_count: number;
  /** O total; `rejected_lines` vem cortada nas primeiras. */
  rejected_count: number;
  rejected_lines: { row_index: number; reason: string }[];
};

export type CorrectionItem = {
  id: number;
  row_index: number;
  clipping_service: string;
  raw_row: Record<string, string>;
  status: string;
  /** Texto da matéria colado à mão quando a coleta automática falhou —
   * o reprocessamento usa como conteúdo e PULA o scraping. */
  manual_content: string | null;
  /** Texto que a COLETA automática trouxe na última tentativa que falhou
   * (`null` quando a coleta não rodou ou falhou). Só leitura: a tela o
   * mostra pra pessoa julgar se veio incompleto; o reprocessamento nunca
   * o usa — quem manda é `manual_content`. */
  collected_content: string | null;
  /** Já mandado reprocessar, esperando o worker chegar nele — a fila é
   * a mesma dos uploads, então pode demorar se tiver arquivo na frente. */
  dispatched: boolean;
  /** Veículo/canal da linha, do erro MAIS RECENTE dela. `null` quando a
   * falha foi de parse — aí o post nem chegou a existir. */
  channel: string | null;
  /** Mensagem do erro mais recente. */
  message: string | null;
};

/** Um balde da fila de correção, agrupado por veículo/canal.
 *
 * Existe por causa da escala que o gate de catálogo produz: um arquivo
 * de 5.000 linhas de um veículo não cadastrado vira 5.000 itens, e UM
 * cadastro resolve todos. O agrupamento vem do servidor porque a lista
 * de itens é paginada — contar no cliente diria "50 linhas" pra um
 * grupo de cinco mil, e a ação em massa agiria só sobre essas 50. */
export type CorrectionGroup = {
  channel: string | null;
  pending: number;
  /** Dos `pending`, quantos já foram mandados reprocessar e esperam o
   *  worker. O clique só publica na fila, então sem isso o botão voltava
   *  ao normal na hora e parecia que nada tinha acontecido. */
  dispatched: number;
  sample_message: string | null;
  /** `'vehicle'`/`'channel'` quando um CADASTRO resolve o balde inteiro,
   * `null` quando a pendência é de outra natureza. Vem estruturado do
   * servidor: a tela derivava isso procurando "aba Veículos" dentro da
   * mensagem, e qualquer reescrita do texto quebrava o link em silêncio. */
  catalog_kind: "channel" | "vehicle" | null;
  /** `'no_brand_match'` quando o balde TEM linha em que nenhum termo de
   *  marca casou (a tela oferece a planilha de pendências por isso); fora
   *  isso, o motivo estruturado do balde. Nunca farejar a mensagem. */
  failure_kind: string | null;
  /** O balde de VALOR IMPORTADO (2026-10-08): quando o motivo é "o que a
   *  planilha trouxe não casa com o cadastro", o grupo é o trio abaixo e
   *  o `channel` vem `null`. `import_value` é a chave normalizada (a do
   *  mapeamento); `import_label`, a grafia original, pra sugerir o nome
   *  ao criar no cadastro. `import_brand_id` só em tema e mensagem. */
  import_dimension: ImportDimension | null;
  import_value: string | null;
  import_brand_id: number | null;
  import_label: string | null;
};

/** As dimensões que a planilha pode trazer prontas — as mesmas do
 *  `shared/vocabulary/canonical-fields.ts`. */
export type ImportDimension = "polarity" | "approach" | "themes" | "key_messages";

/** O balde de uma ação de grupo: canal OU o trio de valor importado. */
export type CorrectionBucket = {
  channel: string | null;
  import_dimension?: ImportDimension | null;
  import_value?: string | null;
  import_brand_id?: number | null;
  /** O motivo do balde de canal (2026-10-19). PRESENTE — inclusive nulo,
   *  que é o motivo não registrado de upload antigo — reprocessa só as
   *  linhas daquele motivo; ausente reprocessa o canal inteiro. */
  failure_kind?: string | null;
};

/** "Usar um existente": neste upload, o valor da planilha vale o item. */
export type ImportMappingInput = {
  upload_id: number;
  dimension: ImportDimension;
  value: string;
  brand_id?: number | null;
  target_id?: number | null;
  target_value?: string | null;
};

export type UploadSkipItem = {
  id: number;
  row_index: number;
  channel: string | null;
  /** 'already_ingested' (o post já existia de um upload anterior),
   *  'catalog_ignored' (canal/veículo marcado pra não entrar) ou, só em
   *  upload anterior a 2026-09-14, 'no_brand_match' — hoje linha sem
   *  marca é item da fila de correção, não pulo. */
  reason: string;
  raw_row: Record<string, string> | null;
};

export type SubmitUploadItem = {
  file: File;
  uploadTemplateId: number | null;
  refreshEngagementMetrics: boolean;
  /** As dimensões tomadas DA PLANILHA neste envio; vazio (o padrão) =
   *  tudo pelo Reputaition. */
  importedDimensions: string[];
};

export type CopilotoMode = "padrao" | "web";

export type CopilotoConversation = {
  id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
};

export type CopilotoMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  mode: CopilotoMode | null;
  created_at: string;
};

export type CopilotoStreamEvent =
  | { type: "delta"; text: string }
  | {
      type: "done";
      conversationId: string;
      messageId: string | null;
      userMessageId: string | null;
      title: string | null;
    }
  | { type: "error"; message: string };

export type CopilotoSettings = {
  prompt_padrao: string;
  prompt_web: string;
  icebreakers: string[];
  /**
   * O modelo que o Copiloto usa, pelo id do catálogo da instalação
   * (`ai_model.id`, desde 2026-09-24; era o nome). `null` = nenhum
   * escolhido, e o chat responde 503 explicando.
   */
  model_id: number | null;
};

export type CopilotoStatus = {
  gemini_configured: boolean;
  /** Separado da chave: as duas se cadastram em telas diferentes. */
  model_configured: boolean;
};

// Núcleo HTTP e parsing de erro moram em `http.ts` — re-exportado aqui
// porque 20+ arquivos importam `ApiError` deste módulo.
export { ApiError };

export /** Só o que tem valor entra na query — `undefined` e vazio ficam de
 *  fora, senão `stage=` chegaria como string vazia e tomaria 422. */
function aiUsageQuery(query: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  for (const [chave, valor] of Object.entries(query)) {
    if (valor !== undefined && valor !== "") params.set(chave, String(valor));
  }
  return params.toString();
}

/** O alvo de um lote: o recorte da tela, ou os ids marcados mais o
 *  escopo em que foram marcados (a empresa, concorrentes ou todas). */
export type BulkTarget =
  | { query: MentionQuery }
  | { mentionIds: number[]; scope?: MentionQuery["scope"] }
  /** O recorte GUARDADO numa execução de reprocessamento, repetido cru
   *  — é como a prévia reaberta pelo sino aplica ao mesmo recorte sem
   *  reconstruir os filtros da tela. */
  | { savedQuery: string; savedMentionIds: number[] | null };

function aiReprocessBody(
  target: BulkTarget,
  request: AiReprocessRequest
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    tasks: request.tasks,
    include_reviewed: request.includeReviewed
  };
  if ("mentionIds" in target) body.mention_ids = target.mentionIds;
  if ("savedQuery" in target && target.savedMentionIds) {
    body.mention_ids = target.savedMentionIds;
  }
  return body;
}

function bulkTargetQuery(target: BulkTarget): URLSearchParams {
  if ("query" in target) return mentionQueryString(target.query);
  if ("savedQuery" in target) return new URLSearchParams(target.savedQuery);
  const query = new URLSearchParams();
  if (target.scope && target.scope !== "own") query.set("scope", target.scope);
  return query;
}

/** As rotas de cadastro que o CONCORRENTE tem: temas, grupos de temas,
 *  mensagens-chave, porta-vozes, a LISTAGEM de marcas e a configuração
 *  de classificação (textos de prompt, regras, termos de classificação
 *  e de foco). O MODELO por tarefa fica de fora: a menção do concorrente
 *  usa o do cliente, então `/prompt-models` passa intacto. Canal, veículo e
 *  publicador ficam de fora de propósito — são lista única do cliente,
 *  consumida por todas as marcas (ver
 *  `web_api/tests/test_competitor_catalog_scope.py`). A regra de tema
 *  por publicador/veículo ENTRA (`/themes/{id}/assignment-rule`): o
 *  tema é do concorrente, e os alvos que ela aponta são os do cliente. */
const COMPETITOR_CATALOG_PATH =
  /^\/(themes|theme-groups|key-messages|spokespersons|brands|prompts|prompt-rules|classification-terms|focus-terms)(?=[/?]|$)/;

/** O caminho com `competitor_id`, quando é rota de catálogo do
 *  concorrente; qualquer outro volta como veio. */
export function withCompetitorTarget(path: string, competitorId: number): string {
  if (!COMPETITOR_CATALOG_PATH.test(path)) return path;
  return `${path}${path.includes("?") ? "&" : "?"}competitor_id=${competitorId}`;
}

class ApiClient {
  constructor(private readonly baseUrl = API_URL) {}

  private request<T>(
    path: string,
    init: RequestInit = {},
    token?: string,
    scope?: RequestScope
  ): Promise<T> {
    return coreRequest<T>(this.baseUrl, path, init, token, scope);
  }

  /**
   * O mesmo cliente, com as chamadas de CATÁLOGO mirando um concorrente
   * (2026-10-03): temas, grupos de temas, mensagens-chave e porta-vozes
   * do concorrente são cadastro próprio dele, e a tela de Ajustes troca
   * o alvo sem que cada chamada precise saber disso. `null` devolve o
   * próprio cliente. Só as rotas de `COMPETITOR_CATALOG_PATH` ganham o
   * parâmetro; o resto passa intacto, então usar o cliente com alvo
   * numa chamada de canal não cria canal "do concorrente".
   */
  onCompetitor(competitorId: number | null): ApiClient {
    if (competitorId === null) return this;
    const scoped = Object.create(this) as ApiClient;
    const base = this.request.bind(this);
    (scoped as unknown as { request: typeof base }).request = (path, init, token, scope) =>
      base(withCompetitorTarget(path, competitorId), init, token, scope);
    return scoped;
  }

  /** O sino. Escopado pela PESSOA, como o `/me`: o servidor não exige
   *  empresa ativa, e devolve os avisos de todas as empresas que ela
   *  enxerga mais os globais. */
  listNotifications(token: string): Promise<AppNotificationList> {
    return this.request<AppNotificationList>("/notifications", {}, token);
  }

  markNotificationRead(token: string, notificationId: number): Promise<void> {
    return this.request<void>(
      `/notifications/${notificationId}/read`,
      { method: "POST" },
      token
    );
  }

  markAllNotificationsRead(token: string): Promise<void> {
    return this.request<void>("/notifications/read-all", { method: "POST" }, token);
  }

  /** A tela que mostrou o resultado avisa que a pessoa já viu — hoje só
   *  a prévia do reprocessamento por IA. */
  markNotificationSubjectRead(
    token: string,
    kind: AppNotificationKind,
    subjectId: string
  ): Promise<void> {
    return this.request<void>(
      "/notifications/read-subject",
      { method: "POST", body: JSON.stringify({ kind, subject_id: subjectId }) },
      token
    );
  }

  autofillCompany(
    token: string,
    name: string,
    website?: string
  ): Promise<CompanyAutofillSuggestion> {
    return this.request<CompanyAutofillSuggestion>(
      "/companies/autofill",
      { method: "POST", body: JSON.stringify({ name, website: website?.trim() || undefined }) },
      token
    );
  }

  /** O assistente de configuração: a IA sugere o cadastro inteiro a
   * partir de uma amostra. Multipart — a planilha vai inteira, sem
   * ingerir; `sampleSource=history` dispensa arquivo. Entra na cota de
   * IA por pessoa (429 com `Retry-After`). Especialista, sob a empresa
   * ativa. Responde 202 com o pedido: a sugestão vem de
   * `getConfigSuggestionRun`. */
  suggestCompanyConfig(token: string, input: ConfigSuggestionInput): Promise<ConfigSuggestionRun> {
    const body = new FormData();
    body.set("sample_source", input.sampleSource);
    body.set("notes", input.notes ?? "");
    if (input.sample) body.set("sample", input.sample, input.sample.name);
    for (const file of input.contextFiles ?? []) {
      body.append("context_files", file, file.name);
    }
    return this.request<ConfigSuggestionRun>(
      "/company-config/suggestion",
      { method: "POST", body },
      token
    );
  }

  /** O andamento de um pedido de `suggestCompanyConfig` (da empresa
   * ativa; 404 pra de outra). */
  getConfigSuggestionRun(token: string, runId: number): Promise<ConfigSuggestionRun> {
    return this.request<ConfigSuggestionRun>(`/company-config/suggestion/${runId}`, {}, token);
  }

  /** A mini base: a planilha da amostra volta ao servidor, que recorta
   *  as `size` linhas mais relevantes e as envia como upload comum. */
  createCalibrationUpload(
    token: string,
    input: { sample: File; size?: number }
  ): Promise<CalibrationUpload> {
    const body = new FormData();
    body.set("sample", input.sample, input.sample.name);
    if (input.size !== undefined) body.set("size", String(input.size));
    return this.request<CalibrationUpload>(
      "/company-config/calibration",
      { method: "POST", body },
      token
    );
  }

  /** As premissas relidas à luz do que a pessoa revisou/corrigiu na
   *  base (`uploadId`), ou nas menções revisadas mais recentes. */
  revisePremises(
    token: string,
    input: { uploadId: number | null; notes?: string }
  ): Promise<PremiseRevisionResult> {
    return this.request<PremiseRevisionResult>(
      "/company-config/premise-revision",
      {
        method: "POST",
        body: JSON.stringify({ upload_id: input.uploadId, notes: input.notes ?? "" })
      },
      token
    );
  }

  createCompany(token: string, payload: CreateCompanyPayload): Promise<CreatedCompany> {
    return this.request<CreatedCompany>(
      "/companies",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  /** Funda a empresa a partir da planilha que "Baixar configuração"
   * gera (multipart) e já põe a importação na fila. */
  createCompanyFromSpreadsheet(
    token: string,
    name: string,
    file: File,
    groupId?: number
  ): Promise<CompanyFromSpreadsheet> {
    const body = new FormData();
    body.set("name", name);
    body.set("file", file, file.name);
    if (groupId !== undefined) body.set("group_id", String(groupId));
    return this.request<CompanyFromSpreadsheet>(
      "/companies/from-spreadsheet",
      { method: "POST", body },
      token
    );
  }

  // --- dados da empresa em escopo ---------------------------------
  //
  // `/company-profile`, no singular e sem id: a empresa vem do
  // `X-Company-Id`, conferido no servidor. Substitui o antigo
  // `GET/PUT /companies/{id}`, que não existe mais e era de onde vinha
  // o "Not Found" no topo da tela de Ajustes.
  //
  // **Não há `deleteCompany`.** A rota não existe e não vai existir:
  // apagar a empresa levaria junto marcas, menções e uploads, contra a
  // regra de histórico do projeto.

  companyProfile(token: string): Promise<CompanyProfile> {
    return this.request<CompanyProfile>("/company-profile", {}, token);
  }

  updateCompanyProfile(
    token: string,
    payload: {
      name?: string;
      has_competitor_analysis?: boolean;
      is_government?: boolean;
      displays_cena?: boolean;
      publisher_group_counts_toward_cena?: boolean;
      polarity_scale?: "four_level" | "three_level";
      /** SVG inteiro; `null` limpa. Campo ausente não mexe. */
      color_logo?: string | null;
      gray_logo?: string | null;
    }
  ): Promise<CompanyProfile> {
    return this.request<CompanyProfile>(
      "/company-profile",
      { method: "PATCH", body: JSON.stringify(payload) },
      token
    );
  }

  /** Só Máster: liga ou desliga, por empresa, a edição do prompt
   * base e do modelo por tarefa por quem está abaixo dele. Chave
   * ausente não mexe. */
  setAiEditingAccess(
    token: string,
    payload: { can_edit_prompts?: boolean; can_edit_models?: boolean }
  ): Promise<CompanyProfile> {
    return this.request<CompanyProfile>(
      "/company-profile/ai-editing-access",
      { method: "PATCH", body: JSON.stringify(payload) },
      token
    );
  }

  /** O painel "Pontos de atenção" da empresa. Rota própria, e não campo
   * do perfil: a Visão Geral lê a cada abertura, e o perfil traz os
   * logos inteiros. */
  overviewAttention(token: string): Promise<OverviewAttention> {
    return this.request<OverviewAttention>("/company-profile/overview-attention", {}, token);
  }

  /** Especialista para cima. Substitui o painel INTEIRO. */
  updateOverviewAttention(token: string, payload: OverviewAttention): Promise<OverviewAttention> {
    return this.request<OverviewAttention>(
      "/company-profile/overview-attention",
      { method: "PUT", body: JSON.stringify(payload) },
      token
    );
  }

  listCitationTags(token: string): Promise<CitationTagGroup[]> {
    return this.request<CitationTagGroup[]>("/citation-tags", {}, token);
  }

  /** Substitui a lista INTEIRA da categoria — é uma linha por
   * categoria, com os termos concatenados no banco. */
  setCitationTags(
    token: string,
    category: string,
    tags: string[]
  ): Promise<CitationTagGroup> {
    return this.request<CitationTagGroup>(
      "/citation-tags",
      { method: "PUT", body: JSON.stringify({ category, tags }) },
      token
    );
  }

  removeCitationTags(token: string, category: string): Promise<void> {
    return this.request<void>(
      `/citation-tags/${encodeURIComponent(category)}`,
      { method: "DELETE" },
      token
    );
  }

  // --- catálogo da empresa ---------------------------------------
  //
  // Todas escopadas pelo `X-Company-Id` que o `request` já manda —
  // por isso NENHUMA leva `companyId` no caminho. O contrato antigo
  // repetia a empresa na URL e no header; o servidor confere o vínculo
  // em `CompanyUserLink` de qualquer jeito, então o par na URL só dava
  // uma segunda fonte de verdade pra discordar da primeira.
  //
  // `DELETE` aqui é DESATIVAÇÃO: carimba `deleted_on` e devolve a
  // linha. Nada de catálogo é apagado de verdade — menção já
  // classificada tem que continuar resolvendo o nome do tema que
  // usou, mesmo depois de o tema sair de circulação.

  listCompetitors(token: string, includeInactive = false): Promise<Competitor[]> {
    return this.request<Competitor[]>(
      `/competitors${includeInactive ? "?include_inactive=true" : ""}`,
      {},
      token
    );
  }

  createCompetitor(
    token: string,
    payload: { name: string; terms: string }
  ): Promise<Competitor> {
    return this.request<Competitor>(
      "/competitors",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  updateCompetitor(
    token: string,
    competitorId: number,
    payload: { name?: string; terms?: string; is_active?: boolean }
  ): Promise<Competitor> {
    return this.request<Competitor>(
      `/competitors/${competitorId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
      token
    );
  }

  deactivateCompetitor(token: string, competitorId: number): Promise<Competitor> {
    return this.request<Competitor>(
      `/competitors/${competitorId}`,
      { method: "DELETE" },
      token
    );
  }

  /** `includeInactive` traz as desativadas junto. O default esconde —
   *  lista de ESCOLHA não oferece o que saiu de circulação. Quem pede
   *  `true` é a aba de Ajustes (com "mostrar desativados") e o
   *  resolvedor de NOME, que precisa saber como se chamava a marca de
   *  um tema antigo. */
  listBrands(
    token: string,
    opts?: { includeInactive?: boolean }
  ): Promise<BrandOut[]> {
    const qs = opts?.includeInactive ? "?include_inactive=true" : "";
    return this.request<BrandOut[]>(`/brands${qs}`, {}, token);
  }

  createBrand(
    token: string,
    payload: { name: string; terms?: string | null; is_main_brand?: boolean }
  ): Promise<BrandOut> {
    return this.request<BrandOut>(
      "/brands",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  updateBrand(
    token: string,
    brandId: number,
    payload: {
      name?: string;
      terms?: string | null;
      is_main_brand?: boolean;
      isActive?: boolean;
    }
  ): Promise<BrandOut> {
    const { isActive, ...resto } = payload;
    return this.request<BrandOut>(
      `/brands/${brandId}`,
      {
        method: "PATCH",
        body: JSON.stringify(
          isActive === undefined ? resto : { ...resto, is_active: isActive }
        )
      },
      token
    );
  }

  /** Desativa a marca — nunca apaga. Devolve a linha com `deleted_on`,
   *  pra tela escrever "desativada em" sem uma segunda ida ao servidor.
   *  Na marca principal o servidor responde 409 com o caminho de saída
   *  (promover outra antes). */
  deactivateBrand(token: string, brandId: number): Promise<BrandOut> {
    return this.request<BrandOut>(
      `/brands/${brandId}`,
      { method: "DELETE" },
      token
    );
  }

  /** Os selos da tela de Ajustes: quantos cadastros ativos há em cada
   *  aba, numa requisição só (era uma lista inteira por aba). */
  getSettingsCounts(token: string): Promise<SettingsCounts> {
    return this.request<SettingsCounts>("/settings/counts", {}, token);
  }

  /** A lista de verificação da configuração da empresa (o que falta pra
   *  classificar). Calculada no servidor; escopo pelo `X-Company-Id`. */
  companyChecklist(token: string): Promise<CompanyChecklist> {
    return this.request<CompanyChecklist>("/company-config/checklist", {}, token);
  }

  listThemeGroups(token: string, includeInactive = false): Promise<ThemeGroup[]> {
    return this.request<ThemeGroup[]>(
      `/theme-groups${includeInactive ? "?include_inactive=true" : ""}`,
      {},
      token
    );
  }

  createThemeGroup(
    token: string,
    payload: { name: string; weight?: number | null }
  ): Promise<ThemeGroup> {
    return this.request<ThemeGroup>(
      "/theme-groups",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  updateThemeGroup(
    token: string,
    groupId: number,
    payload: {
      name?: string;
      weight?: number | null;
      is_active?: boolean;
    }
  ): Promise<ThemeGroup> {
    return this.request<ThemeGroup>(
      `/theme-groups/${groupId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
      token
    );
  }

  deactivateThemeGroup(token: string, groupId: number): Promise<ThemeGroup> {
    return this.request<ThemeGroup>(
      `/theme-groups/${groupId}`,
      { method: "DELETE" },
      token
    );
  }

  listThemes(
    token: string,
    opts?: { brandId?: number; includeInactive?: boolean }
  ): Promise<Theme[]> {
    const query = new URLSearchParams();
    if (opts?.brandId !== undefined) query.set("brand_id", String(opts.brandId));
    if (opts?.includeInactive) query.set("include_inactive", "true");
    const qs = query.toString();
    return this.request<Theme[]>(`/themes${qs ? `?${qs}` : ""}`, {}, token);
  }

  createTheme(
    token: string,
    payload: {
      brand_id: number;
      theme_group_id: number;
      name: string;
      terms?: string | null;
      classification_mode?: ThemeClassificationMode;
      deterministic_scope?: DeterministicScope;
      detection_assumptions?: string | null;
      non_detection_assumptions?: string | null;
    }
  ): Promise<Theme> {
    return this.request<Theme>(
      "/themes",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  updateTheme(
    token: string,
    themeId: number,
    payload: {
      brand_id?: number;
      theme_group_id?: number;
      name?: string;
      terms?: string | null;
      classification_mode?: ThemeClassificationMode;
      deterministic_scope?: DeterministicScope;
      detection_assumptions?: string | null;
      non_detection_assumptions?: string | null;
      is_active?: boolean;
      /** A consultoria da IA de onde veio a mudança: só a trilha a guarda. */
      rule_review_id?: number;
    }
  ): Promise<Theme> {
    return this.request<Theme>(
      `/themes/${themeId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
      token
    );
  }

  deactivateTheme(token: string, themeId: number): Promise<Theme> {
    return this.request<Theme>(`/themes/${themeId}`, { method: "DELETE" }, token);
  }

  getThemeAssignment(token: string, themeId: number): Promise<ThemeAssignment> {
    return this.request<ThemeAssignment>(`/themes/${themeId}/assignment-rule`, {}, token);
  }

  /** Substitui as três listas — o corpo é o estado inteiro do formulário. */
  replaceThemeAssignment(
    token: string,
    themeId: number,
    payload: {
      publisher_group_ids: number[];
      publisher_person_ids: number[];
      vehicle_ids: number[];
    }
  ): Promise<ThemeAssignment> {
    return this.request<ThemeAssignment>(
      `/themes/${themeId}/assignment-rule`,
      { method: "PUT", body: JSON.stringify(payload) },
      token
    );
  }

  /** `{theme_id: quantos alvos}` só dos temas que têm regra. */
  countThemeAssignmentTargets(token: string): Promise<Record<string, number>> {
    return this.request<Record<string, number>>("/themes/assignment-rule-counts", {}, token);
  }

  /**
   * A lista de canais, PAGINADA e com busca.
   *
   * Devolve envelope (`items`/`total`) desde 2026-08-12: a importação em
   * massa criou a escala que o cadastro manual nunca criou, e a lista
   * inteira era ~3,4 MB de JSON numa empresa do legado. `q` casa pedaço
   * do nome **e dos apelidos** — quem procura pela grafia que a
   * clipadora manda tem que achar o cadastro que a resolve.
   *
   * Quem só quer o número (o selo da navegação) pede `size: 1` e lê o
   * `total`, sem baixar linha.
   */
  listChannels(
    token: string,
    opts?: {
      q?: string;
      postSource?: "network" | "press";
      includeInactive?: boolean;
      page?: number;
      size?: number;
    }
  ): Promise<PageOf<Channel>> {
    const query = new URLSearchParams();
    if (opts?.q?.trim()) query.set("q", opts.q.trim());
    if (opts?.postSource) query.set("post_source", opts.postSource);
    if (opts?.includeInactive) query.set("include_inactive", "true");
    if (opts?.page) query.set("page", String(opts.page));
    if (opts?.size) query.set("size", String(opts.size));
    const qs = query.toString();
    return this.request<PageOf<Channel>>(
      `/channels${qs ? `?${qs}` : ""}`,
      {},
      token
    );
  }

  createChannel(
    token: string,
    payload: {
      name: string;
      post_source?: "network" | "press";
      ignore_term_verification?: boolean;
      tier?: string | null;
      weight?: number | null;
      ignored?: boolean;
    }
  ): Promise<Channel> {
    return this.request<Channel>(
      "/channels",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  updateChannel(
    token: string,
    channelId: number,
    payload: {
      name?: string;
      post_source?: "network" | "press";
      ignore_term_verification?: boolean;
      tier?: string | null;
      weight?: number | null;
      ignored?: boolean;
      is_active?: boolean;
    }
  ): Promise<Channel> {
    return this.request<Channel>(
      `/channels/${channelId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
      token
    );
  }

  deactivateChannel(token: string, channelId: number): Promise<Channel> {
    return this.request<Channel>(
      `/channels/${channelId}`,
      { method: "DELETE" },
      token
    );
  }

  /** Devolve o CANAL inteiro, não só o apelido: a tela redesenha os
   * chips com a resposta, sem uma segunda ida ao servidor. */
  addChannelAlias(
    token: string,
    channelId: number,
    name: string
  ): Promise<Channel> {
    return this.request<Channel>(
      `/channels/${channelId}/aliases`,
      { method: "POST", body: JSON.stringify({ name }) },
      token
    );
  }

  removeChannelAlias(
    token: string,
    channelId: number,
    aliasId: number
  ): Promise<Channel> {
    return this.request<Channel>(
      `/channels/${channelId}/aliases/${aliasId}`,
      { method: "DELETE" },
      token
    );
  }

  /** Os canais de rede da base (Facebook, Instagram, YouTube, Twitter e
   * os comentários) que faltam na empresa — a mesma base com que a
   * empresa nova nasce. Só cria; `kept_inactive` são os que a empresa
   * desativou e por isso não voltaram. */
  createStandardNetworkChannels(token: string): Promise<{
    created: Channel[];
    aliased: Channel[];
    kept_inactive: string[];
  }> {
    return this.request(
      "/channels/standard-network",
      { method: "POST" },
      token
    );
  }

  /** Ver `listChannels` — mesmo envelope, mesma busca. Aqui a paginação
   * é o caso mais agudo: ~14 mil veículos numa empresa do legado. */
  listVehicles(
    token: string,
    opts?: {
      q?: string;
      includeInactive?: boolean;
      page?: number;
      size?: number;
    }
  ): Promise<PageOf<Vehicle>> {
    const query = new URLSearchParams();
    if (opts?.q?.trim()) query.set("q", opts.q.trim());
    if (opts?.includeInactive) query.set("include_inactive", "true");
    if (opts?.page) query.set("page", String(opts.page));
    if (opts?.size) query.set("size", String(opts.size));
    const qs = query.toString();
    return this.request<PageOf<Vehicle>>(
      `/vehicles${qs ? `?${qs}` : ""}`,
      {},
      token
    );
  }

  createVehicle(
    token: string,
    payload: {
      name: string;
      vehicle_kind?: VehicleKind;
      tier_key?: string | null;
      content_optional?: boolean;
      ignored?: boolean;
      ignore_term_verification?: boolean;
      state?: string | null;
      region?: string | null;
      country?: string | null;
      base_price?: number | null;
    }
  ): Promise<Vehicle> {
    return this.request<Vehicle>(
      "/vehicles",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  updateVehicle(
    token: string,
    vehicleId: number,
    payload: {
      name?: string;
      vehicle_kind?: VehicleKind;
      tier_key?: string | null;
      content_optional?: boolean;
      ignored?: boolean;
      ignore_term_verification?: boolean;
      is_active?: boolean;
      state?: string | null;
      region?: string | null;
      country?: string | null;
      base_price?: number | null;
    }
  ): Promise<Vehicle> {
    return this.request<Vehicle>(
      `/vehicles/${vehicleId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
      token
    );
  }

  deactivateVehicle(token: string, vehicleId: number): Promise<Vehicle> {
    return this.request<Vehicle>(
      `/vehicles/${vehicleId}`,
      { method: "DELETE" },
      token
    );
  }

  addVehicleAlias(
    token: string,
    vehicleId: number,
    name: string
  ): Promise<Vehicle> {
    return this.request<Vehicle>(
      `/vehicles/${vehicleId}/aliases`,
      { method: "POST", body: JSON.stringify({ name }) },
      token
    );
  }

  removeVehicleAlias(
    token: string,
    vehicleId: number,
    aliasId: number
  ): Promise<Vehicle> {
    return this.request<Vehicle>(
      `/vehicles/${vehicleId}/aliases/${aliasId}`,
      { method: "DELETE" },
      token
    );
  }

  // --- importação de catálogo por planilha ------------------------------
  //
  // O ciclo que fecha o gate de catálogo: o upload diz o que falta →
  // "Baixar pendências" traz a planilha com os nomes já preenchidos →
  // a pessoa completa tier/peso → importa aqui → reprocessa o grupo.
  // A mesma máquina serve o import avulso das abas Canais/Veículos.
  //
  // Assíncrono com RECIBO, sem prévia e sem desfazer: o veredito por
  // linha vem depois, e "baixar só as recusadas" é o laço de correção.

  /** Um arquivo por vez (multipart), 202 com o id — o resultado vem
   * pelo poll de `catalogImport`. */
  importCatalog(
    token: string,
    file: File,
    kind: CatalogImportKind = "catalog"
  ): Promise<{ id: number; status: string }> {
    const body = new FormData();
    body.set("file", file, file.name);
    body.set("kind", kind);
    return this.request("/catalog-imports", { method: "POST", body }, token);
  }

  listCatalogImports(
    token: string,
    options: { page?: number; size?: number; kind?: CatalogImportKind } = {}
  ): Promise<PageOf<CatalogImport>> {
    const query = new URLSearchParams({
      page: String(options.page ?? 1),
      size: String(options.size ?? 10)
    });
    if (options.kind) query.set("kind", options.kind);
    return this.request(`/catalog-imports?${query}`, {}, token);
  }

  catalogImportRows(
    token: string,
    importId: number,
    options: { outcome?: string; page?: number; size?: number } = {}
  ): Promise<PageOf<CatalogImportRow>> {
    const query = new URLSearchParams({
      page: String(options.page ?? 1),
      size: String(options.size ?? 50)
    });
    if (options.outcome) query.set("outcome", options.outcome);
    return this.request(
      `/catalog-imports/${importId}/rows?${query}`,
      {},
      token
    );
  }

  /** Libera a trava de execução. O que já foi aplicado FICA — o recibo
   * mostra até onde foi, e reimportar o mesmo arquivo completa (o
   * upsert é idempotente). */
  cancelCatalogImport(token: string, importId: number): Promise<void> {
    return this.request(
      `/catalog-imports/${importId}/cancel`,
      { method: "POST" },
      token
    );
  }

  /** O modelo em branco, com as colunas na ordem que o parser espera. */
  downloadCatalogTemplate(
    token: string,
    kind: CatalogImportKind = "catalog"
  ): Promise<Blob> {
    return requestBlob(
      this.baseUrl,
      `/catalog-imports/template-spreadsheet?kind=${kind}`,
      token
    );
  }

  /** O modelo em branco de um template de clipadora, em xlsx: todas as
   * colunas, na ordem do cadastro. Rota pública; o token vai como nos
   * outros downloads e não faz diferença. */
  downloadUploadTemplateSpreadsheet(
    token: string,
    templateId: number
  ): Promise<Blob> {
    return requestBlob(
      this.baseUrl,
      `/upload-templates/${templateId}/spreadsheet`,
      token
    );
  }

  /** Só as linhas recusadas, nas colunas ORIGINAIS do arquivo + o
   * motivo: corrige no Excel e reenvia só elas. */
  downloadCatalogRejected(token: string, importId: number): Promise<Blob> {
    return requestBlob(
      this.baseUrl,
      `/catalog-imports/${importId}/rejected-spreadsheet`,
      token
    );
  }

  /** Os cadastros que faltam PRA ESTE UPLOAD, com os nomes já
   * preenchidos — o começo do ciclo. */
  downloadPendingCatalog(token: string, uploadId: number): Promise<Blob> {
    return requestBlob(
      this.baseUrl,
      `/uploads/${uploadId}/corrections/catalog-pending-spreadsheet`,
      token
    );
  }

  /** A configuração completa da empresa numa planilha de várias abas
   * (a primeira metade da migração por planilha). Escopo pela
   * credencial (`X-Company-Id`); o nome do arquivo é escolhido pela
   * tela. */
  downloadCompanyConfig(
    token: string,
    scope?: RequestScope,
    options?: { sheets?: string[] }
  ): Promise<Blob> {
    // `sheets` recorta a exportação nas abas pedidas (chaves do formato,
    // p.ex. `rules`); sem ele, a configuração inteira.
    const query = options?.sheets?.length
      ? `?sheets=${options.sheets.map(encodeURIComponent).join(",")}`
      : "";
    return requestBlob(this.baseUrl, `/company-config/export${query}`, token, scope);
  }

  /** O modelo em branco da configuração (as abas só com cabeçalho). Rota
   * SEM empresa: a página de criação ainda não tem `X-Company-Id`. */
  downloadCompanyConfigTemplate(token: string): Promise<Blob> {
    return requestBlob(this.baseUrl, "/company-config/template", token);
  }

  // --- publicadores ---------------------------------------------------
  //
  // As duas metades do `p_publi` do CENA social. Escopo pela credencial
  // (header `X-Company-Id`), como o resto dos catálogos — nenhuma rota
  // leva `companyId`.

  listPublisherTiers(
    token: string,
    opts?: { includeInactive?: boolean }
  ): Promise<PublisherTier[]> {
    const query = opts?.includeInactive ? "?include_inactive=true" : "";
    return this.request<PublisherTier[]>(`/publisher-tiers${query}`, {}, token);
  }

  createPublisherTier(
    token: string,
    payload: {
      name: string;
      weight: number;
      min_count?: number | null;
      max_count?: number | null;
      valid_until?: string | null;
      is_default?: boolean;
    }
  ): Promise<PublisherTier> {
    return this.request<PublisherTier>(
      "/publisher-tiers",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  updatePublisherTier(
    token: string,
    tierId: number,
    payload: Partial<{
      name: string;
      weight: number;
      min_count: number | null;
      max_count: number | null;
      valid_until: string | null;
      is_active: boolean;
      is_default: boolean;
    }>
  ): Promise<PublisherTier> {
    return this.request<PublisherTier>(
      `/publisher-tiers/${tierId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
      token
    );
  }

  /** Desativa (não apaga) e devolve a linha, pra tela atualizar sem uma
   * segunda requisição. */
  deactivatePublisherTier(token: string, tierId: number): Promise<PublisherTier> {
    return this.request<PublisherTier>(
      `/publisher-tiers/${tierId}`,
      { method: "DELETE" },
      token
    );
  }

  listPublisherGroups(
    token: string,
    opts?: { includeInactive?: boolean }
  ): Promise<PublisherGroup[]> {
    const query = opts?.includeInactive ? "?include_inactive=true" : "";
    return this.request<PublisherGroup[]>(`/publisher-groups${query}`, {}, token);
  }

  createPublisherGroup(
    token: string,
    payload: { name: string; weight?: number | null }
  ): Promise<PublisherGroup> {
    return this.request<PublisherGroup>(
      "/publisher-groups",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  updatePublisherGroup(
    token: string,
    groupId: number,
    payload: Partial<{ name: string; weight: number | null; is_active: boolean }>
  ): Promise<PublisherGroup> {
    return this.request<PublisherGroup>(
      `/publisher-groups/${groupId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
      token
    );
  }

  deactivatePublisherGroup(token: string, groupId: number): Promise<PublisherGroup> {
    return this.request<PublisherGroup>(
      `/publisher-groups/${groupId}`,
      { method: "DELETE" },
      token
    );
  }

  listPublisherPersons(
    token: string,
    opts?: { includeInactive?: boolean }
  ): Promise<PublisherPerson[]> {
    const query = opts?.includeInactive ? "?include_inactive=true" : "";
    return this.request<PublisherPerson[]>(`/publisher-persons${query}`, {}, token);
  }

  /** Cria a pessoa com perfis e grupos numa requisição só — o servidor
   * grava as três tabelas numa transação. */
  createPublisherPerson(
    token: string,
    payload: {
      name: string;
      profiles: Array<{ platform: string; profile_url: string }>;
      group_ids: number[];
    }
  ): Promise<PublisherPerson> {
    return this.request<PublisherPerson>(
      "/publisher-persons",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  /** PATCH parcial: omitir `profiles`/`group_ids` NÃO os toca; mandar
   * `[]` esvazia. */
  updatePublisherPerson(
    token: string,
    personId: number,
    payload: Partial<{
      name: string;
      profiles: Array<{ platform: string; profile_url: string }>;
      group_ids: number[];
      is_active: boolean;
    }>
  ): Promise<PublisherPerson> {
    return this.request<PublisherPerson>(
      `/publisher-persons/${personId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
      token
    );
  }

  deactivatePublisherPerson(token: string, personId: number): Promise<PublisherPerson> {
    return this.request<PublisherPerson>(
      `/publisher-persons/${personId}`,
      { method: "DELETE" },
      token
    );
  }


  listKeyMessages(
    token: string,
    opts?: { brandId?: number; includeInactive?: boolean }
  ): Promise<KeyMessage[]> {
    const query = new URLSearchParams();
    if (opts?.brandId !== undefined) query.set("brand_id", String(opts.brandId));
    if (opts?.includeInactive) query.set("include_inactive", "true");
    const qs = query.toString();
    return this.request<KeyMessage[]>(`/key-messages${qs ? `?${qs}` : ""}`, {}, token);
  }

  createKeyMessage(
    token: string,
    payload: {
      brand_id: number;
      data_source: "press" | "network";
      message: string;
      rules: string;
      exclusion_rules?: string | null;
    }
  ): Promise<KeyMessage> {
    return this.request<KeyMessage>(
      "/key-messages",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  updateKeyMessage(
    token: string,
    keyMessageId: number,
    payload: {
      brand_id?: number;
      data_source?: "press" | "network";
      message?: string;
      rules?: string;
      exclusion_rules?: string | null;
      is_active?: boolean;
      /** A consultoria da IA de onde veio a mudança: só a trilha a guarda. */
      rule_review_id?: number;
    }
  ): Promise<KeyMessage> {
    return this.request<KeyMessage>(
      `/key-messages/${keyMessageId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
      token
    );
  }

  deactivateKeyMessage(token: string, keyMessageId: number): Promise<KeyMessage> {
    return this.request<KeyMessage>(
      `/key-messages/${keyMessageId}`,
      { method: "DELETE" },
      token
    );
  }

  // --- configuração de classificação ------------------------------
  //
  // Mesma regra do catálogo: escopo vem do `X-Company-Id`, `DELETE` é
  // desativação. `/prompt-global-config` é a exceção — é da
  // INSTALAÇÃO, não da empresa.

  listFocusTerms(
    token: string,
    opts?: { brandId?: number; source?: PostSource; includeInactive?: boolean }
  ): Promise<FocusTerm[]> {
    const query = new URLSearchParams();
    if (opts?.brandId !== undefined) query.set("brand_id", String(opts.brandId));
    if (opts?.source) query.set("source", opts.source);
    if (opts?.includeInactive) query.set("include_inactive", "true");
    const qs = query.toString();
    return this.request<FocusTerm[]>(`/focus-terms${qs ? `?${qs}` : ""}`, {}, token);
  }

  createFocusTerm(
    token: string,
    payload: { brand_id: number; category: FocusCategory; source: PostSource; term: string }
  ): Promise<FocusTerm> {
    return this.request<FocusTerm>(
      "/focus-terms",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  /** O interruptor (reativar é PATCH e desativar é DELETE: o `DELETE` do
   * catálogo é "desativar", nunca apaga) e a CATEGORIA, que inverte o termo
   * entre "Foco cliente" e "Foco concorrente". */
  updateFocusTerm(
    token: string,
    termId: number,
    payload: { is_active?: boolean; category?: FocusCategory }
  ): Promise<FocusTerm> {
    return this.request<FocusTerm>(
      `/focus-terms/${termId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
      token
    );
  }

  deactivateFocusTerm(token: string, termId: number): Promise<FocusTerm> {
    return this.request<FocusTerm>(`/focus-terms/${termId}`, { method: "DELETE" }, token);
  }

  listClassificationTerms(
    token: string,
    opts?: { brandId?: number; source?: PostSource; includeInactive?: boolean }
  ): Promise<ClassificationTerm[]> {
    const query = new URLSearchParams();
    if (opts?.brandId !== undefined) query.set("brand_id", String(opts.brandId));
    if (opts?.source) query.set("source", opts.source);
    if (opts?.includeInactive) query.set("include_inactive", "true");
    const qs = query.toString();
    return this.request<ClassificationTerm[]>(
      `/classification-terms${qs ? `?${qs}` : ""}`,
      {},
      token
    );
  }

  createClassificationTerm(
    token: string,
    payload: {
      brand_id: number;
      term_type: TermType;
      category: TermCategory;
      source: PostSource;
      term: string;
    }
  ): Promise<ClassificationTerm> {
    return this.request<ClassificationTerm>(
      "/classification-terms",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  deactivateClassificationTerm(token: string, termId: number): Promise<ClassificationTerm> {
    return this.request<ClassificationTerm>(
      `/classification-terms/${termId}`,
      { method: "DELETE" },
      token
    );
  }

  updateClassificationTerm(
    token: string,
    termId: number,
    payload: { is_active?: boolean }
  ): Promise<ClassificationTerm> {
    return this.request<ClassificationTerm>(
      `/classification-terms/${termId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
      token
    );
  }

  listPromptRules(
    token: string,
    opts?: {
      brandId?: number;
      task?: string;
      source?: PostSource;
      includeInactive?: boolean;
    }
  ): Promise<PromptRule[]> {
    const query = new URLSearchParams();
    if (opts?.brandId !== undefined) query.set("brand_id", String(opts.brandId));
    if (opts?.task) query.set("task", opts.task);
    if (opts?.source) query.set("source", opts.source);
    if (opts?.includeInactive) query.set("include_inactive", "true");
    const qs = query.toString();
    return this.request<PromptRule[]>(`/prompt-rules${qs ? `?${qs}` : ""}`, {}, token);
  }

  /** `position` NÃO entra: é derivada da faixa reservada do `rule_type`
   * mais quantas já existem. Deixar o cliente escolher permitiria
   * gravar número fora da faixa — e aí o motor descarta a polaridade
   * inteira, em silêncio. */
  createPromptRule(
    token: string,
    payload: {
      brand_id: number;
      task: string;
      rule_type: string;
      source: PostSource;
      text: string;
      /** A consultoria da IA de onde veio a premissa: só a trilha a guarda. */
      rule_review_id?: number;
    }
  ): Promise<PromptRule> {
    return this.request<PromptRule>(
      "/prompt-rules",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  updatePromptRule(
    token: string,
    ruleId: number,
    payload: {
      brand_id?: number;
      task?: string;
      rule_type?: string;
      source?: PostSource;
      text?: string;
      is_active?: boolean;
      /** A consultoria da IA de onde veio a mudança: só a trilha a guarda. */
      rule_review_id?: number;
    }
  ): Promise<PromptRule> {
    return this.request<PromptRule>(
      `/prompt-rules/${ruleId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
      token
    );
  }

  deactivatePromptRule(token: string, ruleId: number): Promise<PromptRule> {
    return this.request<PromptRule>(`/prompt-rules/${ruleId}`, { method: "DELETE" }, token);
  }

  // --- texto do prompt --------------------------------------------

  /** As 13 linhas efetivas da empresa (ou as filtradas por tarefa/fonte). */
  listPrompts(
    token: string,
    opts?: { task?: string; source?: PostSource }
  ): Promise<Prompt[]> {
    const query = new URLSearchParams();
    if (opts?.task) query.set("task", opts.task);
    if (opts?.source) query.set("source", opts.source);
    const qs = query.toString();
    return this.request<Prompt[]>(`/prompts${qs ? `?${qs}` : ""}`, {}, token);
  }

  /** Personaliza a linha inteira. Texto igual ao geral não cria exceção
   * (a resposta volta com `origin: "default"`); reenviar a mesma
   * personalização com `default_changed` só atualiza a data de referência. */
  customizePrompt(
    token: string,
    task: string,
    source: PostSource,
    position: number,
    payload: PromptCustomizeInput
  ): Promise<Prompt> {
    return this.request<Prompt>(
      `/prompts/${task}/${source}/${position}`,
      { method: "PUT", body: JSON.stringify(payload) },
      token
    );
  }

  /** Volta a linha ao geral. Idempotente: sem personalização devolve a
   * linha como está. */
  resetPrompt(
    token: string,
    task: string,
    source: PostSource,
    position: number
  ): Promise<Prompt> {
    return this.request<Prompt>(
      `/prompts/${task}/${source}/${position}`,
      { method: "DELETE" },
      token
    );
  }

  /**
   * Uma linha por tarefa, sempre — inclusive as sem modelo escolhido,
   * que é o estado que mais precisa aparecer (a classificação dela
   * falha). O formato mudou em 2026-09-01, junto do padrão da
   * instalação.
   */
  listPromptModels(token: string): Promise<EffectivePromptModel[]> {
    return this.request<EffectivePromptModel[]>("/prompt-models", {}, token);
  }

  /** Volta a tarefa ao padrão da instalação, removendo a escolha própria. */
  clearPromptModel(token: string, task: string): Promise<void> {
    return this.request(`/prompt-models/${task}`, { method: "DELETE" }, token);
  }

  /** Os modelos que esta empresa pode escolher: ativos e com credencial. */
  listAiModelOptions(token: string): Promise<AiModel[]> {
    return this.request<AiModel[]>("/ai-model-options", {}, token);
  }

  // --- Administração → Modelos de IA (instalação inteira) ---------------

  listAiProviders(token: string): Promise<AiProviderOption[]> {
    return this.request<AiProviderOption[]>("/admin/ai/providers", {}, token);
  }

  listAiCredentials(token: string): Promise<AiProviderCredential[]> {
    return this.request<AiProviderCredential[]>("/admin/ai/credentials", {}, token);
  }

  /**
   * Omitir `api_key` MANTÉM a chave gravada — é o que permite editar
   * endpoint ou versão sem que a tela conheça o segredo, e é por isso
   * que o campo nasce sempre vazio.
   */
  saveAiCredential(
    token: string,
    provider: AiProvider,
    payload: { api_key?: string; api_base?: string | null; api_version?: string | null }
  ): Promise<AiProviderCredential> {
    return this.request<AiProviderCredential>(
      `/admin/ai/credentials/${provider}`,
      { method: "PUT", body: JSON.stringify(payload) },
      token
    );
  }

  deactivateAiCredential(token: string, provider: AiProvider): Promise<void> {
    return this.request(
      `/admin/ai/credentials/${provider}`,
      { method: "DELETE" },
      token
    );
  }

  // --- Prompts padrão (Administração) ------------------------------------

  listPromptDefaults(token: string): Promise<PromptDefault[]> {
    return this.request<PromptDefault[]>("/admin/prompt-defaults", {}, token);
  }

  updatePromptDefault(
    token: string,
    id: number,
    payload: PromptDefaultInput
  ): Promise<PromptDefault> {
    return this.request<PromptDefault>(
      `/admin/prompt-defaults/${id}`,
      { method: "PUT", body: JSON.stringify(payload) },
      token
    );
  }

  /** Mede, sem gravar, o que o texto novo faz com as empresas que seguem a
   * linha. 422 se o texto é inválido. */
  checkPromptDefault(
    token: string,
    id: number,
    payload: PromptDefaultInput
  ): Promise<PromptDefaultCheck> {
    return this.request<PromptDefaultCheck>(
      `/admin/prompt-defaults/${id}/check`,
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  // --- Templates de upload (Administração) ------------------------------

  listUploadTemplatesAdmin(token: string): Promise<UploadTemplateAdmin[]> {
    return this.request<UploadTemplateAdmin[]>("/admin/upload-templates", {}, token);
  }

  uploadTemplateVocabulary(token: string): Promise<UploadTemplateVocabulary> {
    return this.request<UploadTemplateVocabulary>(
      "/admin/upload-templates/vocabulary",
      {},
      token
    );
  }

  createUploadTemplate(
    token: string,
    payload: UploadTemplateInput
  ): Promise<UploadTemplateAdmin> {
    return this.request<UploadTemplateAdmin>(
      "/admin/upload-templates",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  updateUploadTemplate(
    token: string,
    id: number,
    payload: UploadTemplateInput
  ): Promise<UploadTemplateAdmin> {
    return this.request<UploadTemplateAdmin>(
      `/admin/upload-templates/${id}`,
      { method: "PUT", body: JSON.stringify(payload) },
      token
    );
  }

  deactivateUploadTemplate(token: string, id: number): Promise<void> {
    return this.request(`/admin/upload-templates/${id}`, { method: "DELETE" }, token);
  }

  reactivateUploadTemplate(token: string, id: number): Promise<UploadTemplateAdmin> {
    return this.request<UploadTemplateAdmin>(
      `/admin/upload-templates/${id}/reactivate`,
      { method: "POST" },
      token
    );
  }

  /** `null` = o template não tem credencial (200 com corpo nulo). */
  getUploadTemplateCredential(
    token: string,
    id: number
  ): Promise<UploadTemplateCredential | null> {
    return this.request<UploadTemplateCredential | null>(
      `/admin/upload-templates/${id}/credential`,
      {},
      token
    );
  }

  setUploadTemplateCredential(
    token: string,
    id: number,
    payload: UploadTemplateCredentialInput
  ): Promise<UploadTemplateCredential> {
    return this.request<UploadTemplateCredential>(
      `/admin/upload-templates/${id}/credential`,
      { method: "PUT", body: JSON.stringify(payload) },
      token
    );
  }

  deleteUploadTemplateCredential(token: string, id: number): Promise<void> {
    return this.request(
      `/admin/upload-templates/${id}/credential`,
      { method: "DELETE" },
      token
    );
  }

  /** Testa a definição em rascunho contra uma planilha de verdade: não
   *  grava nada. `definition` vai como JSON num campo do multipart, a
   *  forma do `items` do envio de upload. */
  checkUploadTemplate(
    token: string,
    definition: UploadTemplateInput,
    file: File
  ): Promise<UploadTemplateCheck> {
    const body = new FormData();
    body.set("file", file, file.name);
    body.set("definition", JSON.stringify(definition));
    return this.request<UploadTemplateCheck>(
      "/admin/upload-templates/check",
      { method: "POST", body },
      token
    );
  }

  // --- Consumo de IA ---------------------------------------------------

  getAiUsageSummary(token: string, query: AiUsageQuery): Promise<AiUsageSummary> {
    return this.request<AiUsageSummary>(
      `/admin/ai-usage/summary?${aiUsageQuery(query)}`,
      {},
      token
    );
  }

  listAiUsageCalls(
    token: string,
    query: AiUsageQuery & { page: number; size: number }
  ): Promise<AiUsageCallsPage> {
    return this.request<AiUsageCallsPage>(
      `/admin/ai-usage/calls?${aiUsageQuery(query)}`,
      {},
      token
    );
  }

  /** Empresas ou pessoas com linha no período que casam com `q` — é o
   *  que a caixa de escolha pergunta quando a pessoa digita, pra
   *  alcançar quem ficou fora das 50 do ranking. Paginado como
   *  `/channels`; a caixa pede uma página só. */
  searchAiUsageOptions(
    token: string,
    query: { kind: "company" | "user"; q: string; date_from: string; date_to: string; page?: number; size?: number }
  ): Promise<AiUsageOptionsPage> {
    return this.request<AiUsageOptionsPage>(
      `/admin/ai-usage/options?${aiUsageQuery(query)}`,
      {},
      token
    );
  }

  listAiModels(token: string, includeInactive = false): Promise<AiModel[]> {
    return this.request<AiModel[]>(
      `/admin/ai/models${includeInactive ? "?include_inactive=true" : ""}`,
      {},
      token
    );
  }

  createAiModel(
    token: string,
    payload: { provider: AiProvider; name: string; label?: string | null }
  ): Promise<AiModel> {
    return this.request<AiModel>(
      "/admin/ai/models",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  /**
   * O preço atual do modelo — os dois de uma vez, ou os dois `null`
   * pra tirar o preço. Rota própria, separada do PATCH: o preço é o que
   * cada linha de consumo FOTOGRAFA na hora da chamada, e não pode ser
   * apagado de carona por um toggle de ativação.
   */
  setAiModelPricing(
    token: string,
    id: number,
    payload: { input_price_per_million: string | null; output_price_per_million: string | null }
  ): Promise<AiModel> {
    return this.request<AiModel>(
      `/admin/ai/models/${id}/pricing`,
      { method: "PUT", body: JSON.stringify(payload) },
      token
    );
  }

  /** Só rótulo e ativação: `name` e `provider` são imutáveis no servidor.
   *  Manda SÓ o que mudou — o servidor lê quais campos vieram. */
  updateAiModel(
    token: string,
    id: number,
    payload: { label?: string | null; is_active?: boolean }
  ): Promise<AiModel> {
    return this.request<AiModel>(
      `/admin/ai/models/${id}`,
      { method: "PATCH", body: JSON.stringify(payload) },
      token
    );
  }

  /**
   * Uma linha por tarefa, com o padrão de cada uma (ou `null`).
   *
   * É também a fonte da LISTA DE TAREFAS para a tela de administração:
   * ela é rota de topo, sem empresa ativa, então não pode buscá-la em
   * `/prompt-models`, que é escopada por empresa.
   */
  listAiTaskDefaults(token: string): Promise<TaskDefault[]> {
    return this.request<TaskDefault[]>("/admin/ai/task-defaults", {}, token);
  }

  setAiTaskDefault(token: string, task: string, modelId: number): Promise<PromptModel> {
    return this.request<PromptModel>(
      "/admin/ai/task-defaults",
      { method: "PUT", body: JSON.stringify({ task, model_id: modelId }) },
      token
    );
  }

  clearAiTaskDefault(token: string, task: string): Promise<void> {
    return this.request(
      `/admin/ai/task-defaults/${task}`,
      { method: "DELETE" },
      token
    );
  }

  // --- Administração → CENA (os pesos da fórmula) ----------------------

  getCenaConfig(token: string): Promise<CenaScoreConfig> {
    return this.request<CenaScoreConfig>("/admin/cena/score-config", {}, token);
  }

  /**
   * O corpo leva SEMPRE os 25 campos (mais as 5 flags), não um patch parcial. É o que
   * elimina a ambiguidade do peso neutro, onde "ausente", "null" e "zero"
   * seriam três coisas indistinguíveis e duas delas mudam a nota de toda
   * menção neutra.
   */
  saveCenaConfig(
    token: string,
    values: Record<string, CenaValue>
  ): Promise<CenaSaveResult> {
    return this.request<CenaSaveResult>(
      "/admin/cena/score-config",
      { method: "PUT", body: JSON.stringify(values) },
      token
    );
  }

  /**
   * As empresas com configuração própria, mais o que a busca achar.
   *
   * Nunca a base inteira: são ~12 mil empresas, e sem a busca esta rota
   * devolvia 1 MB para popular um seletor. Mesma razão de `/channels` e
   * `/vehicles` serem paginados com busca.
   */
  listCenaOverrides(token: string, search?: string): Promise<CenaOverrideCompany[]> {
    const query = search?.trim() ? `?q=${encodeURIComponent(search.trim())}` : "";
    return this.request<CenaOverrideCompany[]>(
      `/admin/cena/score-config/overrides${query}`,
      {},
      token
    );
  }

  /** 404 quando a empresa usa o padrão — não devolve a global herdada. */
  getCenaOverride(token: string, companyId: string): Promise<CenaScoreConfig> {
    return this.request<CenaScoreConfig>(
      `/admin/cena/score-config/overrides/${companyId}`,
      {},
      token
    );
  }

  saveCenaOverride(
    token: string,
    companyId: string,
    values: Record<string, CenaValue>
  ): Promise<CenaSaveResult> {
    return this.request<CenaSaveResult>(
      `/admin/cena/score-config/overrides/${companyId}`,
      { method: "PUT", body: JSON.stringify(values) },
      token
    );
  }

  /** Volta a empresa à configuração padrão da instalação. */
  clearCenaOverride(token: string, companyId: string): Promise<void> {
    return this.request(
      `/admin/cena/score-config/overrides/${companyId}`,
      { method: "DELETE" },
      token
    );
  }

  // O CENA da empresa ATIVA, em Ajustes (2026-10-22): Especialista ou
  // acima grava; quem vê a empresa lê. A empresa vem do `X-Company-Id`.
  companyCena(token: string): Promise<CompanyCenaConfig> {
    return this.request<CompanyCenaConfig>("/cena/score-config", {}, token);
  }

  saveCompanyCena(
    token: string,
    values: Record<string, CenaValue>
  ): Promise<CenaSaveResult> {
    return this.request<CenaSaveResult>(
      "/cena/score-config",
      { method: "PUT", body: JSON.stringify(values) },
      token
    );
  }

  clearCompanyCena(token: string): Promise<void> {
    return this.request("/cena/score-config", { method: "DELETE" }, token);
  }

  // --- Administração → Auditoria ---------------------------------------

  listAuditLog(
    token: string,
    filtros: {
      table_name?: string;
      company_id?: string;
      only_global?: boolean;
      action?: string;
      changed_by?: string;
      changed_from?: string;
      changed_to?: string;
      page?: number;
      size?: number;
    } = {}
  ): Promise<ConfigAuditPage> {
    const query = new URLSearchParams();
    for (const [chave, valor] of Object.entries(filtros)) {
      // `encodeURIComponent` embutido no URLSearchParams: sem ele o
      // `+00:00` de uma data ISO chega no servidor como espaço e a
      // requisição volta 422.
      if (valor !== undefined && valor !== "" && valor !== false) {
        query.set(chave, String(valor));
      }
    }
    const sufixo = query.toString();
    return this.request<ConfigAuditPage>(
      `/admin/audit-log${sufixo ? `?${sufixo}` : ""}`,
      {},
      token
    );
  }

  /** As tentativas de login malsucedidas (2026-10-23), mais recentes
   *  primeiro. Só o Máster. */
  listLoginFailures(
    token: string,
    filtros: { occurred_from?: string; occurred_to?: string; page?: number; size?: number } = {}
  ): Promise<LoginFailurePage> {
    const query = new URLSearchParams();
    for (const [chave, valor] of Object.entries(filtros)) {
      if (valor !== undefined && valor !== "") query.set(chave, String(valor));
    }
    const sufixo = query.toString();
    return this.request<LoginFailurePage>(
      `/admin/login-failures${sufixo ? `?${sufixo}` : ""}`,
      {},
      token
    );
  }

  /** O vocabulário vem do servidor: uma cópia aqui envelheceria. */
  /** Quem já alterou algo — as opções do filtro "Quem". */
  listAuditAuthors(token: string): Promise<AuditOption[]> {
    return this.request<AuditOption[]>("/admin/audit-log/authors", {}, token);
  }

  /** As empresas que aparecem na trilha, com busca pelo nome. */
  listAuditCompanies(token: string, q?: string): Promise<AuditOption[]> {
    const query = new URLSearchParams();
    if (q?.trim()) query.set("q", q.trim());
    const sufixo = query.toString();
    return this.request<AuditOption[]>(
      `/admin/audit-log/companies${sufixo ? `?${sufixo}` : ""}`,
      {},
      token
    );
  }

  listAuditedTables(token: string): Promise<string[]> {
    return this.request<string[]>("/admin/audit-log/tables", {}, token);
  }

  listCenaPoints(token: string): Promise<CenaScorePoint[]> {
    return this.request<CenaScorePoint[]>("/admin/cena/points", {}, token);
  }

  /** A chave composta viaja separada por `~` (o formato tem duas colunas). */
  saveCenaPoint(
    token: string,
    table: string,
    key: string[],
    point: string
  ): Promise<CenaScorePoint> {
    return this.request<CenaScorePoint>(
      `/admin/cena/points/${table}/${key.join("~")}`,
      { method: "PUT", body: JSON.stringify({ point }) },
      token
    );
  }

  /** Upsert por tarefa: uma tarefa tem UM modelo. */
  setPromptModel(token: string, task: string, modelId: number): Promise<PromptModel> {
    return this.request<PromptModel>(
      "/prompt-models",
      { method: "PUT", body: JSON.stringify({ task, model_id: modelId }) },
      token
    );
  }

  listSpokespersons(
    token: string,
    opts?: { brandId?: number; includeInactive?: boolean }
  ): Promise<Spokesperson[]> {
    const query = new URLSearchParams();
    if (opts?.brandId !== undefined) query.set("brand_id", String(opts.brandId));
    if (opts?.includeInactive) query.set("include_inactive", "true");
    const qs = query.toString();
    return this.request<Spokesperson[]>(
      `/spokespersons${qs ? `?${qs}` : ""}`,
      {},
      token
    );
  }

  createSpokesperson(
    token: string,
    payload: CreateSpokespersonPayload
  ): Promise<Spokesperson> {
    return this.request<Spokesperson>(
      "/spokespersons",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  updateSpokesperson(
    token: string,
    spokespersonId: number,
    payload: UpdateSpokespersonPayload
  ): Promise<Spokesperson> {
    return this.request<Spokesperson>(
      `/spokespersons/${spokespersonId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
      token
    );
  }

  deactivateSpokesperson(token: string, spokespersonId: number): Promise<Spokesperson> {
    return this.request<Spokesperson>(
      `/spokespersons/${spokespersonId}`,
      { method: "DELETE" },
      token
    );
  }

  // ── Menções: contrato REAL do web_api ────────────────────────────
  // Espelha web_api/infrastructure/http/routes/mentions.py. Filtro e
  // paginação são do SERVIDOR — o contrato morto puxava limit=2000 e
  // filtrava no browser, o que não sobrevive a volume real.

  listMentions(
    token: string,
    options: MentionQuery = {},
    /** `signal` deixa a tela CANCELAR a consulta quando o recorte muda
     *  antes da resposta chegar. `companyId` só é preciso na Home, que não
     *  tem `:companyId` na URL (igual ao `mentionSummary`). `withTotal`
     *  só quando é `false`: ausente = como sempre (o servidor conta). */
    transport: { signal?: AbortSignal; companyId?: string; withTotal?: boolean } = {}
  ): Promise<MentionPage> {
    const query = mentionQueryString(options);
    if (transport.withTotal === false) query.set("with_total", "false");
    return this.request(
      `/mentions?${query}`,
      { signal: transport.signal },
      token,
      transport.companyId ? { companyId: transport.companyId } : undefined
    );
  }

  /** O total do recorte de `listMentions`, pedido à parte quando a
   *  contagem é cara (busca de texto) e a página não espera por ela.
   *  Mesmo recorte, sem paginação nem ordem. */
  /** O total do recorte, à parte. `total: null` com `exact: false` =
   *  a contagem com busca de texto desistiu no orçamento de 2 s do
   *  servidor (2026-10-26): a lista está certa, só o número não veio. */
  countMentions(
    token: string,
    options: MentionQuery = {},
    transport: { signal?: AbortSignal; companyId?: string } = {}
  ): Promise<{ total: number | null; exact: boolean }> {
    const query = mentionQueryString(options);
    query.delete("page");
    query.delete("size");
    query.delete("order");
    return this.request(
      `/mentions/count?${query}`,
      { signal: transport.signal },
      token,
      transport.companyId ? { companyId: transport.companyId } : undefined
    );
  }

  /**
   * Corrige a classificação à mão. PATCH parcial de verdade: o que não
   * for informado NÃO é tocado.
   *
   * `null` em `polarity`/`approach` é valor válido (voltar a ficar sem
   * classificação), por isso "não mexer" é omitir a chave — não mandar
   * `null`. As três listas seguem a mesma regra: ausente não mexe, `[]`
   * esvazia.
   *
   * Devolve o `diff` aplicado; vazio significa que nada mudou de fato e
   * nada foi gravado (nem no histórico).
   */
  updateMentionClassification(
    token: string,
    mentionId: number,
    patch: {
      polarity?: string | null;
      approach?: string | null;
      themeIds?: number[];
      keyMessageIds?: number[];
      spokespersonIds?: number[];
      /** A caixinha "revisada" do editor: ausente não mexe na marca. */
      reviewed?: boolean;
      /** O tema em destaque escolhido. `null` é valor (desfaz a escolha,
       *  volta à sugestão); a chave AUSENTE é que não mexe. */
      highlightThemeId?: number | null;
      /** Citação no título (só imprensa): ausente não mexe. */
      titleCitation?: boolean;
    }
  ): Promise<{ diff: Record<string, { old: unknown; new: unknown }> }> {
    const body: Record<string, unknown> = {};
    if ("polarity" in patch) body.polarity = patch.polarity;
    if ("approach" in patch) body.approach = patch.approach;
    if (patch.themeIds !== undefined) body.theme_ids = patch.themeIds;
    if (patch.keyMessageIds !== undefined) body.key_message_ids = patch.keyMessageIds;
    if (patch.spokespersonIds !== undefined) body.spokesperson_ids = patch.spokespersonIds;
    if (patch.reviewed !== undefined) body.reviewed = patch.reviewed;
    if ("highlightThemeId" in patch) body.highlight_theme_id = patch.highlightThemeId;
    if (patch.titleCitation !== undefined) body.title_citation = patch.titleCitation;
    return this.request(
      `/mentions/${mentionId}/classification`,
      { method: "PATCH", body: JSON.stringify(body) },
      token
    );
  }

  /**
   * Corrige a classificação de VÁRIAS menções de uma vez (2026-09-21).
   *
   * Dois alvos, um por chamada: `mentionIds` (a seleção explícita) ou
   * `query` (o recorte da tela — vai na querystring, pela MESMA
   * montagem da listagem, e o servidor congela os ids no clique). O
   * servidor ignora `page`/`size` da query; o `scope` VALE desde
   * 2026-10-04 (o lote segue o escopo da tela), e na seleção explícita
   * ele viaja ao lado dos ids.
   *
   * A resposta já vem `FINISHED` quando o lote coube na requisição;
   * `QUEUED` quando foi pra fila — aí é `getBulkEdit` até terminar.
   */
  submitBulkClassification(
    token: string,
    target: BulkTarget,
    patch: BulkPatchWire
  ): Promise<BulkEdit> {
    const query = bulkTargetQuery(target);
    const body: Record<string, unknown> = { patch };
    if ("mentionIds" in target) body.mention_ids = target.mentionIds;
    return this.request(
      `/mentions/bulk-classification?${query}`,
      { method: "POST", body: JSON.stringify(body) },
      token
    );
  }

  /** As marcas pra onde dá pra vincular uma menção à mão: as ativas da
   *  empresa e as dos concorrentes ativos. */
  linkTargets(token: string): Promise<LinkTarget[]> {
    return this.request("/mentions/link-targets", {}, token);
  }

  /**
   * Vincula cada menção a cada marca escolhida, à mão (2026-10-03) — a
   * outra metade do descarte. `discardSource` é o "mover": descarta a
   * menção de origem depois de vincular.
   */
  linkMentions(
    token: string,
    payload: { mentionIds: number[]; targets: LinkTargetWire[]; discardSource: boolean }
  ): Promise<LinkMentionsResult> {
    return this.request(
      "/mentions/links",
      {
        method: "POST",
        body: JSON.stringify({
          mention_ids: payload.mentionIds,
          targets: payload.targets,
          discard_source: payload.discardSource
        })
      },
      token
    );
  }

  /**
   * De que marcas são as menções do alvo e quais subtemas TODAS têm. O
   * alvo é o MESMO do envio: seleção explícita no corpo, ou o recorte
   * na querystring.
   */
  bulkTarget(token: string, target: BulkTarget): Promise<BulkTargetSummary> {
    const query = bulkTargetQuery(target);
    const body: Record<string, unknown> = {};
    if ("mentionIds" in target) body.mention_ids = target.mentionIds;
    return this.request(
      `/mentions/bulk-classification/target?${query}`,
      { method: "POST", body: JSON.stringify(body) },
      token
    );
  }

  /**
   * O resumo da seleção (visão Justificativas, 2026-10-20). O alvo é o
   * MESMO do lote; só lê. `signal` cancela a consulta velha quando a
   * pessoa pede de novo.
   */
  selectionBreakdown(
    token: string,
    target: BulkTarget,
    options: { signal?: AbortSignal } = {}
  ): Promise<SelectionBreakdown> {
    const query = bulkTargetQuery(target);
    const body: Record<string, unknown> = {};
    if ("mentionIds" in target) body.mention_ids = target.mentionIds;
    return this.request(
      `/mentions/selection-breakdown?${query}`,
      { method: "POST", body: JSON.stringify(body), signal: options.signal },
      token
    );
  }

  /**
   * Descarta uma menção — soft-delete (2026-09-21): some da lista, do
   * resumo e de relatório novo; relatório salvo não muda. Quem
   * descartou e o motivo vão pro carimbo e pra trilha de correções.
   */
  discardMention(token: string, mentionId: number, reason?: string): Promise<void> {
    return this.request(
      `/mentions/${mentionId}/discard`,
      { method: "POST", body: JSON.stringify(reason ? { reason } : {}) },
      token
    );
  }

  /** O "desfazer" do descarte — e a recuperação pela lista
   *  `discarded: true`. */
  restoreMention(token: string, mentionId: number): Promise<void> {
    return this.request(`/mentions/${mentionId}/restore`, { method: "POST" }, token);
  }

  /** Marca (ou desmarca) a menção como REVISADA (2026-10-03). Grava
   *  linha na trilha da correção; repetir o mesmo estado não grava. */
  setMentionReviewed(
    token: string,
    mentionId: number,
    reviewed: boolean
  ): Promise<void> {
    return this.request(
      `/mentions/${mentionId}/${reviewed ? "review" : "unreview"}`,
      { method: "POST" },
      token
    );
  }

  getBulkEdit(token: string, bulkEditId: number): Promise<BulkEdit> {
    return this.request(`/mentions/bulk-edits/${bulkEditId}`, {}, token);
  }

  /** A lista colada de links/códigos vira uma lista guardada no
   *  servidor (2026-10-03). POST com corpo porque o texto é grande;
   *  quem filtra depois é o `listMentions` de sempre, com
   *  `referenceSetId`. */
  /** `scope` diz ONDE os links são procurados — o mesmo escopo da tela
   *  (decisão da dona, 2026-10-04). A lista em si é sempre da empresa. */
  createMentionReferenceSet(
    token: string,
    text: string,
    scope: MentionScope = "own"
  ): Promise<MentionReferenceSet> {
    const query = scope === "own" ? "" : `?scope=${scope}`;
    return this.request(
      `/mentions/reference-sets${query}`,
      { method: "POST", body: JSON.stringify({ text }) },
      token
    );
  }

  /** Os lotes ainda em andamento da empresa — pra tela retomar o
   *  acompanhamento depois de um F5. */
  listBulkEdits(token: string): Promise<BulkEdit[]> {
    return this.request("/mentions/bulk-edits", {}, token);
  }

  /** O histórico de lotes da empresa, mais novo primeiro — Coordenador
   *  ou acima (2026-10-22). */
  listBulkEditHistory(
    token: string,
    params: { page?: number; size?: number } = {}
  ): Promise<BulkEditHistoryPage> {
    const query = new URLSearchParams();
    query.set("page", String(params.page ?? 1));
    query.set("size", String(params.size ?? 25));
    return this.request(`/mentions/bulk-edits/history?${query.toString()}`, {}, token);
  }

  /** Desfaz um lote: devolve o lote NOVO (`FINISHED` até 100 menções;
   *  acima disso `QUEUED`, e a tela acompanha por `getBulkEdit`). */
  undoBulkEdit(token: string, bulkEditId: number): Promise<BulkEdit> {
    return this.request(
      `/mentions/bulk-edits/${bulkEditId}/undo`,
      { method: "POST" },
      token
    );
  }

  listBulkEditFailures(
    token: string,
    bulkEditId: number,
    limit = 50
  ): Promise<BulkEditFailure[]> {
    return this.request(
      `/mentions/bulk-edits/${bulkEditId}/failures?limit=${limit}`,
      {},
      token
    );
  }

  /**
   * Reprocessamento por IA (2026-10-05). O alvo é o MESMO do lote — o
   * recorte da tela ou os ids marcados — e o corpo diz quais tarefas
   * refazer. Três passos: estimar (sem IA), amostrar (fila rápida, não
   * grava) e aplicar (fila lenta, grava com trilha).
   */
  estimateAiReprocess(
    token: string,
    target: BulkTarget,
    request: AiReprocessRequest
  ): Promise<AiReprocessEstimate> {
    return this.request(
      `/mentions/ai-reprocess/estimate?${bulkTargetQuery(target)}`,
      { method: "POST", body: JSON.stringify(aiReprocessBody(target, request)) },
      token
    );
  }

  sampleAiReprocess(
    token: string,
    target: BulkTarget,
    request: AiReprocessRequest
  ): Promise<AiReprocessRun> {
    return this.request(
      `/mentions/ai-reprocess/sample?${bulkTargetQuery(target)}`,
      { method: "POST", body: JSON.stringify(aiReprocessBody(target, request)) },
      token
    );
  }

  applyAiReprocess(
    token: string,
    target: BulkTarget,
    request: AiReprocessRequest & { sampleRunId: number | null }
  ): Promise<AiReprocessRun> {
    return this.request(
      `/mentions/ai-reprocess?${bulkTargetQuery(target)}`,
      {
        method: "POST",
        body: JSON.stringify({
          ...aiReprocessBody(target, request),
          sample_run_id: request.sampleRunId
        })
      },
      token
    );
  }

  /**
   * "Pedir melhorias à IA" (2026-10-21). Só a SELEÇÃO explícita (1 a 30
   * menções), UMA tarefa e o comentário da pessoa. Responde 202 na hora com
   * a consultoria `QUEUED`; a IA roda fora da requisição e a tela acompanha
   * por `getRuleReview`. 409 = já há uma ativa na empresa; 429 = cota.
   */
  requestRuleReview(
    token: string,
    payload: { mention_ids: number[]; task: RuleReviewTask; comment: string }
  ): Promise<RuleReview> {
    return this.request(
      "/mentions/rule-reviews",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  /** As consultorias da empresa, mais recentes primeiro. */
  listRuleReviews(
    token: string,
    options: { page?: number; size?: number } = {}
  ): Promise<PageOf<RuleReview>> {
    const query = new URLSearchParams({
      page: String(options.page ?? 1),
      size: String(options.size ?? 20)
    });
    return this.request(`/mentions/rule-reviews?${query}`, {}, token);
  }

  getRuleReview(token: string, reviewId: number): Promise<RuleReviewDetail> {
    return this.request(`/mentions/rule-reviews/${reviewId}`, {}, token);
  }

  /** Registra o que a pessoa fez com a proposta `index`. Não escreve regra:
   *  a escrita é pelas rotas de cadastro, antes disto. */
  recordRuleReviewOutcome(
    token: string,
    reviewId: number,
    index: number,
    outcome: RuleReviewOutcome
  ): Promise<RuleReview> {
    return this.request(
      `/mentions/rule-reviews/${reviewId}/applied`,
      { method: "POST", body: JSON.stringify({ index, outcome }) },
      token
    );
  }

  getAiReprocess(token: string, runId: number): Promise<AiReprocessRun> {
    return this.request(`/mentions/ai-reprocess/${runId}`, {}, token);
  }

  /** As aplicações mais recentes — pra tela retomar o acompanhamento
   *  depois de um F5. */
  listAiReprocess(token: string, limit = 5): Promise<AiReprocessRun[]> {
    return this.request(`/mentions/ai-reprocess?limit=${limit}`, {}, token);
  }

  listAiReprocessItems(
    token: string,
    runId: number,
    options: { outcomes?: AiReprocessItemOutcome[]; limit?: number } = {}
  ): Promise<AiReprocessItem[]> {
    const query = new URLSearchParams();
    for (const outcome of options.outcomes ?? []) query.append("outcome", outcome);
    query.set("limit", String(options.limit ?? 50));
    return this.request(
      `/mentions/ai-reprocess/${runId}/items?${query}`,
      {},
      token
    );
  }

  /** Volta a menção a uma versão da trilha (2026-10-31): `toEditId` nulo
   *  é a versão original. `expectedLatestEditId` é a edição mais nova que
   *  a tela mostrava — se a trilha andou, o servidor responde 409. */
  revertMentionClassification(
    token: string,
    mentionId: number,
    toEditId: number | null,
    expectedLatestEditId: number
  ): Promise<ClassificationRevertResult> {
    return this.request(
      `/mentions/${mentionId}/classification/revert`,
      {
        method: "POST",
        body: JSON.stringify({ to_edit_id: toEditId, expected_latest_edit_id: expectedLatestEditId })
      },
      token
    );
  }

  /** A trilha de correções manuais, mais recente primeiro. */
  mentionClassificationHistory(
    token: string,
    mentionId: number
  ): Promise<ClassificationEdit[]> {
    return this.request(
      `/mentions/${mentionId}/classification-history`,
      {},
      token
    );
  }

  /** O histórico de edições da empresa inteira, mais recente primeiro —
   *  Administrador ou acima. `changed_from`/`changed_to` são instantes ISO. */
  listClassificationHistory(
    token: string,
    filtros: {
      changed_by?: string;
      kind?: string;
      /** Só as linhas geradas por este lote de edição. */
      bulk_edit_id?: number;
      changed_from?: string;
      changed_to?: string;
      page?: number;
      size?: number;
    } = {}
  ): Promise<MentionEditPage> {
    const query = new URLSearchParams();
    for (const [chave, valor] of Object.entries(filtros)) {
      // `encodeURIComponent` embutido no URLSearchParams: sem ele o
      // `+00:00` de uma data ISO chega no servidor como espaço e a
      // requisição volta 422.
      if (valor !== undefined && valor !== "") {
        query.set(chave, String(valor));
      }
    }
    const sufixo = query.toString();
    return this.request<MentionEditPage>(
      `/mentions/classification-history${sufixo ? `?${sufixo}` : ""}`,
      {},
      token
    );
  }

  /** Quem já corrigiu classificação na empresa — as opções do filtro
   *  "Editado por" da tela de menções. */
  /** Quem já corrigiu classificação nas empresas do escopo — o mesmo
   *  `scope` da lista, porque o editor de uma sombra só aparece nele. */
  listMentionEditors(
    token: string,
    scope: MentionScope = "own"
  ): Promise<MentionEditor[]> {
    const query = scope === "own" ? "" : `?scope=${scope}`;
    return this.request(`/mentions/editors${query}`, {}, token);
  }

  /** As clipadoras das menções da empresa — as opções do filtro
   *  "Clipadora". Lista curta (uma por clipadora que já subiu upload,
   *  mais "Legado (migrado)" quando há post migrado). */
  clippingServices(token: string): Promise<ClippingServiceOption[]> {
    return this.request("/mentions/clipping-services", {}, token);
  }

  /** O prompt e a resposta que a IA usou pra classificar esta menção.
   *
   * `status: "absent"` cobre três situações que o backend não tem como
   * distinguir — classificação sem IA, menção anterior à memória, ou
   * rastro vencido — e por isso a tela diz as três em vez de escolher
   * uma. O rastro é mantido por 60 dias. */
  mentionAiTrace(token: string, mentionId: number): Promise<MentionAiTrace> {
    return this.request(`/mentions/${mentionId}/ai-trace`, {}, token);
  }

  // --- Relatório CENA de redes sociais ---------------------------------
  //
  // O cálculo roda no worker do `elt/`, não aqui: `requestSocialReport`
  // devolve 202 com uma execução em `QUEUED`, e a tela chama
  // `socialReportRun` até virar `FINISHED` (com rascunho) ou
  // `FINISHED_EMPTY`. Nenhuma rota leva `companyId` — o header já vai.

  /** `windowStart`/`windowEnd` são INSTANTES (com fuso), não dias — a
   *  janela do relatório tem hora desde 2026-08-27. Converta com
   *  `localInstant`, que é o mesmo helper da tela de menções: é o que
   *  faz o total das duas telas bater.
   *
   *  `recorte` é o resto do filtro, montado por `recorteToPayload`
   *  (`features/reports/report-recorte.ts`, dono único da tradução
   *  dimensão → campo do payload). São as doze dimensões que vieram de
   *  menções, cada uma com sua flag de exclusão; chegam uma vez, na
   *  geração, e ficam CONGELADAS na foto — o recálculo não as reenvia,
   *  o servidor as herda do rascunho.
   *
   *  Espalhar o recorte em vez de enumerar os campos aqui é
   *  deliberado: o corpo recusa campo desconhecido (422), então uma
   *  dimensão nova esquecida NESTA função apareceria como erro
   *  imediato, e não como filtro silenciosamente ignorado. */
  requestSocialReport(
    token: string,
    input: {
      windowStart: string;
      windowEnd: string;
      recorte?: Record<string, unknown>;
    }
  ): Promise<SocialReportRun> {
    return this.request(
      "/social-reports/runs",
      {
        method: "POST",
        body: JSON.stringify({
          window_start: input.windowStart,
          window_end: input.windowEnd,
          ...(input.recorte ?? {})
        })
      },
      token
    );
  }

  socialReportRun(token: string, runId: number): Promise<SocialReportRun> {
    return this.request(`/social-reports/runs/${runId}`, {}, token);
  }

  listSocialReportRuns(
    token: string,
    options: { page?: number; size?: number; status?: string } = {}
  ): Promise<PageOf<SocialReportRun>> {
    const query = new URLSearchParams();
    if (options.page) query.set("page", String(options.page));
    if (options.size) query.set("size", String(options.size));
    if (options.status) query.set("status", options.status);
    return this.request(`/social-reports/runs?${query.toString()}`, {}, token);
  }

  listSocialReportDrafts(
    token: string,
    options: { includeExpired?: boolean } = {}
  ): Promise<SocialReportDraft[]> {
    const query = options.includeExpired ? "?include_expired=true" : "";
    return this.request(`/social-reports/drafts${query}`, {}, token);
  }

  socialReportDraft(
    token: string,
    draftId: number,
    options: {
      order?: SocialReportItemOrder;
      page?: number;
      size?: number;
      filters?: SnapshotFacetFilters;
    } = {}
  ): Promise<SocialReportDraftDetail> {
    return this.request(
      `/social-reports/drafts/${draftId}?${itemQueryString(options)}`,
      {},
      token
    );
  }

  /**
   * Refaz os números do rascunho preservando o id que está aberto na
   * tela. O que não for informado é herdado do próprio rascunho —
   * `brandIdGiven` separa "não mexi na marca" de "tirei o recorte de
   * marca", que em `brandId` seriam os dois `null`.
   */
  /** O recálculo manda só o que o FORMULÁRIO edita — janela e marca.
   *
   *  As outras onze dimensões são herdadas do rascunho, no servidor, e
   *  reenviá-las daqui seria o caminho mais curto pra perdê-las: elas
   *  não têm controle nesta tela, então viveriam num estado que a
   *  pessoa não vê nem consegue limpar.
   *
   *  `brandsGiven` distingue "não mexi na marca" de "tirei o recorte de
   *  marca", que em `brandIds` seriam os dois vazios — e governa o PAR
   *  (lista + exclusão), porque herdar um sem o outro inverteria o
   *  recorte. */
  recalculateSocialReportDraft(
    token: string,
    draftId: number,
    input: {
      windowStart?: string;
      windowEnd?: string;
      brandIds?: number[];
      excludeBrands?: boolean;
      brandsGiven?: boolean;
      scope?: MentionScope;
    } = {}
  ): Promise<SocialReportRun> {
    return this.request(
      `/social-reports/drafts/${draftId}/recalculate`,
      {
        method: "POST",
        body: JSON.stringify({
          window_start: input.windowStart ?? null,
          window_end: input.windowEnd ?? null,
          brand_ids: input.brandIds ?? [],
          exclude_brands: input.excludeBrands ?? false,
          brands_given: input.brandsGiven ?? false,
          scope: input.scope ?? null
        })
      },
      token
    );
  }

  /** Salva o rascunho como relatório permanente. O nome é obrigatório —
   *  o servidor recusa vazio com uma frase, que o diálogo mostra. */
  promoteSocialReportDraft(
    token: string,
    draftId: number,
    input: { name: string }
  ): Promise<{ report_id: number; draft_id: number }> {
    return this.request(
      `/social-reports/drafts/${draftId}/promote`,
      { method: "POST", body: JSON.stringify({ name: input.name }) },
      token
    );
  }

  /** Refaz a foto de um relatório SALVO com o recorte original sobre a
   *  base de hoje (2026-10-05). Devolve a execução — a tela acompanha
   *  por `socialReportRun` como qualquer geração. */
  refreshSocialReport(token: string, reportId: number): Promise<SocialReportRun> {
    return this.request(
      `/social-reports/${reportId}/refresh`,
      { method: "POST" },
      token
    );
  }

  discardSocialReportDraft(token: string, draftId: number): Promise<void> {
    return this.request(
      `/social-reports/drafts/${draftId}`,
      { method: "DELETE" },
      token
    );
  }

  listSocialReports(
    token: string,
    options: {
      brandId?: number | null;
      dateFrom?: string;
      dateTo?: string;
      page?: number;
      size?: number;
    } = {}
  ): Promise<PageOf<SocialReportSummary>> {
    const query = new URLSearchParams();
    if (options.brandId) query.set("brand_id", String(options.brandId));
    if (options.dateFrom) query.set("date_from", options.dateFrom);
    if (options.dateTo) query.set("date_to", options.dateTo);
    if (options.page) query.set("page", String(options.page));
    if (options.size) query.set("size", String(options.size));
    return this.request(`/social-reports?${query.toString()}`, {}, token);
  }

  socialReport(
    token: string,
    reportId: number,
    options: {
      order?: SocialReportItemOrder;
      page?: number;
      size?: number;
      filters?: SnapshotFacetFilters;
    } = {}
  ): Promise<SocialReportDetail> {
    return this.request(
      `/social-reports/${reportId}?${itemQueryString(options)}`,
      {},
      token
    );
  }

  /**
   * Pede a planilha do relatório salvo (ou do upload) em segundo plano:
   * responde 202 com a linha NOVA, ou 200 com a já ativa quando o pedido
   * é igual (duplo clique). O recorte de leitura vai na querystring, o
   * mesmo da lista — o que está na tela é o que sai no arquivo. O worker
   * gera, o sino avisa e a página de Downloads entrega.
   */
  createExport(
    token: string,
    payload: CreateExportPayload,
    filters?: SnapshotFacetFilters
  ): Promise<ExportRow> {
    const query = filters ? itemQueryString({ filters }).toString() : "";
    return this.request<ExportRow>(
      `/exports${query ? `?${query}` : ""}`,
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  /**
   * Pede a planilha das menções VIVAS do recorte da tela de Menções, em
   * segundo plano (mesmo desfecho do `createExport`: 202 nova, 200 a já
   * ativa, 409 falha em retomada, 422 recorte inválido). O recorte vai
   * inteiro no corpo JSON, sem querystring.
   */
  createMentionsExport(token: string, payload: MentionsExportPayload): Promise<ExportRow> {
    return this.request<ExportRow>(
      "/exports/mentions",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  /** O catálogo de colunas da planilha (colunas padrão + dados extras). */
  async exportColumns(token: string): Promise<ExportColumn[]> {
    const body = await this.request<{ columns: ExportColumn[] }>("/exports/columns", {}, token);
    return body.columns;
  }

  listExportColumnPresets(token: string): Promise<ExportColumnPreset[]> {
    return this.request<ExportColumnPreset[]>("/exports/column-presets", {}, token);
  }

  createExportColumnPreset(
    token: string,
    payload: {
      name: string;
      columns: string[];
      header: boolean;
      theme_layout: ExportThemeLayout;
      highlight_only: boolean;
    }
  ): Promise<ExportColumnPreset> {
    return this.request<ExportColumnPreset>(
      "/exports/column-presets",
      { method: "POST", body: JSON.stringify(payload) },
      token
    );
  }

  deleteExportColumnPreset(token: string, presetId: number): Promise<void> {
    return this.request<void>(
      `/exports/column-presets/${presetId}`,
      { method: "DELETE" },
      token
    );
  }

  /** "Gerar de novo": repete o pedido (mesmo tipo, alvo, formato e
   *  recorte congelado) como uma linha nova. */
  retryExport(token: string, exportId: number): Promise<ExportRow> {
    return this.request<ExportRow>(
      `/exports/${exportId}/again`,
      { method: "POST" },
      token
    );
  }

  listExports(
    token: string,
    options: { page?: number; size?: number; kind?: ExportKind } = {}
  ): Promise<ExportPage> {
    const query = new URLSearchParams({
      page: String(options.page ?? 1),
      size: String(options.size ?? 20)
    });
    if (options.kind) query.set("kind", options.kind);
    return this.request<ExportPage>(`/exports?${query}`, {}, token);
  }

  getExport(token: string, exportId: number): Promise<ExportRow> {
    return this.request<ExportRow>(`/exports/${exportId}`, {}, token);
  }

  /** O arquivo pronto. 409 (com a saída na mensagem) se ainda não
   *  terminou ou já expirou. */
  downloadExport(token: string, exportId: number): Promise<Blob> {
    return requestBlob(this.baseUrl, `/exports/${exportId}/download`, token);
  }

  /**
   * A MESMA lista, agrupada por publicação em vez de por marca.
   *
   * Aceita exatamente os mesmos filtros de `listMentions` — os dois são
   * duas leituras do mesmo recorte, e um filtro que só existisse num
   * deles seria armadilha ao trocar de visão.
   *
   * **Devolve `has_next`, não `total`**: contar publicações exige
   * agrupar todas as menções da empresa (medido: ~45% do custo da tela
   * numa empresa de 500 mil menções, só pro rótulo do rodapé). A
   * paginação já era `‹ ›`, então o booleano basta.
   */
  listPublications(
    token: string,
    options: MentionQuery = {},
    transport: { signal?: AbortSignal } = {}
  ): Promise<HasNextPageOf<ApiPublication>> {
    return this.request(
      `/publications?${mentionQueryString(options)}`,
      { signal: transport.signal },
      token
    );
  }

  /** Detalhe: mesmo shape da listagem + o texto completo. */
  mentionDetail(token: string, mentionId: number): Promise<ApiMention> {
    return this.request(`/mentions/${mentionId}`, {}, token);
  }

  /**
   * Gera o resumo da IA de uma menção que não tem (2026-10-31). Técnico ou
   * acima; 422 sem texto, 429 na cota (`Retry-After` no `ApiError`) e 503
   * sem IA configurada.
   */
  generateMentionSummary(
    token: string,
    mentionId: number
  ): Promise<{ summary: string; summary_origin: "on_demand" }> {
    return this.request(`/mentions/${mentionId}/summary`, { method: "POST" }, token);
  }

  /** As preferências da PESSOA (`GET /me/preferences`). Hoje só a vista do
   *  texto das menções (2026-10-31). */
  myPreferences(token: string): Promise<MyPreferences> {
    return this.request("/me/preferences", {}, token);
  }

  /** Grava só o que veio (`PATCH`; chave desconhecida é 422). */
  updateMyPreferences(token: string, patch: Partial<MyPreferences>): Promise<MyPreferences> {
    return this.request(
      "/me/preferences",
      { method: "PATCH", body: JSON.stringify(patch) },
      token
    );
  }

  /**
   * Marcas da empresa no contrato REAL (`GET /api/brands`, escopo pela
   * credencial). Distinto do `listBrands` legado logo abaixo, que fala
   * `/companies/{id}/brands` do backend descartado e ainda é usado pela
   * `SettingsPage` — some quando a tela de Ajustes for repontada.
   */
  brands(token: string): Promise<BrandOut[]> {
    return this.request<BrandOut[]>("/brands", {}, token);
  }

  /**
   * Domínio de polaridade da instalação — `core/polarity_domain.json`
   * servido pelo web_api. Sem token: catálogo global, rota sem
   * autenticação (mesmo precedente de `/upload-templates`).
   */
  /** `tone` é o BALDE de agregação (Favorável soma com Positivo) e
   * `palette` é o papel de COR (uma por polaridade, 1:1 com a paleta da
   * marca). Os dois são semânticos — hex nunca vem do servidor. */
  polarities(): Promise<
    Array<{
      name: string;
      label: string;
      tone: "positive" | "negative" | "neutral";
      palette: "positive" | "favorable" | "unfavorable" | "negative" | "neutral";
      scales: Array<"four_level" | "three_level">;
    }>
  > {
    return this.request(`/polarities`, {});
  }

  /** `GET /api/login-options` — pública, perguntada ANTES de haver
   *  sessão: `provider` é o alias do login por pessoa (`"google"`), ou
   *  nulo com o modo desligado. */
  loginOptions(): Promise<{ provider: string | null }> {
    return this.request(`/login-options`, {});
  }

  /**
   * `GET /api/me` — o login_user_id de quem chama + as empresas que ele
   * enxerga (`CompanyUserLink`). É o que alimenta sidebar/switcher.
   * Lista vazia NÃO é erro: é o estado "nenhuma empresa vinculada".
   * Nome/e-mail não vêm daqui. Os papéis vêm dos claims do access token
   * que o backend já validou, evitando depender de mapper no id_token.
   */
  me(token: string): Promise<{
    user_id: string;
    roles: string[];
    companies: Array<{
      id: string;
      name: string;
      displays_cena: boolean;
      polarity_scale: "four_level" | "three_level";
      is_active: boolean;
      /** Versão da logo da empresa (md5), ou nulo sem logo (2026-10-16). */
      logo_version?: string | null;
      /** Núcleos ativos da empresa (2026-11-03). O Máster recebe todos;
       *  os demais, só os núcleos de que fazem parte. */
      groups?: CompanyGroupRef[];
    }>;
    /** Por onde a sessão entrou (`"google"`), ou nulo pro login direto
     *  no Keycloak. */
    login_provider?: string | null;
    /** Nome, e-mail e método de login do CADASTRO (2026-10-14): o nome
     *  que vale é o do convite, não o do token. Nulos sem cadastro. */
    name?: string | null;
    email?: string | null;
    login_method?: string | null;
    /** Quando a foto de perfil mudou pela última vez, ou nulo sem foto
     *  (2026-10-15). É a versão da foto. */
    avatar_updated_on?: string | null;
  }> {
    return this.request(`/me`, {}, token);
  }

  /** A foto de perfil da própria pessoa, em bytes. A rota exige o token,
   *  então a tela a baixa por aqui e mostra por `URL.createObjectURL`. */
  getMyAvatar(token: string): Promise<Blob> {
    return requestBlob(this.baseUrl, "/me/avatar", token);
  }

  /** Marcas do Logo.dev pelo NOME (2026-10-16). Passa pelo servidor porque
   *  a busca exige a chave secreta; 503 quando ela não está configurada. */
  searchLogos(token: string, query: string): Promise<Array<{ name: string; domain: string }>> {
    return this.request(`/companies/logo-search?q=${encodeURIComponent(query)}`, {}, token);
  }

  /** A logo de uma empresa que a pessoa enxerga (a em cores, ou a em cinza
   *  quando só há ela). Exige o token como a foto, então a casca a baixa
   *  por aqui e a mostra por `URL.createObjectURL`. */
  getCompanyLogo(token: string, companyId: string): Promise<Blob> {
    return requestBlob(this.baseUrl, `/me/companies/${encodeURIComponent(companyId)}/logo`, token);
  }

  /** Troca a foto. O servidor confere o tipo pelos bytes (JPEG, PNG ou
   *  WebP) e recusa acima de 200 KB; a tela reduz antes de mandar. */
  uploadMyAvatar(token: string, image: Blob): Promise<void> {
    const body = new FormData();
    body.set("file", image, "foto");
    return this.request<void>("/me/avatar", { method: "PUT", body }, token);
  }

  deleteMyAvatar(token: string): Promise<void> {
    return this.request<void>("/me/avatar", { method: "DELETE" }, token);
  }

  /** As sessões do ReputAItion de quem chama, mais recentes primeiro. */
  listMySessions(token: string): Promise<MySession[]> {
    return this.request(`/me/sessions`, {}, token);
  }

  /** O "Sair" (2026-10-20): a sessão desta aba deixa de valer na API antes
   *  de a pessoa ir ao logout do Keycloak. */
  logoutMySession(token: string): Promise<void> {
    return this.request(`/me/logout`, { method: "POST" }, token);
  }

  /** As chaves de acesso (passkey) da própria pessoa (2026-10-21). */
  listMyPasskeys(token: string): Promise<MyPasskey[]> {
    return this.request(`/me/passkeys`, {}, token);
  }

  /** Apaga uma passkey da própria pessoa — a do aparelho perdido ou
   *  trocado. Senha e código continuam valendo. */
  removeMyPasskey(token: string, passkeyId: string): Promise<void> {
    return this.request(
      `/me/passkeys/${encodeURIComponent(passkeyId)}`,
      { method: "DELETE" },
      token
    );
  }

  /** Encerra uma sessão da própria pessoa. No Keycloak a sessão é do
   *  navegador: os outros sistemas da FSB abertos nele saem junto. */
  endMySession(token: string, sessionId: string): Promise<void> {
    return this.request(
      `/me/sessions/${encodeURIComponent(sessionId)}`,
      { method: "DELETE" },
      token
    );
  }

  /** A Central de ajuda: o suporte por chamado está ligado aqui? */
  getSupportStatus(token: string): Promise<SupportStatus> {
    return this.request(`/me/support`, {}, token);
  }

  /** Abre o chamado no helpdesk em nome de quem chama (nome e e-mail vêm
   *  do cadastro, no servidor). */
  /** `multipart`: o chamado em JSON no campo `payload` e os anexos em
   *  `files` (2026-10-20), a forma do envio de planilhas. */
  openSupportTicket(
    token: string,
    input: SupportTicketInput,
    files: File[] = []
  ): Promise<SupportTicket> {
    const body = new FormData();
    body.set("payload", JSON.stringify(input));
    for (const file of files) body.append("files", file, file.name);
    return this.request(`/me/support/tickets`, { method: "POST", body }, token);
  }

  /** Só Máster. Idempotente; nada é apagado (ver
   * `Company.deleted_on`). A empresa é a do caminho, não a da sessão. */
  disableCompany(token: string, companyId: string): Promise<CompanyState> {
    return this.request(`/companies/${encodeURIComponent(companyId)}/disable`, { method: "POST" }, token);
  }

  enableCompany(token: string, companyId: string): Promise<CompanyState> {
    return this.request(`/companies/${encodeURIComponent(companyId)}/enable`, { method: "POST" }, token);
  }

  listAccessUsers(token: string): Promise<AccessUser[]> {
    return this.request("/admin/users", {}, token);
  }

  accessUser(token: string, userId: number): Promise<AccessUserDetail> {
    return this.request(`/admin/users/${userId}`, {}, token);
  }

  createAccessUser(
    token: string,
    payload: {
      name: string;
      email: string;
      role: AccessUser["role"];
      login_method?: LoginMethod;
      /** O acesso com que a conta nasce (2026-10-22, RN-02/04). */
      group_ids?: number[];
      company_ids?: string[];
    }
  ): Promise<AccessUser & { welcome_email?: WelcomeEmailOutcome | null }> {
    return this.request("/admin/users", {
      method: "POST",
      body: JSON.stringify(payload)
    }, token);
  }

  /** Corrige o nome do cadastro — o que vale no ReputAItion inteiro
   *  (2026-10-15). Máster, ou Administrador pra Especialista e Técnico. */
  renameAccessUser(token: string, userId: number, name: string): Promise<AccessUser> {
    return this.request(
      `/admin/users/${userId}/name`,
      { method: "PUT", body: JSON.stringify({ name }) },
      token
    );
  }

  /** `welcome_email`: o e-mail de boas-vindas da conta Google (2026-10-27),
   *  que sai junto com o reenvio; nulo quando a conta recebe o do Keycloak. */
  resendAccessInvite(
    token: string,
    userId: number
  ): Promise<{ welcome_email: WelcomeEmailOutcome | null }> {
    return this.request(`/admin/users/${userId}/resend-invite`, { method: "POST" }, token);
  }

  setAccessUserEnabled(token: string, userId: number, enabled: boolean): Promise<AccessUser> {
    return this.request(`/admin/users/${userId}/${enabled ? "enable" : "disable"}`, { method: "POST" }, token);
  }

  /** Suspensão manual (RN-11): só por afastamento. Não mexe em cargo nem
   *  vínculos — reativar devolve tudo como estava. */
  suspendAccessUser(
    token: string,
    userId: number,
    payload: { reason: "leave"; note?: string | null }
  ): Promise<AccessUser> {
    return this.request(`/admin/users/${userId}/suspend`, {
      method: "POST",
      body: JSON.stringify(payload)
    }, token);
  }

  reactivateAccessUser(token: string, userId: number): Promise<AccessUser> {
    return this.request(`/admin/users/${userId}/reactivate`, { method: "POST" }, token);
  }

  setAccessUserLoginMethod(
    token: string,
    userId: number,
    loginMethod: LoginMethod
  ): Promise<AccessUser> {
    return this.request(`/admin/users/${userId}/login-method`, {
      method: "PUT",
      body: JSON.stringify({ login_method: loginMethod })
    }, token);
  }

  /** Manda à pessoa o e-mail de definir uma senha nova (vale 24 h). Só o
   *  Máster, e só pra quem não entra pela conta Google. */
  resetAccessUserPassword(token: string, userId: number): Promise<void> {
    return this.request(`/admin/users/${userId}/reset-password`, { method: "POST" }, token);
  }

  /** O "Desbloquear" do Máster (2026-10-22): libera antes do tempo a conta
   *  bloqueada por tentativas erradas de login. */
  unlockAccessUserLogin(token: string, userId: number): Promise<void> {
    return this.request(`/admin/users/${userId}/unlock-login`, { method: "POST" }, token);
  }

  changeAccessUserRole(
    token: string,
    userId: number,
    payload: { role: AccessUser["role"]; group_ids?: number[]; company_ids?: string[] }
  ): Promise<AccessUser> {
    return this.request(`/admin/users/${userId}/role`, {
      method: "PUT",
      body: JSON.stringify(payload)
    }, token);
  }

  listAccessCompanies(token: string): Promise<AccessCompany[]> {
    return this.request("/admin/companies", {}, token);
  }

  listCompanyGroups(token: string): Promise<CompanyGroup[]> {
    return this.request("/admin/company-groups", {}, token);
  }

  companyGroup(token: string, groupId: number): Promise<CompanyGroupDetail> {
    return this.request(`/admin/company-groups/${groupId}`, {}, token);
  }

  createCompanyGroup(
    token: string,
    payload: { name: string; description?: string | null }
  ): Promise<CompanyGroup> {
    return this.request("/admin/company-groups", {
      method: "POST",
      body: JSON.stringify(payload)
    }, token);
  }

  setCompanyGroupEnabled(token: string, groupId: number, enabled: boolean): Promise<CompanyGroup> {
    return this.request(`/admin/company-groups/${groupId}/${enabled ? "enable" : "disable"}`, { method: "POST" }, token);
  }

  grantGroupUser(token: string, groupId: number, userId: number): Promise<void> {
    return this.request(`/admin/company-groups/${groupId}/users`, {
      method: "POST",
      body: JSON.stringify({ user_id: userId })
    }, token);
  }

  revokeGroupUser(token: string, groupId: number, userId: number): Promise<void> {
    return this.request(`/admin/company-groups/${groupId}/users/${userId}`, { method: "DELETE" }, token);
  }

  grantGroupCompany(token: string, groupId: number, companyId: string): Promise<void> {
    return this.request(`/admin/company-groups/${groupId}/companies`, {
      method: "POST",
      body: JSON.stringify({ company_id: companyId })
    }, token);
  }

  revokeGroupCompany(token: string, groupId: number, companyId: string): Promise<void> {
    return this.request(`/admin/company-groups/${groupId}/companies/${companyId}`, { method: "DELETE" }, token);
  }

  grantDirectCompany(token: string, userId: number, companyId: string): Promise<void> {
    return this.request(`/admin/users/${userId}/companies`, {
      method: "POST",
      body: JSON.stringify({ company_id: companyId })
    }, token);
  }

  revokeDirectCompany(token: string, userId: number, companyId: string): Promise<void> {
    return this.request(`/admin/users/${userId}/companies/${companyId}`, { method: "DELETE" }, token);
  }

  // Saíram em 2026-08-04 junto com o repoint de menções:
  // `updateMentionClassification`, `reclassifyMentions`,
  // `deleteMentions`, `mentionPreviewImage` e os 3 de filter-preset.
  // Todos falavam o backend descartado. Reclassificação volta com a
  // Camada 2.5 (`ReclassificationDraft`, desenho de rascunho→aprovação);
  // preview de imagem, com o `link_preview.py` já colhido em
  // `web_api/infrastructure/`. Os presets voltaram em 2026-08-26 como
  // `saved-filters` — e desta vez COMPARTILHADOS pela empresa, não um
  // rascunho por navegador como no sistema antigo.

  /**
   * Os filtros salvos de uma tela — todos os da EMPRESA, não só os de
   * quem pede. É o ponto da feature: o recorte que um analista montou
   * serve o time inteiro que atende aquele cliente.
   */
  listSavedFilters(token: string, kind: SavedFilterKind): Promise<SavedFilter[]> {
    const params = new URLSearchParams({ kind });
    return this.request(`/saved-filters?${params.toString()}`, {}, token);
  }

  createSavedFilter(
    token: string,
    input: { kind: SavedFilterKind; name: string; criteria: MentionFilterCriteria }
  ): Promise<SavedFilter> {
    return this.request(
      `/saved-filters`,
      { method: "POST", body: JSON.stringify(input) },
      token
    );
  }

  updateSavedFilter(
    token: string,
    filterId: number,
    input: { name: string; criteria: MentionFilterCriteria }
  ): Promise<SavedFilter> {
    return this.request(
      `/saved-filters/${filterId}`,
      { method: "PUT", body: JSON.stringify(input) },
      token
    );
  }

  deleteSavedFilter(token: string, filterId: number): Promise<void> {
    return this.request(`/saved-filters/${filterId}`, { method: "DELETE" }, token);
  }


  /** O instante (hora de parede, sem fuso) da menção mais recente do
   *  escopo, ou `null` sem dado. A tela de menções abre com uma janela de
   *  data relativa a ele (2026-10-26), como a Visão geral. */
  latestPublished(
    token: string,
    options: { scope?: "own" | "competitors" | "all"; companyId?: string } = {}
  ): Promise<{ latest_published_at: string | null }> {
    const query = options.scope && options.scope !== "own" ? `?scope=${options.scope}` : "";
    return this.request(
      `/mentions/latest-published${query}`,
      {},
      token,
      options.companyId ? { companyId: options.companyId } : undefined
    );
  }

  /**
   * O resumo da janela. `companyId` só é preciso na Home, que não tem
   * `:companyId` na URL e mostra os números da última empresa visitada —
   * dentro de `/empresas/:id` o escopo já vem do shell.
   */
  mentionSummary(
    token: string,
    options: { days?: number; companyId?: string } = {}
  ): Promise<MentionSummary> {
    const query = options.days ? `?days=${options.days}` : "";
    return this.request<MentionSummary>(
      `/mentions/summary${query}`,
      {},
      token,
      options.companyId ? { companyId: options.companyId } : undefined
    );
  }

  /**
   * O dashboard de UM relatório — a foto congelada, agregada no servidor.
   *
   * `ref` é `{reportId}` (salvo) ou `{draftId}` (rascunho): duas rotas,
   * mesmo payload. Facetas vão por chave repetida
   * (`?sentiments=a&sentiments=b`) com um `exclude_<faceta>` ao lado, e o
   * valor `__none__` filtra o bucket "Não informado" que a resposta
   * devolve — é o que deixa a fatia sem dimensão virar recorte clicável.
   */
  socialReportDashboard(
    token: string,
    ref: { reportId: number } | { draftId: number },
    filters: SocialReportDashboardFilters = {}
  ): Promise<SocialReportDashboard> {
    const query = new URLSearchParams();
    appendSnapshotFacets(query, filters);
    query.set("granularity", filters.granularity ?? "week");
    query.set("week_start", String(filters.weekStart ?? 1));
    const path =
      "reportId" in ref
        ? `/social-reports/${ref.reportId}/dashboard`
        : `/social-reports/drafts/${ref.draftId}/dashboard`;
    return this.request<SocialReportDashboard>(`${path}?${query}`, {}, token);
  }

  /** A matriz estratégica (imprensa e redes) do MESMO recorte de leitura
   *  do dashboard. `items` restringe as bolhas aos rótulos escolhidos. */
  socialReportDashboardMatrix(
    token: string,
    ref: { reportId: number } | { draftId: number },
    filters: SnapshotFacetFilters,
    group: string,
    items: string[] = []
  ): Promise<DashboardMatrix> {
    const query = new URLSearchParams();
    appendSnapshotFacets(query, filters);
    query.set("group", group);
    items.forEach(item => query.append("item", item));
    const path =
      "reportId" in ref
        ? `/social-reports/${ref.reportId}/dashboard/matrix`
        : `/social-reports/drafts/${ref.draftId}/dashboard/matrix`;
    return this.request<DashboardMatrix>(`${path}?${query}`, {}, token);
  }

  /** Os textos da Análise de Temas do relatório salvo, um por recorte. */
  listReportThemeNotes(token: string, reportId: number): Promise<ThemeNote[]> {
    return this.request<ThemeNote[]>(`/social-reports/${reportId}/theme-notes`, {}, token);
  }

  /** Grava o par de textos do recorte; os dois vazios apagam (`null`). */
  saveReportThemeNote(
    token: string,
    reportId: number,
    body: { recorte: ThemeNoteRecorte; positive_text: string; negative_text: string }
  ): Promise<ThemeNote | null> {
    return this.request<ThemeNote | null>(
      `/social-reports/${reportId}/theme-notes`,
      { method: "PUT", body: JSON.stringify(body) },
      token
    );
  }

  // ── Uploads: o contrato REAL do web_api ──────────────────────────
  // A tela de Uploads é a primeira totalmente repontada; os métodos
  // abaixo espelham web_api/infrastructure/http/routes/{uploads,corrections}.py.

  /** Os 7 templates de clipadora — chamado uma vez, popula o select. */
  uploadTemplates(): Promise<UploadTemplateInfo[]> {
    return this.request<UploadTemplateInfo[]>("/upload-templates", {});
  }

  /** Pré-flight ao soltar cada arquivo: detecta o template pela
   * estrutura. `detected_upload_template_id: null` NÃO é erro — é o
   * select nascendo vazio pro usuário escolher. */
  inspectUpload(token: string, file: File): Promise<InspectResult> {
    const body = new FormData();
    body.set("file", file, file.name);
    return this.request<InspectResult>(
      "/uploads/inspect",
      { method: "POST", body },
      token
    );
  }

  /** Envio de até 10 arquivos: `files` repetido + `items` como JSON,
   * pareados por índice E por nome no servidor. 202 com sucesso PARCIAL
   * — iterar `uploads[]` e olhar o `status` de cada um. */
  submitUploads(
    token: string,
    items: SubmitUploadItem[]
  ): Promise<{ uploads: SubmittedUpload[] }> {
    const body = new FormData();
    for (const item of items) {
      body.append("files", item.file, item.file.name);
    }
    body.set(
      "items",
      JSON.stringify(
        items.map(item => ({
          filename: item.file.name,
          upload_template_id: item.uploadTemplateId,
          refresh_engagement_metrics: item.refreshEngagementMetrics,
          imported_dimensions: item.importedDimensions
        }))
      )
    );
    return this.request(
      "/uploads",
      { method: "POST", body },
      token
    );
  }

  listUploads(
    token: string,
    options: {
      page?: number;
      size?: number;
      status?: string;
      situation?: UploadSituation;
      uploadTemplateId?: number;
      q?: string;
      /** Instantes ISO com fuso (carimbo de verdade, ver `periodoDaConsulta`). */
      uploadedFrom?: string;
      uploadedTo?: string;
    } = {}
  ): Promise<PageOf<UploadSummary>> {
    const query = new URLSearchParams({
      page: String(options.page ?? 1),
      size: String(options.size ?? 20)
    });
    if (options.status) query.set("status", options.status);
    if (options.situation) query.set("situation", options.situation);
    if (options.uploadTemplateId !== undefined) {
      query.set("upload_template_id", String(options.uploadTemplateId));
    }
    if (options.q?.trim()) query.set("q", options.q.trim());
    if (options.uploadedFrom) query.set("uploaded_from", options.uploadedFrom);
    if (options.uploadedTo) query.set("uploaded_to", options.uploadedTo);
    return this.request(`/uploads?${query}`, {}, token);
  }

  uploadSummary(token: string, uploadId: number): Promise<UploadSummary> {
    return this.request(`/uploads/${uploadId}`, {}, token);
  }

  uploadSkips(
    token: string,
    uploadId: number,
    options: { page?: number; size?: number } = {}
  ): Promise<PageOf<UploadSkipItem>> {
    const query = new URLSearchParams({
      page: String(options.page ?? 1),
      size: String(options.size ?? 50)
    });
    return this.request(`/uploads/${uploadId}/skips?${query}`, {}, token);
  }

  /** A tabela "Linhas com erro": uma linha por linha do upload, agrupada
   * por motivo pelo servidor. `pendingOnly` deixa só quem espera correção. */
  listErrorLines(
    token: string,
    uploadId: number,
    options: { page?: number; size?: number; pendingOnly?: boolean } = {}
  ): Promise<ErrorLinePage> {
    const query = new URLSearchParams({
      page: String(options.page ?? 1),
      size: String(options.size ?? 50),
      pending_only: String(options.pendingOnly ?? false)
    });
    return this.request(`/uploads/${uploadId}/error-lines?${query}`, {}, token);
  }

  /** O histórico de tentativas de UMA linha, da mais recente pra mais
   * antiga. */
  listErrorLineAttempts(
    token: string,
    uploadId: number,
    rowIndex: number
  ): Promise<ErrorLineAttempt[]> {
    return this.request(
      `/uploads/${uploadId}/error-lines/${rowIndex}/attempts`,
      {},
      token
    );
  }

  /** A planilha de pendências de volta, corrigida (multipart, um
   * arquivo). 201 com o recibo; as linhas recusadas vêm nele, nunca como
   * erro do envio. */
  submitCorrectionSheet(
    token: string,
    uploadId: number,
    file: File
  ): Promise<CorrectionSheetSubmission> {
    const body = new FormData();
    body.set("file", file, file.name);
    return this.request(
      `/uploads/${uploadId}/corrections/spreadsheet`,
      { method: "POST", body },
      token
    );
  }

  /** Os envios da planilha corrigida deste upload (até 20, o mais
   * recente primeiro). */
  listCorrectionSheetSubmissions(
    token: string,
    uploadId: number
  ): Promise<CorrectionSheetSubmission[]> {
    return this.request(
      `/uploads/${uploadId}/corrections/sheet-submissions`,
      {},
      token
    );
  }

  /** O conjunto de colunas tem que ser IDÊNTICO ao original — o
   * servidor recusa chave adicionada/removida com 422. `manualContent`
   * vazio/null limpa o texto manual (volta pra coleta automática). */
  saveCorrection(
    token: string,
    itemId: number,
    rawRow: Record<string, string>,
    manualContent: string | null
  ): Promise<void> {
    return this.request(
      `/corrections/${itemId}`,
      {
        method: "PATCH",
        body: JSON.stringify({
          raw_row: rawRow,
          manual_content: manualContent
        })
      },
      token
    );
  }

  reprocessCorrections(
    token: string,
    itemIds: number[]
  ): Promise<{ dispatched: number[] }> {
    return this.request(
      "/corrections/reprocess",
      { method: "POST", body: JSON.stringify({ item_ids: itemIds }) },
      token
    );
  }

  uploadCorrectionGroups(
    token: string,
    uploadId: number
  ): Promise<CorrectionGroup[]> {
    return this.request(`/uploads/${uploadId}/corrections/groups`, {}, token);
  }

  /**
   * Reprocessa um balde inteiro com UMA mensagem — o worker é quem
   * expande a lista. A rota irmã (`reprocessCorrections`) publica uma
   * mensagem por item, o que é certo pro clique numa linha e inviável
   * pras milhares que um cadastro faltando produz.
   */
  reprocessCorrectionGroup(
    token: string,
    uploadId: number,
    bucket: CorrectionBucket
  ): Promise<{ pending: number }> {
    return this.request(
      "/corrections/reprocess-group",
      {
        method: "POST",
        body: JSON.stringify({
          upload_id: uploadId,
          channel: bucket.channel,
          import_dimension: bucket.import_dimension ?? null,
          import_value: bucket.import_value ?? null,
          import_brand_id: bucket.import_brand_id ?? null,
          // Só quando veio: ausente e nulo dizem coisas diferentes ao servidor.
          ...(bucket.failure_kind !== undefined ? { failure_kind: bucket.failure_kind } : {})
        })
      },
      token
    );
  }

  /**
   * "Usar um existente" (2026-10-08): neste upload, o valor da planilha
   * que não casou com o cadastro vale o item escolhido. Vale SÓ pro
   * upload, e NÃO reprocessa — são dois cliques, e
   * `reprocessCorrectionGroup` é o segundo.
   */
  saveImportMapping(token: string, body: ImportMappingInput): Promise<void> {
    return this.request(
      "/corrections/import-mappings",
      { method: "POST", body: JSON.stringify(body) },
      token
    );
  }

  discardCorrection(token: string, itemId: number): Promise<void> {
    return this.request(
      `/corrections/${itemId}/discard`,
      { method: "POST" },
      token
    );
  }

  copilotoIcebreakers(token: string): Promise<{ icebreakers: string[] }> {
    return this.request("/copiloto/icebreakers", {}, token);
  }

  copilotoConversations(token: string): Promise<CopilotoConversation[]> {
    return this.request("/copiloto/conversations", {}, token);
  }

  createCopilotoConversation(token: string): Promise<CopilotoConversation> {
    return this.request("/copiloto/conversations", { method: "POST" }, token);
  }

  copilotoMessages(token: string, conversationId: string): Promise<CopilotoMessage[]> {
    return this.request(`/copiloto/conversations/${conversationId}/messages`, {}, token);
  }

  deleteCopilotoConversation(token: string, conversationId: string): Promise<void> {
    return this.request(`/copiloto/conversations/${conversationId}`, { method: "DELETE" }, token);
  }

  streamCopilotoMessage(
    token: string,
    conversationId: string,
    content: string,
    mode: CopilotoMode
  ): AsyncGenerator<CopilotoStreamEvent> {
    return this.consumeCopilotoStream(
      `${this.baseUrl}/copiloto/conversations/${conversationId}/messages`,
      { method: "POST", headers: this.jsonHeaders(token), body: JSON.stringify({ content, mode }) },
      conversationId
    );
  }

  editCopilotoMessage(
    token: string,
    conversationId: string,
    messageId: string,
    content: string
  ): AsyncGenerator<CopilotoStreamEvent> {
    return this.consumeCopilotoStream(
      `${this.baseUrl}/copiloto/conversations/${conversationId}/messages/${messageId}`,
      { method: "PUT", headers: this.jsonHeaders(token), body: JSON.stringify({ content }) },
      conversationId
    );
  }

  /**
   * Os headers do stream do Copiloto. Passa por `applyAuth` porque o
   * SSE não cabe no `request()` (que faz `response.json()` no fim) e
   * ficaria de fora da autenticação da casa: em desenvolvimento a
   * identidade é o `X-User-Id`, não o Bearer, então um objeto literal
   * escrito à mão aqui garantia 401 em toda mensagem enviada.
   */
  private jsonHeaders(token: string): Headers {
    const headers = new Headers({ "Content-Type": "application/json" });
    applyAuth(headers, token);
    return headers;
  }

  private async *consumeCopilotoStream(
    url: string,
    init: RequestInit,
    conversationId: string
  ): AsyncGenerator<CopilotoStreamEvent> {
    const response = await fetch(url, init);
    if (!response.ok || !response.body) {
      throw await toApiError(response);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split("\n\n");
      buffer = frames.pop() ?? "";
      for (const frame of frames) {
        const line = frame.trim();
        if (!line.startsWith("data:")) continue;
        const raw = line.slice(5).trim();
        if (!raw) continue;
        const parsed = JSON.parse(raw) as {
          delta?: string;
          done?: boolean;
          error?: string;
          conversation_id?: string;
          message_id?: string | null;
          user_message_id?: string | null;
          title?: string | null;
        };
        if (parsed.error) {
          yield { type: "error", message: parsed.error };
        } else if (parsed.done) {
          yield {
            type: "done",
            conversationId: parsed.conversation_id ?? conversationId,
            messageId: parsed.message_id ?? null,
            userMessageId: parsed.user_message_id ?? null,
            title: parsed.title ?? null
          };
        } else if (parsed.delta) {
          yield { type: "delta", text: parsed.delta };
        }
      }
    }
  }

  copilotoAdminSettings(token: string): Promise<CopilotoSettings> {
    return this.request("/copiloto/admin/settings", {}, token);
  }

  updateCopilotoAdminSettings(
    token: string,
    payload: Partial<CopilotoSettings>
  ): Promise<CopilotoSettings> {
    return this.request(
      "/copiloto/admin/settings",
      { method: "PUT", body: JSON.stringify(payload) },
      token
    );
  }

  copilotoAdminStatus(token: string): Promise<CopilotoStatus> {
    return this.request("/copiloto/admin/status", {}, token);
  }
}

export const apiClient = new ApiClient();
